/**
 * Rising Auctions PLP – auction grid, tiles, quick view, active bids / favourites.
 * Active auctions loaded via fetch from app proxy (data-api-url).
 * Scoped to section containers. Vanilla JS, no dependencies.
 */
(function () {
  'use strict';

  /**
   * Countdown "now". Auction end times are absolute instants, so ticking them against the
   * device clock shows every timer shifted by whatever that clock is wrong by. Falls back to
   * Date.now() when rising-auctions-server-clock.js is absent.
   */
  function raNow() {
    var clock = window['RAServerClock'];
    return clock ? clock.now() : Date.now();
  }

  /** Feed the shared clock from any RA response carrying serverTime. */
  function raSyncClock(data, sentAt) {
    var clock = window['RAServerClock'];
    if (clock) clock.syncFromPayload(data, sentAt);
  }

  function escapeHtml(text) {
    if (text == null) return '';

    var div = document.createElement('div');
    div.textContent = text;

    return div.innerHTML;
  }

  function cleanImageUrl(url) {
    if (url == null) return '';
    return String(url).trim();
  }

  function getPlaceholderImageUrl(section) {
    if (!section) return '';
    return cleanImageUrl(section.getAttribute('data-placeholder-image-url'));
  }

  function resolveAuctionImageUrl(url, placeholderUrl) {
    return cleanImageUrl(url) || cleanImageUrl(placeholderUrl);
  }

  function normalizeImageWidth(value, fallback) {
    var width = parseInt(value, 10);
    return width > 0 ? width : fallback;
  }

  // Grid columns when the grid has no data-columns-* value: the defaults getVisibleAuctionColumnCount
  // and sections/ra-product-grid.liquid use.
  var AUCTION_DEFAULT_COLUMNS = { tablet: 3, smallDesktop: 4, desktop: 5 };

  function getAuctionImageWidths(section) {
    var grid = section && section.querySelector('[data-grid-inner]');
    return {
      mobile: normalizeImageWidth(section && section.getAttribute('data-image-mobile-width'), 356),
      tablet: normalizeImageWidth(section && section.getAttribute('data-image-tablet-width'), 356),
      desktop: normalizeImageWidth(section && section.getAttribute('data-image-desktop-width'), 356),
      // Columns per breakpoint, as getVisibleAuctionColumnCount reads them: they size the tiles.
      columns: {
        tablet: normalizeImageWidth(grid && grid.getAttribute('data-columns-tablet'), AUCTION_DEFAULT_COLUMNS.tablet),
        smallDesktop: normalizeImageWidth(
          grid && grid.getAttribute('data-columns-small-desktop'),
          AUCTION_DEFAULT_COLUMNS.smallDesktop
        ),
        desktop: normalizeImageWidth(grid && grid.getAttribute('data-columns'), AUCTION_DEFAULT_COLUMNS.desktop),
      },
    };
  }

  // Smaller widths offered next to each configured width (always the largest candidate), so a
  // tile on a 1x screen or a narrow column downloads about the size it is drawn at. With the
  // default columns a tile is 180 to 341 CSS px wide (50vw on 360 to 430px phones, a third of a
  // 768 to 1023px tablet, a quarter or a fifth of a desktop up to 1440px): 180, 240 and 360
  // cover that at 1x, 480 a desktop tile at 1.5x.
  var AUCTION_IMAGE_SMALLER_WIDTHS = [180, 240, 360, 480];

  function getAuctionImageSrcset(url, maxWidth) {
    var largest = getSizedAuctionImageUrl(url, maxWidth);
    // Hosts whose resize parameter is unknown return the same URL for every width: keep one URL.
    if (!largest || largest === getSizedAuctionImageUrl(url, maxWidth + 1)) return largest;
    var candidates = [];
    AUCTION_IMAGE_SMALLER_WIDTHS.forEach(function (width) {
      if (width < maxWidth) candidates.push(getSizedAuctionImageUrl(url, width) + ' ' + width + 'w');
    });
    candidates.push(largest + ' ' + maxWidth + 'w');
    return candidates.join(', ');
  }

  // Tile width per breakpoint: two columns below 768px, then the grid's column settings
  // inside a container of at most 1440px (--ra-container-max). Gaps are ignored, so the
  // estimate is slightly wide and never asks for a smaller image than the tile needs.
  function getAuctionImageSizes(widths) {
    // buildAuctionTile's fallback widths carry no columns.
    var columns = (widths && widths.columns) || AUCTION_DEFAULT_COLUMNS;
    return {
      mobile: '50vw',
      tablet: 'calc(100vw / ' + columns.tablet + ')',
      desktop:
        '(max-width: 1199px) calc(100vw / ' + columns.smallDesktop + '), ' +
        '(max-width: 1440px) calc(100vw / ' + columns.desktop + '), ' +
        Math.ceil(1440 / columns.desktop) + 'px',
    };
  }

  function getVisibleAuctionColumnCount(section) {
    var grid = section && section.querySelector('[data-grid-inner]');
    if (window.matchMedia('(max-width: 767px)').matches) return 2;
    if (window.matchMedia('(max-width: 1023px)').matches) {
      return normalizeImageWidth(grid && grid.getAttribute('data-columns-tablet'), 3);
    }
    if (window.matchMedia('(max-width: 1199px)').matches) {
      return normalizeImageWidth(grid && grid.getAttribute('data-columns-small-desktop'), 4);
    }
    return normalizeImageWidth(grid && grid.getAttribute('data-columns'), 5);
  }

  /**
   * Request the same responsive widths used by regular collection cards.
   * Shopify CDN images use `width`; Sirv images use `w`. Unknown image hosts are
   * left untouched because their resizing query contract is not known.
   */
  function getSizedAuctionImageUrl(url, width) {
    var source = cleanImageUrl(url);
    if (!source || source.indexOf('data:') === 0) return source;

    try {
      var parsed = new URL(source, window.location.origin);
      var host = parsed.hostname.toLowerCase();
      if (host === 'cdn.shopify.com' || parsed.pathname.indexOf('/cdn/shop/') !== -1) {
        parsed.searchParams.set('width', String(width));
      } else if (host === 'tjcuk.sirv.com' || host.slice(-9) === '.sirv.com') {
        parsed.searchParams.set('w', String(width));
      } else {
        return source;
      }
      return parsed.toString();
    } catch (e) {
      return source;
    }
  }

  function buildAuctionPictureHtml(url, alt, className, widths, loadAttributes, defer) {
    var mobileSrcset = getAuctionImageSrcset(url, widths.mobile);
    var tabletSrcset = getAuctionImageSrcset(url, widths.tablet);
    var desktopSrcset = getAuctionImageSrcset(url, widths.desktop);
    var desktopUrl = getSizedAuctionImageUrl(url, widths.desktop);
    var sizes = getAuctionImageSizes(widths);
    var sourceAttribute = defer ? 'data-srcset' : 'srcset';
    var imgSource = defer ? 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=' : desktopUrl;
    var deferredAttributes = defer
      ? ' data-src="' + escapeHtml(desktopUrl) + '" data-deferred-auction-image'
      : '';
    // A sizes attribute only applies to a width set; one URL keeps the old markup.
    var source = function (media, srcset, sizesValue) {
      var sized = srcset.indexOf('w, ') !== -1 || / \d+w$/.test(srcset);
      return '<source media="' + media + '" ' + sourceAttribute + '="' + escapeHtml(srcset) + '"' +
        (sized ? ' sizes="' + sizesValue + '"' : '') + '>';
    };

    return '<picture>' +
      source('(max-width: 767px)', mobileSrcset, sizes.mobile) +
      source('(max-width: 1023px)', tabletSrcset, sizes.tablet) +
      (desktopSrcset !== desktopUrl ? source('(min-width: 1024px)', desktopSrcset, sizes.desktop) : '') +
      '<img src="' + escapeHtml(imgSource) + '"' + deferredAttributes +
      ' alt="' + escapeHtml(alt || '') + '" width="' + widths.desktop + '" height="' + widths.desktop + '" ' +
      loadAttributes + ' class="' + className + '">' +
      '</picture>';
  }

  function loadDeferredAuctionPicture(image) {
    if (!image || !image.hasAttribute('data-deferred-auction-image')) return;
    var picture = image.closest('picture');
    if (picture) {
      picture.querySelectorAll('source[data-srcset]').forEach(function (source) {
        source.srcset = source.getAttribute('data-srcset');
        source.removeAttribute('data-srcset');
      });
    }
    image.src = image.getAttribute('data-src');
    image.removeAttribute('data-src');
    image.removeAttribute('data-deferred-auction-image');
  }

  function getAuctionTilePlaceholderUrl(tile) {
    var section =
      (tile && tile.closest('.rising-auctions-grid')) ||
      (tile && tile.closest('.rising-auctions-active-bids')) ||
      document.querySelector('.rising-auctions-grid[data-placeholder-image-url]');
    return getPlaceholderImageUrl(section);
  }

  function applyAuctionImagePlaceholder(img, placeholderUrl) {
    if (!img || !placeholderUrl) return false;
    if (img.dataset.raImageFallbackApplied === 'true') return false;
    img.dataset.raImageFallbackApplied = 'true';
    img.classList.add('rising-auctions-tile__image--fallback');
    img.src = placeholderUrl;
    img.removeAttribute('srcset');
    img.removeAttribute('data-src');
    img.removeAttribute('data-deferred-auction-image');
    var picture = img.closest('picture');
    if (picture) {
      picture.querySelectorAll('source').forEach(function (source) {
        source.remove();
      });
    }
    return true;
  }

  function handleAuctionImageError(event) {
    var img = event && event.target;
    if (!img || img.tagName !== 'IMG') return;
    var tile = img.closest('[data-auction-tile]');
    var placeholderUrl = getAuctionTilePlaceholderUrl(tile);
    if (!placeholderUrl) return;
    if (img.src && img.src.indexOf(placeholderUrl) !== -1) return;
    applyAuctionImagePlaceholder(img, placeholderUrl);
  }

  function markAuctionImageLoaded(img) {
    if (!img) return;
    img.classList.add('rising-auctions-tile__image--loaded');
  }

  function bindAuctionTileImage(img) {
    if (!img || img.dataset.raImageErrorBound === 'true') return;
    img.dataset.raImageErrorBound = 'true';
    img.addEventListener('error', handleAuctionImageError);
    img.addEventListener('load', function () {
      markAuctionImageLoaded(img);
    });
    if (img.complete && img.naturalWidth > 0) {
      markAuctionImageLoaded(img);
    }
  }

  function bindAuctionTileImages(tile) {
    if (!tile) return;
    tile.querySelectorAll(
      '.rising-auctions-tile__media img, .rising-auctions-active-bids__media img, .rising-auctions-active-bids__img'
    ).forEach(bindAuctionTileImage);
  }

  function retryBrokenAuctionTileImages(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-auction-tile] img').forEach(function (img) {
      if (img.dataset.raImageFallbackApplied === 'true') return;
      if (img.complete && img.naturalWidth > 0) return;
      var deferredSrc = img.getAttribute('data-src');
      if (deferredSrc && img.hasAttribute('data-deferred-auction-image')) {
        loadDeferredAuctionPicture(img);
        return;
      }
      var currentSrc = img.currentSrc || img.src;
      if (!currentSrc || currentSrc.indexOf('data:image/gif') === 0) return;
      img.src = currentSrc;
    });
  }

  /** options.eager: first-row promo on page 1. It is often the LCP image, so it must not be lazy. */
  function buildPromoTile(payload, currencySymbol, section, options) {
    var imgUrl = payload && payload.imageUrl ? String(payload.imageUrl) : '';
    // Same banner, preloaded from <head> by snippets/ra-plp-head-start.liquid at a fitting
    // width. Using that exact URL lets the tile reuse the preloaded file (LCP).
    var preloadedImages = window.__raPlpBannerImages;
    var preloadedUrl = imgUrl && preloadedImages && payload.slot != null
      ? preloadedImages[String(payload.slot)]
      : '';
    if (preloadedUrl) imgUrl = String(preloadedUrl);
    var href = payload && payload.href ? String(payload.href) : '#';
    var ctaLabel = (section && section.getAttribute('data-promo-cta-label')) || 'Shop now';
    var article = document.createElement('article');
    article.className = 'rising-auctions-banner-slot';
    var link = document.createElement('a');
    link.className = 'rising-auctions-banner-slot__link';
    link.href = href;
    var inner = document.createElement('div');
    inner.className = 'rising-auctions-banner-slot__inner';
    if (imgUrl) {
      var img = document.createElement('img');
      var eager = !!(options && options.eager);
      img.loading = eager ? 'eager' : 'lazy';
      if (eager) img.setAttribute('fetchpriority', 'high');
      img.src = imgUrl;
      img.alt = '';
      img.className = 'rising-auctions-banner-slot__image';
      inner.appendChild(img);
    } else {
      var placeholder = document.createElement('div');
      placeholder.className = 'rising-auctions-banner-slot__placeholder';
      placeholder.textContent = ctaLabel || 'Shop now';
      inner.appendChild(placeholder);
    }
    link.appendChild(inner);
    article.appendChild(link);
    return article;
  }

  function normalizeBidAmount(amount) {
    var n = Number(amount);

    if (typeof n !== 'number' || isNaN(n)) return NaN;

    return Math.ceil(n);
  }

  function normalizeMaxBidAmount(amount) {
    var n = Number(amount);

    if (typeof n !== 'number' || isNaN(n) || n <= 0) return null;
    n = Math.floor(n);

    return n >= 1 ? n : null;
  }

  var MAX_BID_AMOUNT = 99999;

  function getEffectiveMaxBidAmount(auctionCap) {
    if (auctionCap == null) return MAX_BID_AMOUNT;
    return Math.min(auctionCap, MAX_BID_AMOUNT);
  }

  function enforceBidInputDigitLimit(input) {
    if (!input) return;
    var raw = String(input.value).replace(/[^\d]/g, '');
    if (raw.length > 5) raw = raw.slice(0, 5);
    if (raw === '') {
      input.value = '';
      return;
    }
    var n = parseInt(raw, 10);
    if (isNaN(n)) {
      input.value = '';
      return;
    }
    if (n > MAX_BID_AMOUNT) n = MAX_BID_AMOUNT;
    input.value = String(n);
  }

  // Bid amounts display as whole pounds; typed decimals are normalized before submit.
  function formatMoney(amount, currencySymbol) {
    var symbol = currencySymbol || '£';
    var n = normalizeBidAmount(amount);

    if (typeof n !== 'number' || isNaN(n)) return symbol + '0';
    return symbol + n;
  }

  function formatBidInputValue(num) {
    var n = normalizeBidAmount(num);

    if (typeof n !== 'number' || isNaN(n)) return '0';
    return String(n);
  }

  function buildTileBidAmountInnerHtml(amount, currencySymbol) {
    var symbol = currencySymbol || '£';
    var value = formatBidInputValue(amount);
    return (
      '<span class="rising-auctions-tile__bid-currency" aria-hidden="true">' +
      escapeHtml(symbol) +
      '</span><span class="rising-auctions-tile__bid-value">' +
      escapeHtml(value) +
      '</span>'
    );
  }

  function setTileBidAmountDisplay(el, amount, currencySymbol) {
    if (!el) return;
    var symbol = currencySymbol || '£';
    var valueEl = el.querySelector('.rising-auctions-tile__bid-value');
    if (valueEl) {
      var currencyEl = el.querySelector('.rising-auctions-tile__bid-currency');
      if (currencyEl) currencyEl.textContent = symbol;
      valueEl.textContent = formatBidInputValue(amount);
      return;
    }
    el.textContent = formatMoney(amount, symbol);
  }

  /** Variant/list price for line item property basePrice (decimal string). */
  function formatRaBasePriceDecimal(num) {
    var n = Number(num);
    if (typeof n !== 'number' || isNaN(n)) return '0.00';
    return n.toFixed(2);
  }

  /** Thumb icons: same paths as `rising-auctions-pdp.js` (not imported — avoids loading PDP script on PLP). */
  var RA_QW_THUMB_PATHS = {
    up: 'M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 11H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h3z',
    down: 'M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17z',
  };

  /**
   * Active Bids / PLP tile status icons (inlined from theme assets).
   * Source: assets/thunbs_up_icon.svg, assets/thunbs_down_icon.svg
   */
  var RA_TILE_THUMB_SVG = {
    up:
      '<svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M11.798 6.74386C11.795 6.74205 11.7919 6.73903 11.7889 6.73903C11.7811 6.73601 11.775 6.73118 11.7672 6.72695C11.7533 6.7179 11.7406 6.70824 11.7316 6.69737C11.7286 6.69436 11.7255 6.69254 11.7225 6.68953C11.7195 6.6847 11.7177 6.68047 11.7165 6.67564C11.7104 6.66659 11.7056 6.65572 11.7026 6.64485C11.6996 6.6358 11.6966 6.62614 11.6948 6.61527C11.6929 6.60441 11.6929 6.59535 11.6929 6.58449C11.6929 6.57362 11.6948 6.56275 11.6978 6.55189C11.6996 6.54706 11.6978 6.54102 11.6996 6.53619C11.7014 6.53136 11.7044 6.52834 11.7074 6.52412C11.7123 6.51325 11.7183 6.50419 11.7243 6.49635C11.7304 6.48729 11.7364 6.47944 11.7461 6.47341C11.7491 6.47039 11.7521 6.46556 11.7569 6.46254L11.9066 6.34965C12.1118 6.1933 12.2314 5.95545 12.2314 5.69767C12.2314 5.39341 12.075 5.12599 11.8143 4.98231C11.8082 4.97929 11.8034 4.97627 11.7974 4.97144C11.6519 4.8664 11.4714 4.81086 11.2982 4.81871L8.65143 4.89779C8.59589 4.89598 8.55425 4.87787 8.52165 4.84045C8.48906 4.80362 8.47699 4.75231 8.48604 4.70281L9.01116 2.06647C9.17474 1.45313 9.07152 0.828327 8.73471 0.386445C8.58623 0.193267 8.35626 0.0791688 8.19088 0.0188047C8.09672 -0.0150014 7.99471 -0.00292706 7.90659 0.0526108C7.81062 0.114187 7.74603 0.219223 7.73517 0.335129C7.51244 2.76314 5.04683 5.32514 2.86779 5.44893L2.86719 10.835C3.33557 10.8368 3.79913 10.9944 4.17155 11.2799C5.59301 12.3666 10.4882 11.906 10.688 11.8872C10.691 11.8872 10.694 11.8854 10.6958 11.8854H10.7127C10.9149 11.8854 11.1069 11.8033 11.2505 11.6566C11.3942 11.5099 11.4744 11.3119 11.4744 11.1019C11.4744 10.8562 11.3604 10.6225 11.1672 10.4758C11.1642 10.474 11.1624 10.4698 11.1612 10.468C11.1521 10.4601 11.1455 10.4511 11.1382 10.4414C11.1322 10.4336 11.1262 10.4257 11.1213 10.4167C11.1165 10.4076 11.1153 10.398 11.1123 10.3871C11.1093 10.3762 11.1063 10.3654 11.1063 10.3545C11.1063 10.3515 11.1044 10.3485 11.1044 10.3436C11.1044 10.3376 11.1075 10.3316 11.1093 10.3237C11.1111 10.3128 11.1123 10.302 11.1153 10.2911C11.1183 10.2802 11.1244 10.2712 11.131 10.2603C11.134 10.2543 11.1358 10.2483 11.1388 10.2434C11.1407 10.2404 11.1449 10.2386 11.1467 10.2374C11.1545 10.2283 11.1624 10.2217 11.1714 10.2157C11.1793 10.2096 11.1871 10.2036 11.1962 10.1987C11.2052 10.1939 11.2149 10.1927 11.224 10.1897C11.2348 10.1867 11.2457 10.1837 11.2565 10.1837C11.2596 10.1837 11.2626 10.1818 11.2674 10.1818H11.2843C11.4895 10.1818 11.6827 10.0997 11.8269 9.95184C11.9736 9.80213 12.0539 9.60231 12.0539 9.39102C12.0539 9.10487 11.9042 8.84227 11.6628 8.70343C11.6598 8.70162 11.658 8.6986 11.6549 8.6974C11.6441 8.68955 11.635 8.68049 11.6254 8.67083C11.6205 8.66601 11.6163 8.66178 11.6115 8.65514C11.6036 8.64427 11.5994 8.6322 11.5946 8.61952C11.5916 8.61348 11.5885 8.60745 11.5867 8.60081C11.5867 8.599 11.5849 8.59779 11.5849 8.59779C11.5831 8.58994 11.5849 8.58209 11.5849 8.57304C11.5831 8.56096 11.5819 8.5501 11.5849 8.53742C11.5867 8.52535 11.5928 8.51448 11.597 8.50361C11.6 8.49577 11.6 8.48792 11.6048 8.48188C11.6048 8.48007 11.6066 8.48007 11.6079 8.47886C11.6139 8.468 11.6235 8.45894 11.6326 8.4511C11.6386 8.44506 11.6435 8.43721 11.6495 8.43238C11.6555 8.42755 11.6652 8.42453 11.6724 8.42152C11.6833 8.41548 11.6954 8.40944 11.7081 8.40582C11.7099 8.40582 11.7099 8.40401 11.7111 8.40401C11.8674 8.3702 12.0092 8.28991 12.1221 8.174C12.2827 8.0086 12.3726 7.79066 12.3726 7.55885C12.3654 7.19241 12.1366 6.86338 11.798 6.74386Z" fill="#198754"/>' +
      '<path d="M2.02868 4.5313H0.508231C0.228767 4.5313 0 4.7583 0 5.03961V11.0994C0 11.3789 0.226959 11.6077 0.508231 11.6077H2.02868C2.30814 11.6077 2.53691 11.3807 2.53691 11.0994V11.0004V10.9973V10.9943V5.29311V5.29009V5.28707V5.03955C2.53691 4.75824 2.30995 4.5313 2.02868 4.5313Z" fill="#198754"/>' +
      '</svg>',
    down:
      '<svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M11.798 5.25614C11.795 5.25795 11.7919 5.26097 11.7889 5.26097C11.7811 5.26399 11.775 5.26882 11.7672 5.27305C11.7533 5.2821 11.7406 5.29176 11.7316 5.30263C11.7286 5.30564 11.7255 5.30746 11.7225 5.31047C11.7195 5.3153 11.7177 5.31953 11.7165 5.32436C11.7104 5.33341 11.7056 5.34428 11.7026 5.35515C11.6996 5.3642 11.6966 5.37386 11.6948 5.38473C11.6929 5.39559 11.6929 5.40465 11.6929 5.41551C11.6929 5.42638 11.6948 5.43725 11.6978 5.44811C11.6996 5.45294 11.6978 5.45898 11.6996 5.46381C11.7014 5.46864 11.7044 5.47166 11.7074 5.47588C11.7123 5.48675 11.7183 5.49581 11.7243 5.50365C11.7304 5.51271 11.7364 5.52056 11.7461 5.52659C11.7491 5.52961 11.7521 5.53444 11.7569 5.53746L11.9066 5.65035C12.1118 5.8067 12.2314 6.04455 12.2314 6.30233C12.2314 6.60659 12.075 6.87401 11.8143 7.01769C11.8082 7.02071 11.8034 7.02373 11.7974 7.02856C11.6519 7.1336 11.4714 7.18914 11.2982 7.18129L8.65143 7.10221C8.59589 7.10402 8.55425 7.12213 8.52165 7.15955C8.48906 7.19638 8.47699 7.24769 8.48604 7.29719L9.01116 9.93353C9.17474 10.5469 9.07152 11.1717 8.73471 11.6136C8.58623 11.8067 8.35626 11.9208 8.19088 11.9812C8.09672 12.015 7.99471 12.0029 7.90659 11.9474C7.81062 11.8858 7.74603 11.7808 7.73517 11.6649C7.51244 9.23686 5.04683 6.67486 2.86779 6.55107L2.86719 1.16497C3.33557 1.16316 3.79913 1.0056 4.17155 0.720057C5.59301 -0.366561 10.4882 0.094038 10.688 0.112753C10.691 0.112753 10.694 0.114564 10.6958 0.114564H10.7127C10.9149 0.114564 11.1069 0.196664 11.2505 0.343363C11.3942 0.490058 11.4744 0.688069 11.4744 0.898138C11.4744 1.14383 11.3604 1.37747 11.1672 1.52416C11.1642 1.52597 11.1624 1.53019 11.1612 1.53201C11.1521 1.53985 11.1455 1.54891 11.1382 1.55857C11.1322 1.56642 11.1262 1.57426 11.1213 1.58332C11.1165 1.59238 11.1153 1.60203 11.1123 1.6129C11.1093 1.62377 11.1063 1.63463 11.1063 1.6455C11.1063 1.64852 11.1044 1.65154 11.1044 1.65637C11.1044 1.6624 11.1075 1.66844 11.1093 1.67629C11.1111 1.68715 11.1123 1.69802 11.1153 1.70889C11.1183 1.71975 11.1244 1.72881 11.131 1.73967C11.134 1.74571 11.1358 1.75175 11.1388 1.75658C11.1407 1.7596 11.1449 1.76141 11.1467 1.76262C11.1545 1.77167 11.1624 1.77831 11.1714 1.78435C11.1793 1.79038 11.1871 1.79642 11.1962 1.80125C11.2052 1.80608 11.2149 1.80729 11.224 1.81031C11.2348 1.81332 11.2457 1.81634 11.2565 1.81634C11.2596 1.81634 11.2626 1.81815 11.2674 1.81815H11.2843C11.4895 1.81815 11.6827 1.90025 11.8269 2.04816C11.9736 2.19787 12.0539 2.39769 12.0539 2.60898C12.0539 2.89513 11.9042 3.15773 11.6628 3.29657C11.6598 3.29838 11.658 3.3014 11.6549 3.3026C11.6441 3.31045 11.635 3.31951 11.6254 3.32917C11.6205 3.33399 11.6163 3.33822 11.6115 3.34486C11.6036 3.35573 11.5994 3.3678 11.5946 3.38048C11.5916 3.38652 11.5885 3.39255 11.5867 3.39919C11.5867 3.401 11.5849 3.40221 11.5849 3.40221C11.5831 3.41006 11.5849 3.41791 11.5849 3.42696C11.5831 3.43904 11.5819 3.4499 11.5849 3.46258C11.5867 3.47465 11.5928 3.48552 11.597 3.49639C11.6 3.50423 11.6 3.51208 11.6048 3.51812C11.6048 3.51993 11.6066 3.51993 11.6079 3.52114C11.6139 3.532 11.6235 3.54106 11.6326 3.5489C11.6386 3.55494 11.6435 3.56279 11.6495 3.56762C11.6555 3.57245 11.6652 3.57547 11.6724 3.57848C11.6833 3.58452 11.6954 3.59056 11.7081 3.59418C11.7099 3.59418 11.7099 3.59599 11.7111 3.59599C11.8674 3.6298 12.0092 3.71009 12.1221 3.826C12.2827 3.9914 12.3726 4.20934 12.3726 4.44115C12.3654 4.80759 12.1366 5.13662 11.798 5.25614Z" fill="#BC0514"/>' +
      '<path d="M2.02868 7.4687H0.508231C0.228767 7.4687 0 7.2417 0 6.96039V0.90064C0 0.621135 0.226959 0.392335 0.508231 0.392335H2.02868C2.30814 0.392335 2.53691 0.619327 2.53691 0.90064V0.999642V1.00266V1.00568V6.70689V6.70991V6.71293V6.96045C2.53691 7.24176 2.30995 7.4687 2.02868 7.4687Z" fill="#BC0514"/>' +
      '</svg>',
  };

  /** Trophy icon shared by Active Bids / PLP winner pills. */
  function getTileStatusIconHtml() {
    return '<span class="rising-auctions-tile__status-icon" aria-hidden="true">&#127942;</span>';
  }

  function raQwFormatExpirationDate(isoString) {
    if (!isoString) return '';
    var d = new Date(isoString);
    if (isNaN(d.getTime())) return '';

    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      day: 'numeric',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).formatToParts(d);

    var getPart = function (type) {
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === type) return parts[i].value;
      }
      return '';
    };

    var day = parseInt(getPart('day'), 10);
    if (!day) return '';
    var suffix = (day >= 11 && day <= 13) ? 'th' : (day % 10 === 1 ? 'st' : day % 10 === 2 ? 'nd' : day % 10 === 3 ? 'rd' : 'th');
    var dateStr = day + suffix + ' ' + getPart('month') + ', ' + getPart('year');
    var dayPeriod = getPart('dayPeriod');
    var timeStr = getPart('hour') + ':' + getPart('minute') + ':' + getPart('second') + (dayPeriod ? ' ' + dayPeriod : '');
    var zone = getPart('timeZoneName');

    return dateStr + ' at ' + timeStr + (zone ? ' ' + zone : '');
  }

  function raQwGetCountdownParts(endTimeMs) {
    var diff = Math.max(0, endTimeMs - raNow());
    var msDay = 86400000;
    var msHour = 3600000;
    var msMin = 60000;
    return {
      days: Math.floor(diff / msDay),
      hours: Math.floor((diff % msDay) / msHour),
      mins: Math.floor((diff % msHour) / msMin),
      secs: Math.floor((diff % msMin) / 1000),
    };
  }

  function raQwGetStatusIconSvg(iconType) {
    var path = iconType === 'thumbs-up' ? RA_QW_THUMB_PATHS.up : RA_QW_THUMB_PATHS.down;
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="' + path + '"/></svg>';
  }

  function raQwGetStatusIconHtml(iconType) {
    return '<span class="auction-pdp__status-icon auction-pdp__status-icon--trophy" aria-hidden="true">&#127942;</span>';
  }

  function raQwUpdatePdpCountdown(el, endMs, section) {
    if (!el) return;
    var parts = raQwGetCountdownParts(endMs);
    var d = el.querySelector('[data-pdp-days]');
    var h = el.querySelector('[data-pdp-hours]');
    var m = el.querySelector('[data-pdp-mins]');
    var s = el.querySelector('[data-pdp-secs]');
    if (d) d.textContent = String(parts.days).padStart(2, '0');
    if (h) h.textContent = String(parts.hours).padStart(2, '0');
    if (m) m.textContent = String(parts.mins).padStart(2, '0');
    if (s) s.textContent = String(parts.secs).padStart(2, '0');
    var dayUnit = el.querySelector('[data-pdp-day-unit]');
    if (dayUnit) dayUnit.hidden = parts.days === 0;

    var themeRoot = el.closest('.auction-pdp');
    if (!themeRoot) return;
    var thresholdRoot = section || el.closest('[data-rising-auctions-plp]') || el.closest('[data-timer-threshold-minutes]');
    var diff = Math.max(0, endMs - raNow());
    var remainingMinutes = diff / 60000;
    var thresholdMinutes = parseTimerThreshold(thresholdRoot);
    var urgent = diff <= 0 || (!isNaN(thresholdMinutes) && remainingMinutes <= thresholdMinutes);
    if (urgent) {
      themeRoot.classList.add('auction-pdp--urgent');
    } else {
      themeRoot.classList.remove('auction-pdp--urgent');
    }
    if (diff > 0 && diff < 10000) {
      el.classList.add('auction-pdp__countdown--blink');
    } else {
      el.classList.remove('auction-pdp__countdown--blink');
    }
  }

  function raQwSyncPdpBidRowFromPubNub(tile, price, betCount, isCurrentUser, winningInitials, currencySymbol) {
    if (!tile) return;
    var sym = currencySymbol || '£';
    var pdpCur = tile.querySelector('[data-pdp-current-bid]');
    if (pdpCur) pdpCur.textContent = formatMoney(price, sym);
    var pdpBc = tile.querySelector('[data-pdp-bid-count]');
    if (pdpBc) pdpBc.textContent = '(' + betCount + ' Bids)';
    var winnerRow = tile.querySelector('[data-auction-pdp-winner-row]');
    if (!winnerRow) return;
    var noBids = betCount === 0;
    var text = noBids
      ? 'Be the first to bid'
      : isCurrentUser
        ? "You're winning!"
        : (winningInitials ? String(winningInitials) + ' is winning!' : '— is winning!');
    winnerRow.className =
      'auction-pdp__winner-row' +
      (noBids ? ' auction-pdp__winner-row--no-bids' : '') +
      (!noBids && isCurrentUser ? ' auction-pdp__winner-row--winning' : '') +
      (!noBids && !isCurrentUser ? ' auction-pdp__winner-row--lost' : '');
    var iconPart = noBids ? '' : raQwGetStatusIconHtml(isCurrentUser ? 'thumbs-up' : 'thumbs-down');
    winnerRow.innerHTML =
      iconPart + '<span class="auction-pdp__winner-text" data-pdp-winner-text>' + escapeHtml(text) + '</span>';
    var pdpSubmit = tile.querySelector('[data-auction-pdp-submit]');
    if (pdpSubmit) {
      if (isCurrentUser) {
        pdpSubmit.textContent = 'Increase bid';
        pdpSubmit.classList.add('auction-pdp__btn--has-bid');
      } else {
        pdpSubmit.textContent = 'Bid now';
        pdpSubmit.classList.remove('auction-pdp__btn--has-bid');
      }
    }
  }

  function raQwBuildPdpActiveMarkup(payload, currencySymbol, maxBidCap, isLoggedIn, loginUrl) {
    var symbol = currencySymbol || '£';
    var endMs = payload.countdownEndsAt ? new Date(payload.countdownEndsAt).getTime() : 0;
    var expDateStr = raQwFormatExpirationDate(payload.countdownEndsAt);
    var currentBid = payload.currentBid != null ? payload.currentBid : 0;
    var bidCount = typeof payload.bidCount === 'number' ? payload.bidCount : 0;
    var bidIncrement = (typeof payload.bidIncrement === 'number' && payload.bidIncrement > 0) ? payload.bidIncrement : 1;
    var nextMin = bidCount === 0 ? currentBid : currentBid + bidIncrement;
    var inputVal = nextMin;
    var normalizedMaxBidCap = normalizeMaxBidAmount(maxBidCap);
    inputVal = Math.min(nextMin, getEffectiveMaxBidAmount(normalizedMaxBidCap));
    var bidStatusText = payload.bidStatusText != null ? payload.bidStatusText : (bidCount === 0 ? 'Be the first to bid' : '— is winning!');
    var statusIcon = payload.isUserWinning ? 'thumbs-up' : 'thumbs-down';
    var noBids = bidCount === 0;
    var winnerRowClass = 'auction-pdp__winner-row' +
      (noBids ? ' auction-pdp__winner-row--no-bids' : '') +
      (payload.isUserWinning ? ' auction-pdp__winner-row--winning' : '');
    if (noBids) statusIcon = '';

    var headerAndBody =
      '<div class="auction-pdp__header ra-qw__pdp-header">' +
        '<div class="auction-pdp__countdown" data-auction-pdp-countdown aria-live="polite">' +
          '<span data-pdp-day-unit>' +
          '<span class="auction-pdp__countdown-box"><span class="auction-pdp__countdown-num" data-pdp-days>00</span><span class="auction-pdp__countdown-label">Days</span></span>' +
          '<span class="auction-pdp__countdown-sep">:</span>' +
          '</span>' +
          '<span class="auction-pdp__countdown-box"><span class="auction-pdp__countdown-num" data-pdp-hours>00</span><span class="auction-pdp__countdown-label">Hours</span></span>' +
          '<span class="auction-pdp__countdown-sep">:</span>' +
          '<span class="auction-pdp__countdown-box"><span class="auction-pdp__countdown-num" data-pdp-mins>00</span><span class="auction-pdp__countdown-label">Mins</span></span>' +
          '<span class="auction-pdp__countdown-sep">:</span>' +
          '<span class="auction-pdp__countdown-box"><span class="auction-pdp__countdown-num" data-pdp-secs>00</span><span class="auction-pdp__countdown-label">Secs</span></span>' +
        '</div>' +
        '<div class="auction-pdp__expires ra-qw__pdp-expires">' +
          '<span class="auction-pdp__expires-label">Expires:</span> ' +
          '<span class="auction-pdp__expires-date">' + escapeHtml(expDateStr) + '</span>' +
        '</div>' +
      '</div>' +
      '<div class="auction-pdp__body">' +
        '<div class="auction-pdp__bid-row">' +
          '<span class="auction-pdp__current-bid" data-pdp-current-bid>' + escapeHtml(formatMoney(currentBid, symbol)) + '</span>' +
          '<span class="auction-pdp__bid-count" data-pdp-bid-count>(' + bidCount + ' Bids)</span>' +
        '</div>' +
        '<div class="' + winnerRowClass + '" data-auction-pdp-winner-row>' +
          (statusIcon ? raQwGetStatusIconHtml(statusIcon) : '') +
          '<span class="auction-pdp__winner-text" data-pdp-winner-text>' + escapeHtml(bidStatusText) + '</span>' +
        '</div>' +
      '</div>';

    var footer;
    if (!isLoggedIn) {
      footer =
        '<div class="auction-pdp__footer ra-qw__pdp-footer">' +
          '<a href="' + escapeHtml(loginUrl || '/account/login') + '" class="rising-auctions-tile__btn rising-auctions-tile__btn--login ra-qw__pdp-login">Login to bid</a>' +
        '</div>';
    } else {
      footer =
        '<div class="auction-pdp__footer ra-qw__pdp-footer">' +
          '<div class="auction-pdp__input-wrap">' +
            '<span class="auction-pdp__input-label">Enter Bid:</span>' +
            '<span class="auction-pdp__input-currency" aria-hidden="true">' + escapeHtml(symbol) + '</span>' +
            '<input type="number" class="auction-pdp__input" value="' + formatBidInputValue(inputVal) + '" min="1" max="99999" step="1" data-auction-pdp-input data-bid-input aria-label="Bid amount in ' + escapeHtml(symbol) + '">' +
          '</div>' +
          '<button type="button" class="auction-pdp__btn' + (payload.hasUserBid ? ' auction-pdp__btn--has-bid' : '') + '" data-auction-pdp-submit data-bid-submit>' + (payload.hasUserBid ? 'Increase bid' : 'Bid now') + '</button>' +
        '</div>';
    }

    return { html: headerAndBody + footer, endMs: endMs, bidIncrement: bidIncrement, nextMin: nextMin };
  }

  function raQwBuildPdpWonInnerHtml(payload, currencySymbol, variantId) {
    var symbol = currencySymbol || '£';
    var currentBid = payload.currentBid != null ? payload.currentBid : 0;
    var bidCount = typeof payload.bidCount === 'number' ? payload.bidCount : 0;
    var vid = variantId || payload.variantId || '';
    var aid = payload.auctionId != null ? String(payload.auctionId) : '';
    var amsCode = payload.amsAuctionCode != null ? String(payload.amsAuctionCode) : '';
    var mpc = payload.mainProductCode != null ? String(payload.mainProductCode) : '';
    var paymentExpirationAt = payload.paymentExpirationAt || payload.paymentDeadlineAt || '';
    var bp = '';
    if (payload.basePrice != null && String(payload.basePrice).trim() !== '') {
      bp = String(payload.basePrice);
    } else if (payload.variantPrice != null && payload.variantPrice !== '') {
      bp = formatRaBasePriceDecimal(payload.variantPrice);
    } else if (payload.price != null && payload.price !== '') {
      bp = formatRaBasePriceDecimal(payload.price);
    }
    return (
      '<div class="auction-pdp__ended auction-pdp__ended--won">' +
      '<div class="auction-pdp__body">' +
      '<div class="auction-pdp__ended-result auction-pdp__ended-result--won">' +
      '<span class="auction-pdp__ended-trophy" aria-hidden="true">&#127942;</span>' +
      '<span class="auction-pdp__ended-copy">' +
      '<span class="auction-pdp__ended-label">YOU WON!</span>' +
      '<span class="auction-pdp__ended-price">' + escapeHtml(formatMoney(currentBid, symbol)) +
      ' <span class="auction-pdp__ended-count">(' + bidCount + ' BIDS)</span></span>' +
      '</span></div></div>' +
      '<div class="auction-pdp__footer ra-qw__pdp-footer">' +
      '<button type="button" class="auction-pdp__add-to-bag" data-plp-add-to-bag data-pdp-add-to-bag data-auction-id="' + escapeHtml(aid) + '" data-ams-auction-code="' + escapeHtml(amsCode) + '" data-ra-main-product-code="' + escapeHtml(mpc) + '" data-base-price="' + escapeHtml(bp) + '" data-variant-id="' + escapeHtml(vid) + '" data-payment-expiration-at="' + escapeHtml(paymentExpirationAt) + '">Add to bag</button>' +
      '</div></div>'
    );
  }

  function raQwBuildPdpLostInnerHtml(payload, currencySymbol) {
    var symbol = currencySymbol || '£';
    var currentBid = payload.currentBid != null ? payload.currentBid : 0;
    var bidCount = typeof payload.bidCount === 'number' ? payload.bidCount : 0;
    var winnerInitials = payload.winnerInitials != null ? String(payload.winnerInitials) : '—';
    return (
      '<div class="auction-pdp__ended auction-pdp__ended--lost">' +
      '<div class="auction-pdp__body">' +
      '<div class="auction-pdp__ended-result auction-pdp__ended-result--lost">' +
      '<span class="auction-pdp__ended-trophy" aria-hidden="true">&#127942;</span>' +
      '<span class="auction-pdp__ended-copy">' +
      '<span class="auction-pdp__ended-label">' + escapeHtml(winnerInitials) + ' WON!</span>' +
      '<span class="auction-pdp__ended-price">' + escapeHtml(formatMoney(currentBid, symbol)) +
      ' <span class="auction-pdp__ended-count">(' + bidCount + ' BIDS)</span></span>' +
      '</span></div></div></div>'
    );
  }

  function raQwBuildPdpClosedInnerHtml() {
    return (
      '<div class="auction-pdp__ended auction-pdp__ended--closed">' +
      '<p class="auction-pdp__closed-label">AUCTION ENDED!</p>' +
      '<p class="auction-pdp__closed-sublabel">There have been no bids</p>' +
      '</div>'
    );
  }

  /** Read attr from QW article, else from existing Add to bag button (timer refetch may leave AMS only on the button). */
  function raQwAttrFromArticleOrBagBtn(tile, attrName) {
    var fromArticle = (tile && tile.getAttribute(attrName)) || '';
    if (fromArticle) return fromArticle;
    var btn = tile && tile.querySelector('[data-plp-add-to-bag]');
    return (btn && btn.getAttribute(attrName)) || '';
  }

  /** True when QW already shows won Add to bag with complete AMS cart fields. */
  function raQwWonAddToBagAlreadyComplete(tile) {
    if (!tile || !tile.querySelector('.auction-pdp__ended--won')) return false;
    var btn = tile.querySelector('[data-plp-add-to-bag]');
    if (!btn) return false;
    var ams = String(btn.getAttribute('data-ams-auction-code') || '').trim();
    var mpc = String(btn.getAttribute('data-ra-main-product-code') || '').trim();
    var aid = String(
      btn.getAttribute('data-auction-id') || tile.getAttribute('data-auction-id') || ''
    ).trim();
    return !!(ams && mpc && aid);
  }

  /** Copy AMS-related attrs from won button onto the article so a later PubNub rebuild can read them. */
  function raQwSyncEndedArticleAmsAttrsFromBagBtn(tile) {
    if (!tile) return;
    var btn = tile.querySelector('[data-plp-add-to-bag]');
    if (!btn) return;
    var attrs = [
      'data-ams-auction-code',
      'data-ra-main-product-code',
      'data-base-price',
      'data-payment-expiration-at',
      'data-variant-id',
      'data-variant-sku',
    ];
    for (var i = 0; i < attrs.length; i++) {
      var name = attrs[i];
      var current = tile.getAttribute(name);
      if (current != null && String(current).trim() !== '') continue;
      var fromBtn = btn.getAttribute(name);
      if (fromBtn != null && String(fromBtn).trim() !== '') {
        tile.setAttribute(name, fromBtn);
      }
    }
  }

  function raQwApplyPdpEndedToArticle(tile, payload) {
    if (!tile || !payload) return;
    var section = getBidSectionRoot(tile);
    var sym = (section && section.getAttribute('data-currency-symbol')) || payload.currencySymbol || '£';
    var state = String(payload.displayState || '').toUpperCase();
    var isUserWinner = payload.isUserWinner === true;
    var inner;
    if (state === 'CLOSED') {
      inner = raQwBuildPdpClosedInnerHtml();
    } else if (state === 'WON' || (isUserWinner && (state === 'PENDING' || state === 'SOLD'))) {
      // Timer refetch may already have rendered a complete won button; PubNub must not wipe AMS fields.
      if (raQwWonAddToBagAlreadyComplete(tile)) {
        raQwSyncEndedArticleAmsAttrsFromBagBtn(tile);
        return;
      }
      var variantId = raQwAttrFromArticleOrBagBtn(tile, 'data-variant-id');
      var amsAuctionCode = raQwAttrFromArticleOrBagBtn(tile, 'data-ams-auction-code');
      var mainProductCode = raQwAttrFromArticleOrBagBtn(tile, 'data-ra-main-product-code');
      var auctionId = raQwAttrFromArticleOrBagBtn(tile, 'data-auction-id');
      var paymentExpirationAt = raQwAttrFromArticleOrBagBtn(tile, 'data-payment-expiration-at');
      var qwBpRaw = raQwAttrFromArticleOrBagBtn(tile, 'data-base-price');
      var qwBpNum = qwBpRaw != null && qwBpRaw !== '' ? parseFloat(qwBpRaw) : NaN;
      if (amsAuctionCode) tile.setAttribute('data-ams-auction-code', amsAuctionCode);
      if (mainProductCode) tile.setAttribute('data-ra-main-product-code', mainProductCode);
      if (qwBpRaw) tile.setAttribute('data-base-price', qwBpRaw);
      if (paymentExpirationAt) tile.setAttribute('data-payment-expiration-at', paymentExpirationAt);
      inner = raQwBuildPdpWonInnerHtml(
        {
          currentBid: payload.currentBid,
          bidCount: payload.bidCount,
          variantId: variantId,
          auctionId: auctionId,
          amsAuctionCode: amsAuctionCode,
          mainProductCode: mainProductCode,
          paymentExpirationAt: paymentExpirationAt,
          price: !isNaN(qwBpNum) ? qwBpNum : null,
        },
        sym,
        variantId
      );
    } else {
      inner = raQwBuildPdpLostInnerHtml(payload, sym);
    }
    tile.innerHTML = '<div class="auction-pdp ra-qw-auction-pdp"><div class="auction-pdp__container">' + inner + '</div></div>';
    tile.removeAttribute('data-auction-tile');
    delete tile.dataset.raTileInit;
    var btn = tile.querySelector('[data-plp-add-to-bag]');
    if (btn) {
      initAddToBagPlaceholder(tile);
      refreshPlpAddToBagStates(section);
    }
  }

  var bidErrorModal = null;

  function ensureBidErrorModal() {
    if (bidErrorModal) return bidErrorModal;

    var overlay = document.createElement('div');
    var panel = document.createElement('div');
    var closeBtn = document.createElement('button');
    var msgEl = document.createElement('p');

    overlay.className = 'rising-auctions-bid-error-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    panel.className = 'rising-auctions-bid-error-panel';
    closeBtn.type = 'button';
    closeBtn.className = 'rising-auctions-bid-error-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '×';
    msgEl.className = 'rising-auctions-bid-error-message';
    panel.appendChild(closeBtn);
    panel.appendChild(msgEl);
    overlay.appendChild(panel);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) hideBidErrorModal();
    });
    closeBtn.addEventListener('click', hideBidErrorModal);
    document.body.appendChild(overlay);
    bidErrorModal = { overlay: overlay, messageEl: msgEl };

    return bidErrorModal;
  }

  function showBidErrorModal(minBid, currencySymbol, tile) {
    var m = ensureBidErrorModal();
    var symbol = currencySymbol || '£';
    var formatted = formatMoney(minBid, symbol);
    var isSpecial = tile && tile.getAttribute('data-special-auction') === 'true';
    m.messageEl.textContent = isSpecial
      ? 'This is a Special Auction. Please place a bid of ' + formatted + ' or more.'
      : 'Close call! Someone else bid too! Please Place a bid of ' + formatted + ' or more.';
    m.overlay.removeAttribute('aria-hidden');
    m.overlay.classList.add('rising-auctions-bid-error-overlay--open');
  }

  function showBidErrorModalMessage(message) {
    var m = ensureBidErrorModal();
    m.messageEl.textContent = message || 'Something went wrong.';
    m.overlay.removeAttribute('aria-hidden');
    m.overlay.classList.add('rising-auctions-bid-error-overlay--open');
  }

  /**
   * Bid POST with a time limit. Never retried: a bid that reached the server must not be
   * placed twice. A reply that is not JSON (e.g. the load balancer's HTML error page while a
   * pod restarts) resolves as { ok: false, notJson: true } instead of throwing. Unclear
   * outcomes are re-checked quietly against the auction state (see recheckBidQuietly).
   */
  var RA_BID_TIMEOUT_MS = 15000;
  var RA_AUCTION_RECHECK_TIMEOUT_MS = 8000;
  var RA_BID_LOGIN_MESSAGE = 'Please log in to place a bid.';
  var RA_BID_NO_EMAIL_MESSAGE =
    'Your account needs an email address to place a bid. Please add one in your account details.';

  function isRaBidLoginRequired(result) {
    return !!(result && !result.ok && result.status === 401 && result.data && result.data.error === 'Customer not identified');
  }

  function showRaBidLoginPrompt(section, message) {
    showBidErrorModalMessage(message || RA_BID_LOGIN_MESSAGE);
    var m = ensureBidErrorModal();
    var loginLink = document.createElement('a');
    loginLink.href = (section && section.getAttribute('data-login-url')) || '/account/login';
    loginLink.className = 'rising-auctions-tile__btn rising-auctions-tile__btn--login';
    loginLink.textContent = 'Login to bid';
    m.messageEl.appendChild(document.createElement('br'));
    m.messageEl.appendChild(loginLink);
    loginLink.focus();
  }

  function postRaBidJson(url, body) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () { controller.abort(); }, RA_BID_TIMEOUT_MS)
      : null;
    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller ? controller.signal : undefined,
    })
      .then(function (res) {
        return res.json().then(
          function (data) { return { ok: res.ok, status: res.status, data: data }; },
          function () { return { ok: false, status: res.status, data: null, notJson: true }; }
        );
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
      });
  }

  /** A reply that does not say what happened to the bid: proxy error page or server error. */
  function isRaBidOutcomeUnclear(result) {
    return !!(result && !result.ok && (result.notJson || result.status >= 500));
  }

  /**
   * Current auction state for one auction, with its own time limit. Resolves null when the
   * auction is no longer active (ended payloads carry displayState) or the reply is unusable.
   */
  function fetchRaAuctionStateQuiet(baseUrl, auctionId, customerId) {
    var url = (baseUrl || '/apps/rising-auctions') + '/auction?auction_id=' + encodeURIComponent(auctionId);
    if (customerId) url += '&logged_in_customer_id=' + encodeURIComponent(customerId);
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () { controller.abort(); }, RA_AUCTION_RECHECK_TIMEOUT_MS)
      : null;
    var sentAt = Date.now();
    return fetch(url, {
      credentials: 'include',
      cache: 'no-store',
      signal: controller ? controller.signal : undefined,
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (!data || data.error || data.displayState || data.currentBid == null) return null;
        raSyncClock(data, sentAt);
        return data;
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
      });
  }

  function hideBidErrorModal() {
    if (!bidErrorModal) return;
    bidErrorModal.overlay.setAttribute('aria-hidden', 'true');
    bidErrorModal.overlay.classList.remove('rising-auctions-bid-error-overlay--open');
  }

  function getBidSectionRoot(tile) {
    if (!tile) return null;
    return (
      tile.closest('.rising-auctions-grid') ||
      tile.closest('.rising-auctions-active-bids') ||
      document.querySelector('[data-rising-auctions-plp]')
    );
  }

  /**
   * PLP / account-table countdown registry: one setInterval per section root, not per tile.
   *
   * section._raCountdownRegistry = { intervalId, tiles: Map(tileEl -> cached state) }
   * Lifecycle: register on initAuctionTiles; clear on plpRefetch / account tbody wipe;
   * unregister on local expiry or PubNub ended (applyEndedStateToTile/Row).
   *
   * Local expiry (applyLocalTileExpiry): ENDED + disabled bids.
   * PubNub ended: SOLD / WON / CLOSED final UI (unchanged handlers).
   *
   * Quick View PDP countdown ([data-auction-pdp-countdown]) is excluded — still uses setTimeout.
   */
  function getOrCreateCountdownRegistry(section) {
    if (!section._raCountdownRegistry) {
      section._raCountdownRegistry = { intervalId: null, tiles: new Map(), observer: null, inView: null };
    }
    return section._raCountdownRegistry;
  }

  function parseTimerThreshold(section) {
    if (!section) return NaN;
    var thresholdStr = section.getAttribute('data-timer-threshold-minutes');
    if (thresholdStr === '' || thresholdStr == null) return NaN;
    return parseFloat(thresholdStr, 10);
  }

  function buildCountdownTileState(tile) {
    var countdownEnds = tile.getAttribute('data-countdown-ends');
    if (!countdownEnds) return null;
    if (tile.getAttribute('data-auction-expired') === 'true') return null;
    var countdownEls = tile.querySelectorAll('[data-countdown-text]');
    if (!countdownEls.length) return null;
    var endMs = new Date(countdownEnds).getTime();
    if (isNaN(endMs)) return null;
    var countdownContainers = [];
    for (var i = 0; i < countdownEls.length; i++) {
      countdownContainers.push(countdownEls[i].closest('[data-tile-countdown]'));
    }
    return {
      el: tile,
      endMs: endMs,
      countdownEls: countdownEls,
      countdownContainers: countdownContainers,
      isAccountRow: !!(tile.classList && tile.classList.contains('rising-auctions-active-bids__row')),
      isFavouritesRow: tile.hasAttribute('data-favourites-row'),
    };
  }

  /**
   * Account-page countdown segment (Active Bids / Favourites): separate spans so
   * numbers and units can use different font sizes (Figma / Bid History pattern).
   */
  function accountCountdownPart(num, unit) {
    return '<span class="rising-auctions-active-bids__time-num">' + num + '</span>' +
      '<span class="rising-auctions-active-bids__time-unit">' + unit + '</span>';
  }

  function formatAccountCountdownHtml(d, h, m, s) {
    var parts = [];
    if (d > 0) parts.push(accountCountdownPart(d, 'd'));
    parts.push(
      accountCountdownPart(h, 'h'),
      accountCountdownPart(m, 'm'),
      accountCountdownPart(s, 's')
    );
    return parts.join(' ');
  }

  function plpCountdownPart(num, unit) {
    return (
      '<span class="rising-auctions-tile__countdown-part">' +
        '<span class="rising-auctions-tile__countdown-num">' + num + '</span>' +
        '<span class="rising-auctions-tile__countdown-unit">' + unit + '</span>' +
      '</span>'
    );
  }

  function formatPlpCountdownHtml(d, h, m, s) {
    var parts = [];
    if (d > 0) parts.push(plpCountdownPart(d, 'd'));
    parts.push(
      plpCountdownPart(h, 'h'),
      plpCountdownPart(m, 'm'),
      plpCountdownPart(s, 's')
    );
    return '<span class="rising-auctions-tile__countdown-parts">' + parts.join('') + '</span>';
  }

  function formatPlpCountdownHtmlFromEndMs(endMs) {
    if (isNaN(endMs)) return '';
    var diff = Math.max(0, endMs - raNow());
    var d = Math.floor(diff / 86400000);
    var h = Math.floor((diff % 86400000) / 3600000);
    var m = Math.floor((diff % 3600000) / 60000);
    var s = Math.floor((diff % 60000) / 1000);
    return formatPlpCountdownHtml(d, h, m, s);
  }

  function updateCountdownDisplay(state, diff, thresholdMinutes) {
    var d = Math.floor(diff / 86400000);
    var h = Math.floor((diff % 86400000) / 3600000);
    var m = Math.floor((diff % 3600000) / 60000);
    var s = Math.floor((diff % 60000) / 1000);
    var cdHtml = state.isAccountRow ? formatAccountCountdownHtml(d, h, m, s) : formatPlpCountdownHtml(d, h, m, s);
    var remainingMinutes = diff / 60000;
    var isUrgent = !isNaN(thresholdMinutes) && remainingMinutes <= thresholdMinutes;
    var isBlink = diff < 10000;
    // Every element/attribute write here wakes each childList/attribute MutationObserver on
    // the page (badge manager, wishlist icons, ...), once a second per tile, and each of them
    // then rescans the page. So the markup is built once per shape (days shown or not) and
    // later ticks only change the digits' text nodes: a characterData change, which those
    // observers do not listen for. Classes are touched only when their state flips.
    var nums = d > 0 ? [d, h, m, s] : [h, m, s];
    var shape = (state.isAccountRow ? 'account' : 'plp') + (d > 0 ? '-d' : '');
    for (var i = 0; i < state.countdownEls.length; i++) {
      writeCountdownDigits(state.countdownEls[i], shape, nums, cdHtml, state.isAccountRow);
      var countdownContainer = state.countdownContainers[i];
      if (countdownContainer) {
        setClassIfChanged(countdownContainer, 'rising-auctions-tile__countdown--urgent', isUrgent);
        setClassIfChanged(countdownContainer, 'rising-auctions-tile__countdown--blink', isBlink);
      }
    }
  }

  function writeCountdownDigits(el, shape, nums, html, isAccountRow) {
    var digits = el._raCountdownDigits;
    var reusable =
      el._raCountdownShape === shape &&
      digits &&
      digits.length === nums.length &&
      digits.every(function (node) { return node && el.contains(node); });
    if (!reusable) {
      el.innerHTML = html;
      el._raCountdownShape = shape;
      var numSelector = isAccountRow
        ? '.rising-auctions-active-bids__time-num'
        : '.rising-auctions-tile__countdown-num';
      el._raCountdownDigits = Array.prototype.map.call(
        el.querySelectorAll(numSelector),
        function (span) { return span.firstChild; }
      );
      return;
    }
    for (var k = 0; k < nums.length; k++) {
      var value = String(nums[k]);
      if (digits[k].nodeValue !== value) digits[k].nodeValue = value;
    }
  }

  function setClassIfChanged(el, className, on) {
    if (el.classList.contains(className) !== on) el.classList.toggle(className, on);
  }

  function applyLocalTileExpiry(tile, state) {
    tile.setAttribute('data-auction-expired', 'true');
    for (var i = 0; i < state.countdownEls.length; i++) {
      state.countdownEls[i].textContent = 'ENDED';
      var countdownContainer = state.countdownContainers[i];
      if (countdownContainer) {
        countdownContainer.classList.add('rising-auctions-tile__countdown--urgent');
        countdownContainer.classList.remove('rising-auctions-tile__countdown--blink');
      }
    }
    if (state.isAccountRow) {
      var yourBidCell = tile.querySelector('.rising-auctions-active-bids__cell--your-bid');
      if (yourBidCell) yourBidCell.innerHTML = '';
      var endLabel = state.isFavouritesRow ? 'CLOSED' : 'SOLD';
      var soldClass = endLabel === 'SOLD' ? 'rising-auctions-active-bids__sold-label rising-auctions-active-bids__sold-label--ended' : 'rising-auctions-active-bids__sold-label';
      var soldLabelHtml = '<span class="' + soldClass + '">' + endLabel + '</span>';
      var increaseCell = tile.querySelector('.rising-auctions-active-bids__cell--increase');
      if (increaseCell) {
        increaseCell.innerHTML = soldLabelHtml;
      }
      tile.querySelectorAll('.rising-auctions-active-bids__mobile-card-actions').forEach(function (actions) {
        actions.innerHTML = soldLabelHtml;
      });
    } else {
      tile.querySelectorAll('[data-bid-submit]').forEach(function (plpSubmitBtn) {
        plpSubmitBtn.disabled = true;
        plpSubmitBtn.classList.add('rising-auctions-tile__btn--expired');
      });
      tile.querySelectorAll('[data-tile-bid-controls]').forEach(function (plpControls) {
        plpControls.style.display = 'none';
      });
    }
  }

  function stopSectionCountdown(section) {
    var reg = section && section._raCountdownRegistry;
    if (!reg || reg.intervalId == null) return;
    clearInterval(reg.intervalId);
    reg.intervalId = null;
  }

  function tickSectionCountdowns(section) {
    var reg = section._raCountdownRegistry;
    if (!reg || reg.tiles.size === 0) return;
    var thresholdMinutes = parseTimerThreshold(section);
    var now = raNow();
    reg.tiles.forEach(function (state, tile) {
      var diff = Math.max(0, state.endMs - now);
      if (diff <= 0) {
        applyLocalTileExpiry(tile, state);
        reg.tiles.delete(tile);
        if (reg.observer) reg.observer.unobserve(tile);
        return;
      }
      // Off-screen tiles are refreshed by the observer as they scroll into view.
      if (reg.inView && !reg.inView.has(tile)) return;
      updateCountdownDisplay(state, diff, thresholdMinutes);
    });
    if (reg.tiles.size === 0) {
      stopSectionCountdown(section);
    }
  }

  /**
   * A PLP page has up to ~75 tiles but only a handful on screen. Without this every tile
   * rewrote its countdown each second, which on a phone kept the main thread busy with
   * style/layout work (and woke other apps' MutationObservers) for the whole visit.
   * Tiles within 200px of the viewport keep ticking; others update when they come back.
   */
  function observeCountdownTile(section, reg, tile) {
    if (typeof IntersectionObserver === 'undefined' || typeof WeakSet === 'undefined') return;
    if (!reg.observer) {
      reg.inView = new WeakSet();
      reg.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) reg.inView.add(entry.target);
          else reg.inView.delete(entry.target);
          var entryState = reg.tiles.get(entry.target);
          if (!entryState || !entry.isIntersecting) return;
          var entryDiff = Math.max(0, entryState.endMs - raNow());
          if (entryDiff > 0) {
            updateCountdownDisplay(entryState, entryDiff, parseTimerThreshold(section));
          }
        });
      }, { rootMargin: '200px 0px' });
    }
    reg.observer.observe(tile);
  }

  function startSectionCountdown(section) {
    var reg = getOrCreateCountdownRegistry(section);
    if (reg.intervalId != null) return;
    tickSectionCountdowns(section);
    reg.intervalId = setInterval(function () {
      tickSectionCountdowns(section);
    }, 1000);
  }

  function registerCountdownTile(section, tile) {
    if (!section || !tile) return;
    var state = buildCountdownTileState(tile);
    if (!state) return;
    var reg = getOrCreateCountdownRegistry(section);
    reg.tiles.set(tile, state);
    observeCountdownTile(section, reg, tile);
    // Countdowns can start before any payload has fed the clock (server-rendered rows, PubNub
    // re-renders); make sure an offset is on its way.
    if (window['RAServerClock']) window['RAServerClock'].ensureSynced();
    var diff = Math.max(0, state.endMs - raNow());
    var thresholdMinutes = parseTimerThreshold(section);
    if (reg.intervalId != null) {
      if (diff <= 0) {
        applyLocalTileExpiry(tile, state);
        reg.tiles.delete(tile);
        if (reg.tiles.size === 0) stopSectionCountdown(section);
      } else {
        updateCountdownDisplay(state, diff, thresholdMinutes);
      }
    } else {
      startSectionCountdown(section);
    }
  }

  function unregisterCountdownTile(section, tile) {
    if (!section || !tile) return;
    var reg = section._raCountdownRegistry;
    if (!reg) return;
    reg.tiles.delete(tile);
    if (reg.observer) reg.observer.unobserve(tile);
    if (reg.tiles.size === 0) {
      stopSectionCountdown(section);
    }
  }

  function clearCountdownRegistry(section) {
    if (!section) return;
    var reg = section._raCountdownRegistry;
    if (!reg) return;
    reg.tiles.clear();
    if (reg.observer) {
      reg.observer.disconnect();
      reg.observer = null;
      reg.inView = null;
    }
    stopSectionCountdown(section);
  }

  function tickAllSectionCountdownsOnVisible() {
    document.querySelectorAll('.rising-auctions-grid, .rising-auctions-active-bids').forEach(function (section) {
      var reg = section._raCountdownRegistry;
      if (reg && reg.tiles.size > 0) {
        tickSectionCountdowns(section);
      }
    });
  }

  function initCountdownVisibilityHandler() {
    if (document._raCountdownVisibilityAttached) return;
    document._raCountdownVisibilityAttached = true;
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        tickAllSectionCountdownsOnVisible();
      }
    });
  }

  // DO NOT REMOVE – loader usage here is a hard UX requirement for bid feedback on all RA surfaces.
  /** Host element for window.loader during bid POST (must be a real DOM node). */
  function getBidLoaderHostElement(submitBtn) {
    if (!submitBtn) return null;
    var host =
      submitBtn.closest('.rising-auctions-active-bids__cell--increase') ||
      submitBtn.closest('.rising-auctions-tile__actions') ||
      submitBtn.closest('.auction-pdp__footer');
    return host || submitBtn.parentElement;
  }

  function parseMaxBidAmountFromSection(section) {
    if (!section) return MAX_BID_AMOUNT;
    var raw = section.getAttribute('data-max-bid-amount');
    if (raw == null || raw === '') return MAX_BID_AMOUNT;
    return getEffectiveMaxBidAmount(normalizeMaxBidAmount(parseFloat(raw, 10)));
  }

  function applyMaxBidAmountDataAttribute(section, maxBidAmount) {
    if (!section) return;
    var normalizedMaxBidAmount = normalizeMaxBidAmount(maxBidAmount);
    if (normalizedMaxBidAmount != null) {
      section.setAttribute('data-max-bid-amount', String(normalizedMaxBidAmount));
    } else {
      section.removeAttribute('data-max-bid-amount');
    }
  }

  /** Per-auction cap on a tile row or article; omit when uncapped. */
  function applyMaxBidAmountToTile(tileEl, maxBidAmount) {
    if (!tileEl) return;
    var normalizedMaxBidAmount = normalizeMaxBidAmount(maxBidAmount);
    if (normalizedMaxBidAmount != null) {
      tileEl.setAttribute('data-max-bid-amount', String(normalizedMaxBidAmount));
    } else {
      tileEl.removeAttribute('data-max-bid-amount');
    }
  }

  /** Prefer cap on the tile (per auction); fall back to section; always capped at MAX_BID_AMOUNT. */
  function parseMaxBidAmountFromTileOrSection(tile, section) {
    var auctionCap = null;
    if (tile) {
      var rawTile = tile.getAttribute('data-max-bid-amount');
      if (rawTile != null && rawTile !== '') {
        auctionCap = normalizeMaxBidAmount(parseFloat(rawTile, 10));
      }
    }
    if (auctionCap == null && section) {
      var rawSection = section.getAttribute('data-max-bid-amount');
      if (rawSection != null && rawSection !== '') {
        auctionCap = normalizeMaxBidAmount(parseFloat(rawSection, 10));
      }
    }
    return getEffectiveMaxBidAmount(auctionCap);
  }

  function computeNextSuggestedBidAmount(tile, section) {
    var currentBid = parseFloat(tile.getAttribute('data-current-bid'), 10);
    var bidIncrement = parseFloat(tile.getAttribute('data-bid-increment'), 10);
    var bidCount = parseInt(tile.getAttribute('data-bid-count'), 10);
    if (isNaN(currentBid)) currentBid = 0;
    if (isNaN(bidIncrement) || bidIncrement <= 0) bidIncrement = 1;
    if (isNaN(bidCount)) bidCount = 0;
    var minBid = normalizeBidAmount(bidCount === 0 ? currentBid : currentBid + bidIncrement);
    var maxBid = parseMaxBidAmountFromTileOrSection(tile, section);
    if (maxBid != null && minBid > maxBid) return maxBid;
    return minBid;
  }

  function applyTooHighBidInputReset(tile, input, section) {
    if (!input) return;
    var nextSuggested = computeNextSuggestedBidAmount(tile, section);
    input.value = formatBidInputValue(nextSuggested);
    if (!tile) return;
    var currentBid = parseFloat(tile.getAttribute('data-current-bid'), 10);
    var bidIncrement = parseFloat(tile.getAttribute('data-bid-increment'), 10);
    var bidCount = parseInt(tile.getAttribute('data-bid-count'), 10);
    if (isNaN(currentBid)) currentBid = 0;
    if (isNaN(bidIncrement) || bidIncrement <= 0) bidIncrement = 1;
    if (isNaN(bidCount)) bidCount = 0;
    var minBid = bidCount === 0 ? currentBid : currentBid + bidIncrement;
    var minusBtn = tile.querySelector('[data-bid-minus]');
    if (minusBtn) {
      var currentVal = parseFloat(input.value, 10) || 0;
      minusBtn.disabled = currentVal <= minBid;
    }
  }

  /**
   * Lazy-load the PubNub SDK only when it is actually needed (after auction tiles render).
   * Multiple callers queue up and all fire once the single script tag loads.
   * If the SDK is already on the page (e.g. from another section), the callback runs synchronously.
   */
  var _pnSdkLoading = false;
  var _pnSdkCallbacks = [];

  function loadPubNubSdkThen(callback) {
    if (typeof window.PubNub !== 'undefined') {
      callback();
      return;
    }
    _pnSdkCallbacks.push(callback);
    if (_pnSdkLoading) return;
    _pnSdkLoading = true;
    var sdkUrl = window.RA_PUBNUB_URL || 'https://cdn.pubnub.com/sdk/javascript/pubnub.10.2.7.min.js';
    var script = document.createElement('script');
    script.src = sdkUrl;
    script.onload = function () {
      _pnSdkLoading = false;
      var pending = _pnSdkCallbacks.splice(0);
      pending.forEach(function (cb) { try { cb(); } catch (e) {} });
    };
    script.onerror = function () {
      _pnSdkLoading = false;
      _pnSdkCallbacks = [];
    };
    document.head.appendChild(script);
  }

  /**
   * Paint a live price on every tile of one auction. `msg` is a PubNub bid message
   * ({ price, betCount, winningInitials, highestBidderId }) or the same fields taken from a
   * fresh list read (see reconcileRaGridLivePrices).
   */
  function applyLiveBidToTiles(matched, msg, customerId, currencySymbol, section) {
    matched.forEach(function (tile) {
      var shownBidCount = parseInt(tile.getAttribute('data-bid-count'), 10);
      if (typeof msg.betCount === 'number' && !isNaN(shownBidCount) && msg.betCount < shownBidCount) {
        return; // Older than what the tile already shows.
      }
      var price = msg.price != null ? msg.price : parseFloat(tile.getAttribute('data-current-bid'), 10);
      var betCount = typeof msg.betCount === 'number' ? msg.betCount : parseInt(tile.getAttribute('data-bid-count'), 10);
      var isCurrentUser = !!(customerId && msg.highestBidderId && msg.highestBidderId === customerId);
      tile.setAttribute('data-current-bid', String(price));
      tile.setAttribute('data-bid-count', String(betCount));
      var bidAmountEl = tile.querySelector('.rising-auctions-tile__bid-amount');
      setTileBidAmountDisplay(bidAmountEl, price, currencySymbol);
      var bidCountEl = tile.querySelector('.rising-auctions-tile__bid-count');
      if (bidCountEl) bidCountEl.textContent = '(' + betCount + ' bids)';
      var winnerText = isCurrentUser ? "You're winning!" : (msg.winningInitials || '') + ' is winning!';
      tile.querySelectorAll('.rising-auctions-tile__winner-pill').forEach(function (winnerPill) {
        winnerPill.className = 'rising-auctions-tile__winner-pill' + (isCurrentUser ? ' rising-auctions-tile__winner-pill--winning' : '');
        winnerPill.innerHTML = getTileStatusIconHtml(isCurrentUser ? 'thumbs-up' : 'thumbs-down') + winnerText;
      });
      raQwSyncPdpBidRowFromPubNub(
        tile,
        price,
        betCount,
        isCurrentUser,
        msg.winningInitials || '',
        currencySymbol
      );
      if (tile.classList && tile.classList.contains('rising-auctions-active-bids__row')) {
        tile.querySelectorAll('.rising-auctions-active-bids__current-bid').forEach(function (currentBidCell) {
          currentBidCell.textContent = formatMoney(price, currencySymbol);
        });
        var timeCell = tile.querySelector('.rising-auctions-active-bids__cell--time');
        var mobileTimeCell = tile.querySelector('.rising-auctions-active-bids__mobile-card-time');
        if (timeCell) {
          var desktopYourBid = tile.querySelector('.rising-auctions-active-bids__cell--your-bid .rising-auctions-active-bids__your-bid-amount');
          var favouritesHasUserBid = !(tile.hasAttribute('data-favourites-row') && desktopYourBid && desktopYourBid.textContent.trim() === '-');
          if (isCurrentUser) {
            tile.querySelectorAll('.rising-auctions-active-bids__outbid-msg').forEach(function (outbidMsgEl) {
              outbidMsgEl.remove();
            });
          } else if (!favouritesHasUserBid) {
            tile.querySelectorAll('.rising-auctions-active-bids__outbid-msg').forEach(function (outbidMsgEl) {
              outbidMsgEl.remove();
            });
          } else {
            if (!timeCell.querySelector('.rising-auctions-active-bids__outbid-msg')) {
              var p = document.createElement('p');
              p.className = 'rising-auctions-active-bids__outbid-msg';
              p.textContent = 'You have been outbid. Please place a higher bid for a chance to win this item.';
              timeCell.appendChild(p);
            }
            if (mobileTimeCell && !mobileTimeCell.querySelector('.rising-auctions-active-bids__outbid-msg')) {
              var mobileP = document.createElement('p');
              mobileP.className = 'rising-auctions-active-bids__outbid-msg';
              mobileP.textContent = 'You have been outbid. Please place a higher bid for a chance to win this item.';
              mobileTimeCell.appendChild(mobileP);
            }
          }
        }
      }
      var controls = tile.querySelectorAll('[data-tile-bid-controls]');
      if (controls.length) {
        controls.forEach(function (controls) {
        var inp = controls.querySelector('[data-bid-input]');
        if (inp) {
          var bidIncrement = parseFloat(tile.getAttribute('data-bid-increment'), 10);
          if (isNaN(bidIncrement) || bidIncrement <= 0) bidIncrement = 1;
          var nextBid = betCount === 0 ? price : price + bidIncrement;
          var maxBid = parseMaxBidAmountFromTileOrSection(tile, section);
          if (maxBid != null && nextBid > maxBid) nextBid = maxBid;
          inp.value = formatBidInputValue(nextBid);
          validateAuctionBidInput(tile, inp, { silent: true });
        }
        });
      } else {
        var pdpInpPub = tile.querySelector('.auction-pdp__input[data-bid-input]');
        if (pdpInpPub) {
          var bidIncP = parseFloat(tile.getAttribute('data-bid-increment'), 10);
          if (isNaN(bidIncP) || bidIncP <= 0) bidIncP = 1;
          var nextBidP = betCount === 0 ? price : price + bidIncP;
          var maxBidP = parseMaxBidAmountFromTileOrSection(tile, section);
          if (maxBidP != null && nextBidP > maxBidP) nextBidP = maxBidP;
          pdpInpPub.value = formatBidInputValue(nextBidP);
          validateAuctionBidInput(tile, pdpInpPub, { silent: true });
        }
      }
    });
  }

  function initRisingAuctionsPubNub(container) {
    if (!container) return;

    var tiles = container.querySelectorAll('[data-auction-id]');
    var auctionIds = [];
    tiles.forEach(function (el) {
      var id = (el.getAttribute('data-auction-id') || '').trim();
      if (id && auctionIds.indexOf(id) < 0) auctionIds.push(id);
    });

    if (auctionIds.length === 0) return;

    var channels = auctionIds.map(function (id) { return 'auction-' + id; });
    var section = container.closest('.rising-auctions-grid') || container.closest('.rising-auctions-active-bids') || container;
    var customerId = (section && section.getAttribute('data-customer-id')) || '';
    var currencySymbol = (section && section.getAttribute('data-currency-symbol')) || '£';

    var configUrl = '/apps/rising-auctions/pubnub-config';
    if (customerId) {
      configUrl += (configUrl.indexOf('?') >= 0 ? '&' : '?') + 'logged_in_customer_id=' + encodeURIComponent(customerId);
    }
    fetch(configUrl, { credentials: 'include' })
      .then(function (r) { return r.json(); })
      .then(function (config) {
        if (!config || !config.subscribeKey) return;

        loadPubNubSdkThen(function () {
        if (typeof window.PubNub === 'undefined') return;

        // Each grid render calls this again. Drop the previous client so old pages do not
        // keep their channels (and a second long-poll) open for the rest of the visit.
        if (container._raPubNub) {
          try {
            container._raPubNub.unsubscribeAll();
            container._raPubNub.stop();
          } catch (e) {}
          container._raPubNub = null;
        }

        var pubnub = new window.PubNub({
          subscribeKey: config.subscribeKey,
          userId: config.userId || 'anon-' + Math.random().toString(36).slice(2, 12)
        });
        container._raPubNub = pubnub;

        pubnub.addListener({
            // Messages sent while the connection was down are not replayed, so read the
            // prices again after a reconnect.
            status: function (statusEvent) {
              var category = statusEvent && statusEvent.category;
              if (category === 'PNReconnectedCategory' || category === 'PNNetworkUpCategory') {
                reconcileRaGridLivePrices(section);
              }
            },
            message: function (event) {
              var msg = event && event.message;

              if (!msg) return;

              var auctionId = msg.auctionId || (event.channel && event.channel.replace(/^auction-/, ''));

              if (!auctionId) return;

              var candidates = container.querySelectorAll('[data-auction-id]');
              var matched = [];
              for (var i = 0; i < candidates.length; i++) {
                if (candidates[i].getAttribute('data-auction-id') === auctionId) matched.push(candidates[i]);
              }

              if (matched.length === 0) return;

              if (msg.type === 'bid') {
                applyLiveBidToTiles(matched, msg, customerId, currencySymbol, section);
              } else if (msg.type === 'ended') {
                var endedPayload = {
                  displayState: msg.displayState || 'CLOSED',
                  winnerInitials: msg.winnerInitials,
                  currentBid: msg.currentBid != null ? msg.currentBid : 0,
                  bidCount: typeof msg.bidCount === 'number' ? msg.bidCount : 0,
                  isUserWinner: !!(customerId && msg.highestBidderId && msg.highestBidderId === customerId),
                  currencySymbol: currencySymbol
                };
                var anyWinner = false;
                matched.forEach(function (tile) {
                  var p = {
                    displayState: endedPayload.displayState,
                    winnerInitials: endedPayload.winnerInitials,
                    currentBid: msg.currentBid != null ? msg.currentBid : parseFloat(tile.getAttribute('data-current-bid'), 10),
                    bidCount: typeof msg.bidCount === 'number' ? msg.bidCount : parseInt(tile.getAttribute('data-bid-count'), 10),
                    isUserWinner: endedPayload.isUserWinner,
                    currencySymbol: currencySymbol
                  };
                  if (tile.classList && tile.classList.contains('rising-auctions-active-bids__row')) {
                    applyEndedStateToRow(tile, p);
                  } else {
                    applyEndedStateToTile(tile, p);
                  }
                  if (p.isUserWinner) anyWinner = true;
                });
                if (anyWinner) {
                  document.dispatchEvent(new CustomEvent('rising-auctions:refresh-pay-now-badge'));
                  setTimeout(function () {
                    document.dispatchEvent(new CustomEvent('rising-auctions:refresh-pay-now-badge'));
                  }, 1500);
                }
              }
            }
          });

        pubnub.subscribe({ channels: channels });
        }); // end loadPubNubSdkThen — SDK injected lazily, only after auction tiles are known
      })
      .catch(function () {});
  }

  function applyEndedStateToRow(row, payload) {
    if (!row || !payload || !payload.displayState) return;
    unregisterCountdownTile(getBidSectionRoot(row), row);

    var currencySymbol = payload.currencySymbol || '£';
    var state = payload.displayState;

    row.setAttribute('data-auction-state', state);
    row.classList.remove('rising-auctions-tile--active', 'rising-auctions-tile--winning');
    row.classList.add('rising-auctions-tile--' + state);
    var countdownEls = row.querySelectorAll('[data-countdown-text]');
    countdownEls.forEach(function (countdownEl) {
      countdownEl.textContent = 'ENDED';
      var countdownContainer = countdownEl.closest('[data-tile-countdown]');
      if (countdownContainer) {
        countdownContainer.classList.add('rising-auctions-tile__countdown--urgent');
        countdownContainer.classList.remove('rising-auctions-tile__countdown--blink');
      }
    });
    row.querySelectorAll('.rising-auctions-active-bids__current-bid').forEach(function (currentBidCell) {
      currentBidCell.textContent = formatMoney(payload.currentBid, currencySymbol);
    });
    var yourBidCell = row.querySelector('.rising-auctions-active-bids__cell--your-bid');
    if (yourBidCell) yourBidCell.innerHTML = '';
    var increaseCell = row.querySelector('.rising-auctions-active-bids__cell--increase');
    var soldLabelHtml = '';
    if (increaseCell) {
      var soldClass = 'rising-auctions-active-bids__sold-label';
      if (state === 'CLOSED') {
        soldLabelHtml = '<span class="' + soldClass + '">CLOSED</span>';
      } else if (state === 'PENDING' && payload.isUserWinner) {
        soldLabelHtml = '<span class="' + soldClass + '">Pay now</span>';
      } else {
        soldClass = state === 'SOLD' ? soldClass + ' rising-auctions-active-bids__sold-label--ended' : soldClass;
        soldLabelHtml = '<span class="' + soldClass + '">' + state + '</span>';
      }
      increaseCell.innerHTML = soldLabelHtml;
    }
    if (soldLabelHtml) {
      row.querySelectorAll('.rising-auctions-active-bids__mobile-card-actions').forEach(function (actions) {
        actions.innerHTML = soldLabelHtml;
      });
    }
    row.querySelectorAll('[data-tile-bid-controls]').forEach(function (bidInputWrap) {
      bidInputWrap.style.display = 'none';
    });
  }

  /**
   * Compact ended-state markup for PLP tiles (Figma sold block — trophy, status, price stack).
   */
  function buildPlpEndedSoldBlockHtml(options) {
    var variant = options && options.variant;
    if (variant === 'closed') {
      return (
        '<div class="rising-auctions-tile__sold-block rising-auctions-tile__sold-block--closed">' +
          '<p class="rising-auctions-tile__sold-status">AUCTION ENDED!</p>' +
          '<p class="rising-auctions-tile__no-bids">There have been no bids</p>' +
        '</div>'
      );
    }
    var statusText = options.statusText || '';
    var bidDisplay = options.bidDisplay || '';
    var bidCount = typeof options.bidCount === 'number' ? options.bidCount : 0;
    var isWon = variant === 'won';
    return (
      '<div class="rising-auctions-tile__sold-block">' +
        '<p class="rising-auctions-tile__sold-status-row' + (isWon ? ' rising-auctions-tile__sold-status-row--won' : '') + '">' +
          '<span class="rising-auctions-tile__ended-trophy rising-auctions-tile__ended-trophy--gold" aria-hidden="true">&#127942;</span>' +
          '<span class="rising-auctions-tile__sold-status' + (isWon ? ' rising-auctions-tile__sold-status--won' : '') + '">' +
            statusText +
          '</span>' +
        '</p>' +
        '<p class="rising-auctions-tile__sold-bid">' +
          '<span class="rising-auctions-tile__bid-amount">' + escapeHtml(bidDisplay) + '</span>' +
          ' <span class="rising-auctions-tile__bid-count">(' + bidCount + ' BIDS)</span>' +
        '</p>' +
      '</div>'
    );
  }

  /**
   * Apply ended state (SOLD / CLOSED / WON) to a PLP grid tile. Used when PubNub delivers
   * the final state for an auction. Payload: { displayState: 'SOLD'|'CLOSED'|'WON', winnerInitials?: string, currentBid, bidCount, isUserWinner }.
   * Only affects tiles with .rising-auctions-tile (grid cards), not Active Bids table rows.
   */
  function applyEndedStateToTile(tile, payload) {
    if (!tile || !payload || !payload.displayState) return;
    if (tile.classList && tile.classList.contains('rising-auctions-active-bids__row')) return;
    unregisterCountdownTile(getBidSectionRoot(tile), tile);

    var qwMount = tile.closest('[data-ra-qw-bid-mount]');
    if (qwMount && tile.querySelector('.auction-pdp')) {
      tile.setAttribute('data-auction-state', String(payload.displayState || '').toLowerCase());
      raQwApplyPdpEndedToArticle(tile, {
        displayState: String(payload.displayState || '').toUpperCase(),
        winnerInitials: payload.winnerInitials,
        currentBid: payload.currentBid,
        bidCount: payload.bidCount,
        isUserWinner: payload.isUserWinner === true,
        currencySymbol: payload.currencySymbol,
      });
      return;
    }

    var section = getBidSectionRoot(tile);
    var currencySymbol = (section && section.getAttribute('data-currency-symbol')) || payload.currencySymbol || '£';
    var state = String(payload.displayState || '').toUpperCase();
    var stateLower = state.toLowerCase();
    var bidDisplay = formatMoney(payload.currentBid, currencySymbol);
    var bidCount = typeof payload.bidCount === 'number' ? payload.bidCount : 0;
    var winnerInitials = payload.winnerInitials || '';
    var isUserWinner = payload.isUserWinner === true;
    var userWonGrid = state === 'WON' || (isUserWinner && (state === 'PENDING' || state === 'SOLD'));
    var tileVisualState = stateLower;
    if (state === 'PENDING') tileVisualState = userWonGrid ? 'won' : 'sold';

    tile.setAttribute('data-auction-state', stateLower);
    tile.setAttribute('data-confetti-user-winner', userWonGrid ? 'true' : 'false');
    tile.classList.remove('rising-auctions-tile--active', 'rising-auctions-tile--winning');
    tile.classList.add('rising-auctions-tile--' + tileVisualState, 'rising-auctions-tile--ended');

    var countdownRow = tile.querySelector('.rising-auctions-tile__countdown-row');
    if (countdownRow) countdownRow.hidden = true;

    var actions = tile.querySelector('.rising-auctions-tile__actions');
    if (!actions) return;
    var container = tile.querySelector('.rising-auctions-tile__actions-state--container');
    var pricing = tile.querySelector('.rising-auctions-tile__actions-state--pricing');
    var ctaWrap = tile.querySelector('.rising-auctions-tile__actions-state--ctas');
    var bidStatus = tile.querySelector('.rising-auctions-tile__actions-state--bid-status');

    if (!container || !pricing) return;

    if (state === 'CLOSED') {
      container.innerHTML = buildPlpEndedSoldBlockHtml({ variant: 'closed' });
      pricing.innerHTML = '';
      if (ctaWrap) ctaWrap.innerHTML = '';
      if (bidStatus) bidStatus.innerHTML = '';
    } else {
      if (userWonGrid) {
        container.innerHTML = buildPlpEndedSoldBlockHtml({
          variant: 'won',
          statusText: 'YOU WON!',
          bidDisplay: bidDisplay,
          bidCount: bidCount,
        });
      } else {
        container.innerHTML = buildPlpEndedSoldBlockHtml({
          variant: 'lost',
          statusText: escapeHtml(winnerInitials || '—') + ' WON!',
          bidDisplay: bidDisplay,
          bidCount: bidCount,
        });
      }
      pricing.innerHTML = '';
      if (userWonGrid) {
        if (bidStatus) bidStatus.innerHTML = '';
        if (ctaWrap) {
          var auctionId = tile.getAttribute('data-auction-id') || '';
          var variantId = tile.getAttribute('data-variant-id') || '';
          var variantSku = tile.getAttribute('data-variant-sku') || '';
          var mainCode = tile.getAttribute('data-ra-main-product-code') || '';
          var amsCode = tile.getAttribute('data-ams-auction-code') || '';
          var basePriceVal = tile.getAttribute('data-base-price') || '';
          if (payload.mainProductCode != null && String(payload.mainProductCode).trim() !== '') {
            mainCode = String(payload.mainProductCode);
          }
          if (payload.amsAuctionCode != null && String(payload.amsAuctionCode).trim() !== '') {
            amsCode = String(payload.amsAuctionCode);
          }
          if (payload.price != null && !isNaN(Number(payload.price))) {
            basePriceVal = formatRaBasePriceDecimal(payload.price);
          }
          ctaWrap.innerHTML = '<button type="button" class="rising-auctions-tile__btn rising-auctions-tile__btn--primary" data-plp-add-to-bag data-auction-id="' + escapeHtml(auctionId) + '" data-ams-auction-code="' + escapeHtml(amsCode) + '" data-variant-id="' + escapeHtml(variantId) + '" data-variant-sku="' + escapeHtml(variantSku) + '" data-ra-main-product-code="' + escapeHtml(mainCode) + '" data-base-price="' + escapeHtml(basePriceVal) + '">Add to bag</button>';
          initAddToBagPlaceholder(tile);
          refreshPlpAddToBagStates(getBidSectionRoot(tile));
        }
      } else {
        if (bidStatus) bidStatus.innerHTML = '';
        if (ctaWrap) ctaWrap.innerHTML = '';
      }
    }
    if (tileVisualState === 'sold' || userWonGrid) launchTileConfetti(tile);
  }

  /**
   * Add a short, pointer-safe celebration over a tile.
   * Theme settings control whether this runs for every sold tile or only a customer win.
   */
  function launchTileConfetti(tile) {
    if (!tile || !tile.classList.contains('rising-auctions-tile')) return;
    var section = getBidSectionRoot(tile);
    if (!section || section.getAttribute('data-sold-confetti-enabled') !== 'true') return;
    var mode = section.getAttribute('data-sold-confetti-mode') || 'all_sold';
    if (mode === 'customer_won' && tile.getAttribute('data-confetti-user-winner') !== 'true') return;
    if (tile.querySelector('.rising-auctions-tile__confetti')) return;

    var colors = ['#BC0614', '#F5B700', '#198754', '#6F42C1', '#0D6EFD', '#FF6B9A'];
    var overlay = document.createElement('div');
    overlay.className = 'rising-auctions-tile__confetti';
    overlay.setAttribute('aria-hidden', 'true');

    for (var i = 0; i < 42; i++) {
      var piece = document.createElement('i');
      piece.className = 'rising-auctions-tile__confetti-piece';
      piece.style.setProperty('--confetti-x', (Math.random() * 100).toFixed(2) + '%');
      piece.style.setProperty('--confetti-width', (5 + Math.random() * 5).toFixed(1) + 'px');
      piece.style.setProperty('--confetti-height', (7 + Math.random() * 7).toFixed(1) + 'px');
      piece.style.setProperty('--confetti-color', colors[i % colors.length]);
      piece.style.setProperty('--confetti-drift', (-45 + Math.random() * 90).toFixed(1) + 'px');
      piece.style.setProperty('--confetti-spin', (360 + Math.random() * 720).toFixed(0) + 'deg');
      piece.style.setProperty('--confetti-duration', (2.2 + Math.random() * 1.5).toFixed(2) + 's');
      piece.style.setProperty('--confetti-delay', (Math.random() * 1.1).toFixed(2) + 's');
      overlay.appendChild(piece);
    }

    tile.appendChild(overlay);
    window.setTimeout(function () {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 5000);
  }

  function initSoldTileConfettiObserver() {
    if (window._raSoldTileConfettiObserver || !document.body) return;

    window._raSoldTileConfettiObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'attributes') return;
        var tile = mutation.target;
        var previousClasses = mutation.oldValue || '';
        var currentStateClass = tile.classList && tile.classList.contains('rising-auctions-tile--won')
          ? 'rising-auctions-tile--won'
          : 'rising-auctions-tile--sold';
        if (
          tile.classList &&
          tile.classList.contains('rising-auctions-tile') &&
          tile.classList.contains(currentStateClass) &&
          previousClasses.split(/\s+/).indexOf(currentStateClass) === -1
        ) {
          launchTileConfetti(tile);
        }
      });
    });

    window._raSoldTileConfettiObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
      subtree: true,
    });
  }

  function variantIdForCart(gid) {
    if (gid == null) return '';
    var m = String(gid).match(/ProductVariant\/(\d+)/);
    return m ? m[1] : gid;
  }

  function setPlpAddToBagState(btn, inBag) {
    if (!btn) return;
    var isPdpBag = btn.classList.contains('auction-pdp__add-to-bag');
    if (inBag) {
      btn.textContent = 'Already in bag';
      btn.disabled = true;
      btn.classList.add('rising-auctions-tile__btn--in-bag');
      if (isPdpBag) btn.classList.add('auction-pdp__add-to-bag--in-bag');
    } else {
      btn.textContent = 'Add to bag';
      btn.disabled = false;
      btn.classList.remove('rising-auctions-tile__btn--in-bag');
      if (isPdpBag) btn.classList.remove('auction-pdp__add-to-bag--in-bag');
    }
  }

  function refreshPlpAddToBagStates(section) {
    if (!section) return;
    var buttons = section.querySelectorAll('[data-plp-add-to-bag]');
    if (buttons.length === 0) return;
    fetchCartWithCache()
      .then(function (cart) {
        var items = cart && cart.items ? cart.items : [];
        var variantIdsInCart = new Set(items.map(function (item) { return String(item.variant_id); }));
        buttons.forEach(function (btn) {
          var gid = btn.getAttribute('data-variant-id');
          var num = variantIdForCart(gid);
          if (!num) return;
          var inCart = variantIdsInCart.has(String(num));
          var markedInBag =
            btn.classList.contains('rising-auctions-tile__btn--in-bag') ||
            btn.classList.contains('auction-pdp__add-to-bag--in-bag');
          // Only flip mismatches so an in-flight add (disabled but not yet marked) is left alone.
          if (inCart !== markedInBag) setPlpAddToBagState(btn, inCart);
        });
      })
      .catch(function () {});
  }

  /**
   * Cart data cache for Add-to-Bag state sync.
   *
   * WHY: refreshPlpAddToBagStates() was called multiple times in quick succession —
   * once after each page of auctions loads AND once per cart:update / cart:updated event.
   * Each call independently hit /cart.js, causing 4-6 duplicate network requests on a
   * single page load (confirmed in the HAR: 6× /cart.js calls).
   *
   * HOW: We cache the last /cart.js response for RA_CART_CACHE_TTL_MS (5 seconds).
   * If the same data is requested again within that window, we return the cached Promise
   * instead of issuing a new fetch. When a real cart change fires (cart:update /
   * cart:updated), invalidatePlpCartCache() resets the cache so the next read is fresh.
   */
  var _plpCartFetchPromise = null;
  var _plpCartCacheTime = 0;
  var RA_CART_CACHE_TTL_MS = 5000;

  function fetchCartWithCache() {
    var now = Date.now();
    if (_plpCartFetchPromise && (now - _plpCartCacheTime) < RA_CART_CACHE_TTL_MS) {
      return _plpCartFetchPromise;
    }
    _plpCartCacheTime = now;
    _plpCartFetchPromise = fetch('/cart.js', { credentials: 'include' })
      .then(function (r) { return r.json(); })
      .catch(function () {
        _plpCartFetchPromise = null;
        return { items: [] };
      });
    return _plpCartFetchPromise;
  }

  function invalidatePlpCartCache() {
    _plpCartFetchPromise = null;
    _plpCartCacheTime = 0;
  }

  var plpCartSyncTimer = null;

  /** Re-sync Add to Bag buttons when the cart changes (e.g. item removed from mini cart). */
  function schedulePlpAddToBagCartSync() {
    invalidatePlpCartCache(); // cart changed — next read must be fresh
    if (plpCartSyncTimer) clearTimeout(plpCartSyncTimer);
    plpCartSyncTimer = setTimeout(function () {
      plpCartSyncTimer = null;
      refreshPlpAddToBagStates(document);
    }, 200);
  }

  function initAddToBagPlaceholder(tile) {
    var btn = tile.querySelector('[data-plp-add-to-bag]');
    if (!btn || btn.dataset.raAddToBagInit) return;
    btn.dataset.raAddToBagInit = 'true';
    btn.addEventListener('click', function () {
      if (
        this.disabled ||
        this.classList.contains('rising-auctions-tile__btn--in-bag') ||
        this.classList.contains('auction-pdp__add-to-bag--in-bag')
      ) {
        return;
      }
      var variantIdGid = this.getAttribute('data-variant-id');
      var variantIdNum = variantIdForCart(variantIdGid);
      if (!variantIdNum) return;
      var self = this;
      var el = this;
      var auctionId = el.getAttribute('data-auction-id') || '';
      var amsAuctionCode = el.getAttribute('data-ams-auction-code') || '';
      var raMain = el.getAttribute('data-ra-main-product-code') || '';
      var variantSku = el.getAttribute('data-variant-sku') || '';
      var basePrice = el.getAttribute('data-base-price') || '';
      var paymentExpirationAt = el.getAttribute('data-payment-expiration-at') || '';
      // Button also has data-auction-id — start from parent so we reach the tile/article.
      var art = el.parentElement && el.parentElement.closest('[data-auction-id]');
      if (art) {
        if (!auctionId) auctionId = art.getAttribute('data-auction-id') || '';
        if (!amsAuctionCode) amsAuctionCode = art.getAttribute('data-ams-auction-code') || '';
        if (!raMain) raMain = art.getAttribute('data-ra-main-product-code') || '';
        if (!variantSku) variantSku = art.getAttribute('data-variant-sku') || '';
        if (!basePrice) basePrice = art.getAttribute('data-base-price') || '';
        if (!paymentExpirationAt) paymentExpirationAt = art.getAttribute('data-payment-expiration-at') || '';
      }
      var RA = window.RA;
      var validate = RA && typeof RA.validateAmsCartFieldsForAdd === 'function' ? RA.validateAmsCartFieldsForAdd : function () { return { ok: true }; };
      var validation = validate({ amsAuctionCode: amsAuctionCode, auctionId: auctionId, raMainProductCode: raMain });
      if (!validation.ok) {
        console.warn('[RA] Add to bag blocked:', validation.message);
        if (RA && typeof RA.reportMissingAmsCartFields === 'function') {
          RA.reportMissingAmsCartFields({
            auctionId: auctionId,
            source: 'plp',
            amsAuctionCode: amsAuctionCode,
            raMainProductCode: raMain
          });
        }
        alert(validation.message);
        return;
      }
      this.disabled = true;
      var ensureBasket = RA && typeof RA.ensureBasketId === 'function' ? RA.ensureBasketId : function () { return Promise.resolve(''); };
      var buildProps = RA && typeof RA.buildLineItemProperties === 'function' ? RA.buildLineItemProperties : function (o) { return o || {}; };
      ensureBasket()
        .then(function (basketId) {
          var props = buildProps({
            amsAuctionCode: amsAuctionCode,
            auctionId: auctionId,
            raMainProductCode: raMain,
            variantSku: variantSku,
            basePrice: basePrice,
            paymentExpirationAt: paymentExpirationAt,
            basketId: basketId || ''
          });
          return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(variantIdNum, 10), quantity: 1, properties: props }] })
          });
        })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.status && data.status !== 200) {
            self.disabled = false;
            return;
          }
          if (RA && typeof RA.syncCartTokenAfterAdd === 'function') {
            RA.syncCartTokenAfterAdd();
          }
          setPlpAddToBagState(self, true);
          try { document.dispatchEvent(new CustomEvent('cart:updated', { detail: data })); } catch (e) {}
          try { document.dispatchEvent(new CustomEvent('cart:update', { detail: { data: { source: 'product-form-component' } } })); } catch (e) {}
        })
        .catch(function () { self.disabled = false; });
    });
    refreshPlpAddToBagStates(getBidSectionRoot(tile));
  }

  /**
   * Countdown row + bid actions only (shared by grid tile and quickview bid column).
   * options: { isLoggedIn, loginUrl, state (default 'active') }
   */
  function buildAuctionTileCountdownAndActionsHtml(payload, currencySymbol, options) {
    var isLoggedIn = options && options.isLoggedIn === true;
    var loginUrl = (options && options.loginUrl) || '/account/login';
    var state = (options && options.state) || payload.auctionState || 'active';
    var symbol = currencySymbol || '£';
    var bidIncrement = typeof payload.bidIncrement === 'number' && !isNaN(payload.bidIncrement) ? payload.bidIncrement : 1;
    var nextBid = payload.currentBid + bidIncrement;
    var inputVal = payload.bidCount === 0 ? formatBidInputValue(payload.currentBid) : formatBidInputValue(nextBid);
    var winningText = payload.bidStatusText != null ? payload.bidStatusText : (payload.bidCount === 0 ? 'Be the first to bid' : (payload.winningInitials ? payload.winningInitials + ' is winning!' : '— is winning!'));
    var noBids = payload.bidCount === 0;
    var hasUserBid = payload.hasUserBid === true;
    var isUserWinning = payload.isUserWinning === true;
    var statusIconHtml = noBids ? '' : getTileStatusIconHtml(isUserWinning ? 'thumbs-up' : 'thumbs-down');
    var winnerPillClass = 'rising-auctions-tile__winner-pill' + (noBids ? ' rising-auctions-tile__winner-pill--no-bids' : '') + (isUserWinning ? ' rising-auctions-tile__winner-pill--winning' : '');
    var ctaHtml = !isLoggedIn
      ? '<a href="' + escapeHtml(loginUrl) + '" class="rising-auctions-tile__btn rising-auctions-tile__btn--login">Login to bid</a>'
      : hasUserBid
        ? '<button type="button" class="rising-auctions-tile__btn rising-auctions-tile__btn--increase" data-bid-submit>Increase bid</button>'
        : '<button type="button" class="rising-auctions-tile__btn rising-auctions-tile__btn--primary" data-bid-submit>Bid now</button>';
    var initialCountdownHtml = '';
    if (payload.countdownEndsAt) {
      var countdownEndMs = new Date(payload.countdownEndsAt).getTime();
      initialCountdownHtml = formatPlpCountdownHtmlFromEndMs(countdownEndMs);
    }
    return (
      '<div class="rising-auctions-tile__countdown-row">' +
        '<span class="rising-auctions-tile__countdown" data-tile-countdown aria-live="polite">' +
          '<span class="rising-auctions-tile__countdown-icon" aria-hidden="true"></span>' +
          '<span data-countdown-text>' + initialCountdownHtml + '</span>' +
        '</span>' +
      '</div>' +
      '<div class="rising-auctions-tile__actions rising-auctions-tile__actions--' + state + '">' +
        '<div class="rising-auctions-tile__actions-state--container">' +
          '<p class="rising-auctions-tile__bid">' +
            '<span class="rising-auctions-tile__bid-amount">' +
            buildTileBidAmountInnerHtml(payload.currentBid, symbol) +
            '</span>' +
            '<span class="rising-auctions-tile__bid-count">(' + payload.bidCount + ' bids)</span>' +
          '</p>' +
        '</div>' +
        '<div class="rising-auctions-tile__actions-state--pricing">' +
          '<div class="rising-auctions-tile__bid-controls" data-tile-bid-controls>' +
            '<button type="button" class="rising-auctions-tile__bid-btn" data-bid-minus aria-label="Decrease bid">−</button>' +
            '<span class="rising-auctions-tile__bid-input-wrap">' +
              '<span class="rising-auctions-tile__bid-input-value">' +
              '<span class="rising-auctions-tile__bid-prefix" aria-hidden="true">' + escapeHtml(symbol) + '</span>' +
              '<input type="number" class="rising-auctions-tile__bid-input" value="' + inputVal + '" min="0" max="99999" step="1" data-bid-input aria-label="Bid amount in ' + escapeHtml(symbol) + '">' +
              '</span>' +
            '</span>' +
            '<button type="button" class="rising-auctions-tile__bid-btn" data-bid-plus aria-label="Increase bid">+</button>' +
          '</div>' +
        '</div>' +
        '<div class="rising-auctions-tile__actions-state--ctas">' +
          ctaHtml +
        '</div>' +
        '<div class="rising-auctions-tile__actions-state--bid-status">' +
          '<span class="' + winnerPillClass + '">' +
            statusIconHtml + escapeHtml(winningText) +
          '</span>' +
        '</div>' +
      '</div>'
    );
  }

  /**
   * Shopify/SFCC placeholder sizes are not real variant sizes.
   * Hide them so PLP tiles stay empty when AMS has no size (PDP must match).
   */
  var AUCTION_PLACEHOLDER_SIZES = {
    'default title': true,
    'one size': true,
    'n/a': true,
    na: true,
    none: true,
    '-': true
  };

  function normalizeAuctionVariantSize(rawSize) {
    var size = rawSize == null ? '' : String(rawSize).trim();
    if (!size) return '';
    if (AUCTION_PLACEHOLDER_SIZES[size.toLowerCase()]) return '';
    return size;
  }

  /**
   * Normalize auction variant size for PLP tile + Quick View.
   * Prefers dedicated API field when present; falls back to option1.
   * Hides blank values and placeholders ("Default Title", "One Size").
   */
  function getAuctionVariantSize(payload) {
    if (!payload) return '';
    var rawSize = payload.variantSize != null ? payload.variantSize : payload.variantOption1;
    return normalizeAuctionVariantSize(rawSize);
  }

  /** Active Bids and Favourites: API size, then `(Sz: …)` in the title, then the `sz-r-` handle prefix. */
  function sizeFromAuctionHandle(url) {
    var path = String(url || '').split('?')[0];
    var handle = path.split('/').pop() || '';
    var match = handle.match(/^sz-([a-z0-9]+)(?:-(\d+)-(\d+))?-/i);
    if (!match) return '';
    var size = /^[a-z]$/i.test(match[1]) ? match[1].toUpperCase() : match[1];
    if (match[2] && match[3]) size = size + ' ' + match[2] + '/' + match[3];
    return size;
  }

  function sizeFromAuctionTitle(title) {
    var match = String(title || '').match(/\(Sz:\s*([^)]+)\)/i);
    return match ? match[1].trim() : '';
  }

  function getActiveBidsSize(payload) {
    if (!payload) return '';
    return getAuctionVariantSize(payload) || normalizeAuctionVariantSize(sizeFromAuctionTitle(payload.title)) || normalizeAuctionVariantSize(sizeFromAuctionHandle(payload.url));
  }

  /** TWS-1032: RA PLP/PDP star ratings shown only when average is at least this value. */
  var RA_MIN_RATING_TO_SHOW = 3;

  /**
   * Read averageRating / reviewCount from active-auctions tile payload (and common aliases).
   * @returns {{ average: number, count: number } | null}
   */
  function getAuctionTileRating(payload) {
    if (!payload) return null;
    var rawAvg =
      payload.averageRating != null
        ? payload.averageRating
        : payload.productRating != null
          ? payload.productRating
          : payload.rating;
    var rawCount =
      payload.reviewCount != null
        ? payload.reviewCount
        : payload.ratingCount != null
          ? payload.ratingCount
          : payload.totalReviews;
    var average = typeof rawAvg === 'number' ? rawAvg : parseFloat(rawAvg);
    var count = typeof rawCount === 'number' ? rawCount : parseInt(rawCount, 10);
    if (isNaN(average) || isNaN(count) || count <= 0) return null;
    return { average: average, count: count };
  }

  /**
   * PLP star rating under title — same markup/classes as snippets/product-reviews.liquid
   * (collection deferred cards: shaded, stars_size 14, show_number, rating_count_font_size 16).
   * Uses theme #rating-star sprite from layout/theme.liquid.
   * Respects section setting data-show-tile-rating / options.showRating (default on).
   */
  function buildAuctionTileRatingHtml(payload, options) {
    if (options && options.showRating === false) return '';
    var ratingInfo = getAuctionTileRating(payload);
    if (!ratingInfo || ratingInfo.average < RA_MIN_RATING_TO_SHOW) return '';

    var rating = Math.min(5, Math.max(0, ratingInfo.average));
    var scaleMax = 5;
    var decimal = rating % 1;
    var decimalRating = 0;
    if (decimal >= 0.3 && decimal <= 0.7) {
      decimalRating = 0.5;
    } else if (decimal > 0.7) {
      decimalRating = 1;
    }
    var decimalRounded = decimalRating > 0 ? Math.ceil(decimalRating) : 0;
    var svgFilledStars = Math.floor(rating) + decimalRounded;
    var starsSize = 14;
    var ratingCountFontSize = 16;
    var reviewRowHeight = Math.max(starsSize, ratingCountFontSize);
    var label = 'Rating: ' + rating.toFixed(1) + ' out of 5';
    var starsHtml = '';
    var star;
    for (star = 1; star <= scaleMax; star++) {
      var isFilled = star < svgFilledStars || (star === svgFilledStars && decimalRating !== 0.5);
      var isHalf = decimalRating === 0.5 && star === svgFilledStars;
      var starClass = 'stars';
      if (isFilled) starClass += ' filled-star';
      if (isHalf) starClass += ' half-star';
      var fillAttr = isHalf ? ' fill="url(#rating-star-half-shaded)"' : '';
      starsHtml +=
        '<svg class="' +
        starClass +
        '" viewBox="0 0 32 32" width="' +
        starsSize +
        '" height="' +
        starsSize +
        '" role="presentation" aria-hidden="true">' +
        '<use href="#rating-star"' +
        fillAttr +
        '></use>' +
        '</svg>';
    }

    return (
      '<div class="review-block rating-wrapper" style="--review-row-height: ' +
      reviewRowHeight +
      'px; --star-svg-size: ' +
      starsSize +
      'px;">' +
      '<div class="rating" aria-label="' +
      escapeHtml(label) +
      '">' +
      starsHtml +
      '</div>' +
      '<span class="rating-count" style="--rating-count-font-size: ' +
      ratingCountFontSize +
      'px;">(' +
      Math.round(ratingInfo.count) +
      ')</span>' +
      '</div>'
    );
  }

  /**
   * Build a single auction tile DOM from API payload.
   * Matches auction-product-tile.liquid structure (data-auction-tile, data-countdown-ends, etc.).
   * options: { isLoggedIn, loginUrl, showQuickview }
   */
  /**
   * Second-image hover swap. When section disables hover on mobile, omit the
   * secondary <img> below 1024px so iOS sticky :hover / tap cannot swap to it.
   */
  function shouldIncludeAuctionTileHoverImage(section) {
    var root =
      section ||
      document.querySelector('.rising-auctions-grid[data-disable-tile-hover-image-mobile]');
    var disableMobile =
      !root || root.getAttribute('data-disable-tile-hover-image-mobile') !== 'false';
    if (!disableMobile) return true;
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  function buildAuctionTile(payload, currencySymbol, options) {
    var isLoggedIn = options && options.isLoggedIn === true;
    var loginUrl = (options && options.loginUrl) || '/account/login';
    var showQuickview = options && options.showQuickview === true;
    var state = payload.auctionState || 'active';
    var countdownEnds = payload.countdownEndsAt || '';
    var promoBadgeHtml = (payload.tilePromoLabel && state === 'active') ? '<span class="rising-auctions-tile__promo-badge">' + escapeHtml(payload.tilePromoLabel) + '</span>' : '';
    var badgeHtml = payload.tileBadge ? '<span class="rising-auctions-tile__badge" data-tile-badge>' + escapeHtml(payload.tileBadge) + '</span>' : '';
    var placeholderImageUrl = options && options.placeholderImageUrl ? String(options.placeholderImageUrl) : '';
    var pri = resolveAuctionImageUrl(payload.featuredImageUrl, placeholderImageUrl);
    var sec = cleanImageUrl(payload.secondaryImageUrl);
    var includeHoverImage =
      options && typeof options.includeHoverImage === 'boolean'
        ? options.includeHoverImage
        : shouldIncludeAuctionTileHoverImage(null);
    if (!includeHoverImage) sec = '';
    var imageWidths = (options && options.imageWidths) || { mobile: 356, tablet: 356, desktop: 356 };

    // Load the complete first visible row eagerly. Only its first product receives
    // high fetch priority so the browser still has a clear LCP candidate.
    var isFirstRowTile = !!(options && options.isFirstPage && options.isFirstRow);
    var isLcpTile = isFirstRowTile && options.productTileIndex === 0;
    var primaryLoadAttr = isFirstRowTile
      ? 'loading="eager" fetchpriority="' + (isLcpTile ? 'high' : 'auto') + '"'
      : 'loading="lazy" fetchpriority="low"';

    var imgHtml;
    if (pri && sec) {
      imgHtml =
        '<div class="rising-auctions-tile__media-inner" data-tile-media-hover>' +
        buildAuctionPictureHtml(pri, payload.title, 'rising-auctions-tile__image rising-auctions-tile__image--primary', imageWidths, primaryLoadAttr, false) +
        buildAuctionPictureHtml(
          sec,
          '',
          'rising-auctions-tile__image rising-auctions-tile__image--secondary',
          imageWidths,
          'loading="lazy" fetchpriority="low"',
          true
        ) +
        '</div>';
    } else if (pri) {
      imgHtml = buildAuctionPictureHtml(pri, payload.title, 'rising-auctions-tile__image', imageWidths, primaryLoadAttr, false);
    } else {
      imgHtml = '<div class="rising-auctions-tile__placeholder">No image</div>';
    }
    var sizeLabel = getAuctionVariantSize(payload);
    /* Always render size row so missing size does not collapse card layout / misalign CTAs. */
    var sizeHtml = sizeLabel
      ? '<p class="rising-auctions-tile__size">Size: ' + escapeHtml(sizeLabel) + '</p>'
      : '<p class="rising-auctions-tile__size" aria-hidden="true">&nbsp;</p>';
    var ratingHtml = buildAuctionTileRatingHtml(payload, options);
    var bidIncrement = typeof payload.bidIncrement === 'number' && !isNaN(payload.bidIncrement) ? payload.bidIncrement : 1;
    var galleryArr = Array.isArray(payload.imageGalleryUrls) ? payload.imageGalleryUrls.filter(function (u) {
      return cleanImageUrl(u) !== '';
    }) : [];
    if (galleryArr.length === 0 && pri) {
      galleryArr = [pri];
    }
    var quickviewOverlayBtn = showQuickview
      ? '<button type="button" class="rising-auctions-tile__quickview rising-auctions-tile__quickview--overlay" data-auction-quickview aria-label="Quick view">QUICKVIEW</button>'
      : '';
    var quickviewUnderBtn = showQuickview
      ? '<button type="button" class="rising-auctions-tile__quickview rising-auctions-tile__quickview--under" data-auction-quickview aria-label="Quick view">QUICKVIEW</button>'
      : '';
    var article = document.createElement('article');
    article.className = 'rising-auctions-tile rising-auctions-tile--' + state;
    article.setAttribute('data-auction-tile', '');
    article.setAttribute('data-auction-id', payload.auctionId || '');
    article.setAttribute('data-ams-auction-code', payload.amsAuctionCode || '');
    article.setAttribute('data-product-id', payload.productId || '');
    article.setAttribute('data-variant-id', payload.variantId || '');
    article.setAttribute('data-variant-sku', payload.variantSku || '');
    article.setAttribute('data-ra-main-product-code', payload.mainProductCode || '');
    article.setAttribute('data-base-price', formatRaBasePriceDecimal(payload.price));
    article.setAttribute('data-auction-state', state);
    article.setAttribute('data-countdown-ends', countdownEnds);
    article.setAttribute('data-current-bid', String(payload.currentBid));
    article.setAttribute('data-bid-increment', String(bidIncrement));
    article.setAttribute('data-special-auction', bidIncrement > 1 ? 'true' : 'false');
    article.setAttribute('data-bid-count', String(payload.bidCount));
    applyMaxBidAmountToTile(article, payload.maxBidAmount);
    article.setAttribute('aria-label', payload.title || 'Auction');
    article.setAttribute('data-gallery-images', JSON.stringify(galleryArr));
    article.setAttribute('data-product-title', payload.title || '');
    article.setAttribute('data-pdp-url', payload.url || '');
    article.setAttribute('data-variant-size', sizeLabel);
    article.innerHTML =
      '<div class="rising-auctions-tile__top">' +
        '<div class="rising-auctions-tile__media">' +
          '<a href="' + escapeHtml(payload.url || '#') + '" class="rising-auctions-tile__media-link" aria-hidden="true" tabindex="-1">' +
            imgHtml +
          '</a>' +
          promoBadgeHtml + badgeHtml + quickviewOverlayBtn +
        '</div>' +
        quickviewUnderBtn +
        '<div class="rising-auctions-tile__body">' +
          '<div class="rising-auctions-tile__meta">' +
            '<h3 class="rising-auctions-tile__title"><a href="' + escapeHtml(payload.url || '#') + '">' + escapeHtml(payload.title || '') + '</a></h3>' +
            ratingHtml +
            sizeHtml +
          '</div>' +
        '</div>' +
      '</div>' +
      '<div class="rising-auctions-tile__bottom">' +
        buildAuctionTileCountdownAndActionsHtml(payload, currencySymbol, { isLoggedIn: isLoggedIn, loginUrl: loginUrl, state: state }) +
      '</div>';

    // Finalised auctions may already be present in the initial PLP response.
    // Use the live-ended renderer here too, keeping refreshes and PubNub
    // transitions visually identical.
    var endedState = String(state || '').toUpperCase();
    if (endedState === 'WON' || endedState === 'SOLD' || endedState === 'PENDING' || endedState === 'CLOSED') {
      applyEndedStateToTile(article, {
        displayState: endedState,
        winnerInitials: payload.winnerInitials,
        currentBid: payload.currentBid,
        bidCount: payload.bidCount,
        isUserWinner: payload.isUserWinner === true || endedState === 'WON',
        currencySymbol: currencySymbol,
        mainProductCode: payload.mainProductCode,
        amsAuctionCode: payload.amsAuctionCode,
        price: payload.price,
      });
    }
    var hoverWrap = article.querySelector('[data-tile-media-hover]');
    if (hoverWrap) {
      hoverWrap.addEventListener('mouseenter', function () {
        loadDeferredAuctionPicture(hoverWrap.querySelector('[data-deferred-auction-image]'));
        hoverWrap.classList.add('rising-auctions-tile__media-inner--show-alt');
      });
      hoverWrap.addEventListener('mouseleave', function () {
        hoverWrap.classList.remove('rising-auctions-tile__media-inner--show-alt');
      });
    }
    return article;
  }

  /** Must match RA_DURATION_FACET_KEY in app/utils/ra-plp-facets.server.ts */
  var RA_DURATION_FACET_KEY = 'ra.auction.endingWithinMinutes';
  var RA_FACET_SEARCH_ICON_SVG =
    '<svg class="ra-facet-search__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z" stroke="currentColor" stroke-width="1.5"/>' +
    '<path d="M10.5 10.5L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '</svg>';

  function cloneRaFacetMap(map) {
    var out = {};
    if (!map) return out;
    Object.keys(map).forEach(function (k) {
      if (map[k] && map[k].length) out[k] = map[k].slice();
    });
    return out;
  }

  function raFacetMapHasSelections(map) {
    if (!map) return false;
    return Object.keys(map).some(function (k) {
      return map[k] && map[k].length > 0;
    });
  }

  /** Total selected facet values (retail PLP parity — not facet group count). */
  function countRaFacetSelections(map) {
    if (!map) return 0;
    var total = 0;
    Object.keys(map).forEach(function (k) {
      if (map[k] && map[k].length) total += map[k].length;
    });
    return total;
  }

  function formatRaFilterCountAria(section, count) {
    var baseLabel = (section.getAttribute('data-filter-label') || 'Filter').trim();
    if (!count || count < 1) return baseLabel;
    var template =
      count === 1
        ? section.getAttribute('data-filter-count-aria-one') || '__COUNT__ filter applied'
        : section.getAttribute('data-filter-count-aria-other') || '__COUNT__ filters applied';
    return String(template).replace(/__COUNT__/g, String(count));
  }

  function updateRaFilterButtonCount(section) {
    ensureRaFacetState(section);
    var filterBtn = section.querySelector('[data-filter-toggle]');
    if (!filterBtn) return;
    var countEl = filterBtn.querySelector('[data-filter-count]');
    var count = countRaFacetSelections(section._raFacetApplied);
    filterBtn.setAttribute('aria-label', formatRaFilterCountAria(section, count));
    if (!countEl) return;
    countEl.textContent = String(count);
    if (count > 0) {
      countEl.removeAttribute('hidden');
      countEl.setAttribute('aria-hidden', 'false');
    } else {
      countEl.setAttribute('hidden', '');
      countEl.setAttribute('aria-hidden', 'true');
    }
  }

  function ensureRaFacetState(section) {
    if (!section._raFacetApplied) section._raFacetApplied = {};
    if (!section._raFacetDraft) section._raFacetDraft = {};
    /* Migrate legacy single-map state if present from an older page session */
    if (section._raFacetSelected && !section._raFacetAppliedMigrated) {
      section._raFacetApplied = cloneRaFacetMap(section._raFacetSelected);
      section._raFacetDraft = cloneRaFacetMap(section._raFacetSelected);
      section._raFacetAppliedMigrated = true;
    }
    if (!section._raFacetExpanded) section._raFacetExpanded = {};
    if (!section._raFacetShowAll) section._raFacetShowAll = {};
    if (!section._raFacetSearch) section._raFacetSearch = {};
    return section;
  }

  function updateRaFacetClearAllVisibility(section) {
    ensureRaFacetState(section);
    var clearBtn = section.querySelector('[data-plp-facet-clear-all]');
    if (!clearBtn) return;
    /* Drawer open: Clear all reflects draft picks; toolbar badge stays on applied until Done. */
    var drawerOpen = section.classList.contains('rising-auctions-grid--filters-open');
    var clearMap = drawerOpen ? section._raFacetDraft : section._raFacetApplied;
    var show = raFacetMapHasSelections(clearMap);
    if (show) clearBtn.removeAttribute('hidden');
    else clearBtn.setAttribute('hidden', '');
    var footer = section.querySelector('[data-plp-facet-footer]');
    if (footer) footer.classList.toggle('rising-auctions-grid__facet-sidebar-footer--has-clear', show);
    updateRaFilterButtonCount(section);
  }

  function resetRaFacetDrawerUiState(section) {
    ensureRaFacetState(section);
    section._raFacetExpanded = {};
    section._raFacetShowAll = {};
    section._raFacetSearch = {};
  }

  function facetKeyToDomId(key) {
    return 'ra-fk-' + String(key).replace(/[^a-zA-Z0-9]+/g, '_');
  }

  function fetchUrlSep(fetchUrl) {
    return fetchUrl.indexOf('?') >= 0 ? '&' : '?';
  }

  function getRaPlpSearchQuery() {
    return (new URLSearchParams(window.location.search).get('q') || '').trim();
  }

  /** Facet selections from the page URL's `facets` JSON param, for deep-linked loads. */
  function parseRaFacetsFromUrl() {
    var raw = new URLSearchParams(window.location.search).get('facets');
    var map = {};
    if (!raw) return map;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return map;
      Object.keys(parsed).forEach(function (k) {
        var v = parsed[k];
        var arr = (Array.isArray(v) ? v : [v])
          .map(String)
          .filter(function (x) { return x.trim() !== ''; });
        if (arr.length) map[k] = arr;
      });
    } catch (e) {}
    return map;
  }

  /** Sort sent to the server. */
  function getRaPlpSortValue(section) {
    var sortSelect = section && section.querySelector('[data-sort-select]');
    return (sortSelect && sortSelect.value) || 'ending-soon';
  }

  /**
   * The page size the server actually used. `pageSize` in the response is authoritative;
   * every fallback is derived from the response or from what the server already told us,
   * never from a literal — the merchant can change the allowed sizes in the app admin.
   */
  function resolveRaPageSize(data, section) {
    if (data && typeof data.pageSize === 'number' && data.pageSize > 0) return data.pageSize;
    var allowed = data && Array.isArray(data.allowedPageSizes) ? data.allowedPageSizes : [];
    for (var i = 0; i < allowed.length; i++) {
      var n = typeof allowed[i] === 'number' ? allowed[i] : parseInt(allowed[i], 10);
      if (!isNaN(n) && n > 0) return n;
    }
    var stored = section ? parseInt(section.getAttribute('data-current-limit'), 10) : NaN;
    if (!isNaN(stored) && stored > 0) return stored;
    var count = data && Array.isArray(data.auctions) ? data.auctions.length : 0;
    return count > 0 ? count : 1;
  }

  /**
   * allowedPageSizes from the response, sanitised and ascending. The resolved pageSize is
   * folded in so the current selection is always representable in the dropdown.
   */
  function normalizeRaAllowedPageSizes(allowed, pageSize) {
    var sizes = [];
    if (Array.isArray(allowed)) {
      for (var i = 0; i < allowed.length; i++) {
        var n = typeof allowed[i] === 'number' ? allowed[i] : parseInt(allowed[i], 10);
        if (!isNaN(n) && n > 0 && sizes.indexOf(n) === -1) sizes.push(n);
      }
    }
    if (pageSize > 0 && sizes.indexOf(pageSize) === -1) sizes.push(pageSize);
    sizes.sort(function (a, b) {
      return a - b;
    });
    return sizes;
  }

  function buildRaActiveAuctionsUrl(section, apiUrl, options) {
    options = options || {};
    var fetchUrl = apiUrl;
    var collectionHandle = section.getAttribute('data-collection-handle');
    if (collectionHandle && collectionHandle.trim() !== '') {
      fetchUrl =
        fetchUrl +
        fetchUrlSep(fetchUrl) +
        'collection_handle=' +
        encodeURIComponent(collectionHandle.trim());
    }
    fetchUrl =
      fetchUrl + fetchUrlSep(fetchUrl) + 'sort=' + encodeURIComponent(getRaPlpSortValue(section));
    if (options.searchQuery) {
      fetchUrl =
        fetchUrl + fetchUrlSep(fetchUrl) + 'q=' + encodeURIComponent(options.searchQuery);
    }
    // Emitted independently: a null pageSize omits `limit` so the server applies its
    // own resolved default from allowedPageSizes (used before the shopper picks a size).
    if (options.pageSize != null) {
      fetchUrl =
        fetchUrl +
        fetchUrlSep(fetchUrl) +
        'limit=' +
        encodeURIComponent(String(options.pageSize));
    }
    if (options.page != null) {
      fetchUrl =
        fetchUrl + fetchUrlSep(fetchUrl) + 'page=' + encodeURIComponent(String(options.page));
    }
    fetchUrl = appendRaFacetsToFetchUrl(fetchUrl, section);
    var customerId = section.getAttribute('data-customer-id');
    if (customerId && customerId.trim() !== '') {
      fetchUrl =
        fetchUrl +
        fetchUrlSep(fetchUrl) +
        'logged_in_customer_id=' +
        encodeURIComponent(customerId.trim());
    }
    return fetchUrl;
  }

  /**
   * Loading the list changes nothing on the server, so a failed attempt (dropped
   * connection, the load balancer's HTML error page, or the handler's own error payload)
   * is retried once after a short pause before the grid shows its error/empty state.
   */
  var RA_LIST_RETRY_DELAY_MS = 800;

  function fetchRaActiveAuctionsJson(fetchUrl) {
    return fetchRaActiveAuctionsJsonOnce(fetchUrl)
      .then(function (data) {
        if (data && data.error) throw new Error('active-auctions error: ' + data.error);
        return data;
      })
      .catch(function (err) {
        console.warn('[RA-PLP] Active auctions request failed, retrying once:', err);
        return new Promise(function (resolve) {
          setTimeout(resolve, RA_LIST_RETRY_DELAY_MS);
        }).then(function () {
          return fetchRaActiveAuctionsJsonOnce(fetchUrl);
        });
      });
  }

  function fetchRaActiveAuctionsJsonOnce(fetchUrl) {
    // Single choke point for every active-auctions request, so syncing here lands the
    // server-clock offset before any tile countdown is built.
    var sentAt = Date.now();
    return fetch(fetchUrl, { method: 'GET', credentials: 'same-origin' }).then(function (res) {
      var contentType = res.headers.get('Content-Type') || '';
      if (!contentType.includes('application/json')) {
        return res.text().then(function (text) {
          console.warn(
            '[RA-PLP] Response is not JSON. Status:',
            res.status,
            'Content-Type:',
            contentType,
            'Body preview:',
            text ? text.substring(0, 200) : '(empty)'
          );
          throw new Error('Expected JSON, got ' + contentType);
        });
      }
      return res.json().then(function (data) {
        raSyncClock(data, sentAt);
        return data;
      });
    });
  }

  function appendRaFacetsToFetchUrl(fetchUrl, section) {
    ensureRaFacetState(section);
    var sel = section._raFacetApplied;
    var payload = {};
    Object.keys(sel).forEach(function (k) {
      if (sel[k] && sel[k].length) payload[k] = sel[k];
    });
    if (Object.keys(payload).length === 0) return fetchUrl;
    var join = fetchUrlSep(fetchUrl);
    return fetchUrl + join + 'facets=' + encodeURIComponent(JSON.stringify(payload));
  }

  /**
   * Keep the address bar in sync with sort/facets/page so the current view is a plain,
   * shareable link (ad landing URLs). Drops params at their default (sort=ending-soon,
   * page=1) to keep the URL clean; never reloads the page.
   */
  function syncRaPlpUrl(section) {
    try {
      var url = new URL(window.location.href);
      var params = url.searchParams;

      var sortVal = getRaPlpSortValue(section);
      if (sortVal && sortVal !== 'ending-soon') {
        params.set('sort', sortVal);
      } else {
        params.delete('sort');
      }

      ensureRaFacetState(section);
      var payload = {};
      Object.keys(section._raFacetApplied).forEach(function (k) {
        if (section._raFacetApplied[k] && section._raFacetApplied[k].length) {
          payload[k] = section._raFacetApplied[k];
        }
      });
      if (Object.keys(payload).length) {
        params.set('facets', JSON.stringify(payload));
      } else {
        params.delete('facets');
      }

      var page = parseInt(section.getAttribute('data-current-page'), 10) || 1;
      if (page > 1) {
        params.set('page', String(page));
      } else {
        params.delete('page');
      }

      history.replaceState(null, '', url.toString());
    } catch (e) {}
  }

  /** Highest listing count first; name A–Z only as a tie-breaker (app may still send A–Z). */
  function sortRaFacetValuesByCount(values) {
    return (values || []).slice().sort(function (a, b) {
      var countDiff = Number(b.count || 0) - Number(a.count || 0);
      if (countDiff !== 0) return countDiff;
      var aLabel = String(a.label != null && String(a.label).trim() !== '' ? a.label : a.value || '');
      var bLabel = String(b.label != null && String(b.label).trim() !== '' ? b.label : b.value || '');
      return aLabel.localeCompare(bLabel, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  function renderRaFacetPanel(section, facetPanel, gridInner) {
    var body = section.querySelector('[data-plp-facet-body]');
    if (!body) return;
    ensureRaFacetState(section);
    updateRaFacetClearAllVisibility(section);

    if (!facetPanel || !facetPanel.groups || !facetPanel.groups.length) {
      body.innerHTML = '<p class="rising-auctions-grid__facet-empty">No filters available for these auctions.</p>';
      return;
    }

    var html = '';
    facetPanel.groups.forEach(function (g) {
      var key = g.key;
      var label = g.label || key;
      var selected = section._raFacetDraft[key] || [];
      var n = selected.length;
      var expanded = section._raFacetExpanded[key] === true;
      var showAll = !!section._raFacetShowAll[key];
      var searchQuery = (section._raFacetSearch[key] || '').trim();
      var searchLower = searchQuery.toLowerCase();
      var idBase = facetKeyToDomId(key);
      var panelId = 'ra-facet-panel-' + idBase;
      var searchId = 'ra-facet-search-' + idBase;
      var values = sortRaFacetValuesByCount(g.values || []);
      /* Hide zero-count options once any filter is applied (dependent facets). */
      if (raFacetMapHasSelections(section._raFacetApplied)) {
        values = values.filter(function (v) {
          return Number(v.count) > 0 || selected.indexOf(v.value) >= 0;
        });
        if (!values.length) return;
      }
      var hasSearch = values.length > 5;
      var chev = expanded ? '\u25B2' : '\u25BC';

      html += '<div class="ra-facet-accordion" data-facet-key="' + escapeHtml(key) + '">';
      html += '<button type="button" class="ra-facet-accordion__trigger" data-plp-facet-accordion-trigger aria-expanded="' + (expanded ? 'true' : 'false') + '" aria-controls="' + escapeHtml(panelId) + '">';
      html +=
        '<span class="ra-facet-accordion__title">' +
        escapeHtml(label) +
        (g.hideTitleSelectedCount || n <= 0 ? '' : ' (' + n + ')') +
        '</span>';
      html += '<span class="ra-facet-accordion__chev" aria-hidden="true">' + chev + '</span>';
      html += '</button>';
      html += '<div class="ra-facet-accordion__panel" id="' + escapeHtml(panelId) + '"';
      if (!expanded) html += ' hidden';
      html += '>';

      if (hasSearch) {
        html += '<div class="ra-facet-search">';
        html +=
          '<input type="text" class="ra-facet-search__input" data-plp-facet-search data-facet-key="' +
          escapeHtml(key) +
          '" id="' +
          escapeHtml(searchId) +
          '" placeholder="' +
          escapeHtml('Type ' + label) +
          '" aria-label="' +
          escapeHtml('Type ' + label) +
          '" value="' +
          escapeHtml(section._raFacetSearch[key] || '') +
          '" />';
        html += RA_FACET_SEARCH_ICON_SVG;
        html += '</div>';
      }

      var filtered = values;
      if (searchLower) {
        filtered = values.filter(function (v) {
          var displayVal = v.label != null && String(v.label).trim() !== '' ? v.label : v.value;
          return (
            String(displayVal).toLowerCase().indexOf(searchLower) >= 0 ||
            String(v.value).toLowerCase().indexOf(searchLower) >= 0
          );
        });
      }

      var toShow =
        searchLower || showAll || filtered.length <= 5 ? filtered : filtered.slice(0, 5);

      if (searchLower && filtered.length === 0) {
        html +=
          '<p class="ra-facet-search__no-results" role="status">No ' +
          escapeHtml(label) +
          ' found</p>';
      }

      toShow.forEach(function (v) {
        var checked = selected.indexOf(v.value) >= 0;
        var displayVal = v.label != null && String(v.label).trim() !== '' ? v.label : v.value;
        var countSuffix = g.hideValueCounts ? '' : ' (' + Number(v.count) + ')';
        html += '<label class="ra-facet-value">';
        html +=
          '<input type="checkbox" data-plp-facet-checkbox data-facet-key="' +
          escapeHtml(key) +
          '" data-facet-value="' +
          escapeHtml(v.value) +
          '" ';
        if (checked) html += 'checked ';
        html += '/>';
        html += '<span>' + escapeHtml(displayVal) + countSuffix + '</span>';
        html += '</label>';
      });

      if (!searchLower && !showAll && filtered.length > 5) {
        var more = filtered.length - 5;
        html +=
          '<button type="button" class="ra-facet-show-more" data-plp-facet-show-more data-facet-key="' +
          escapeHtml(key) +
          '">Show ' +
          more +
          ' more</button>';
      } else if (!searchLower && showAll && filtered.length > 5) {
        html +=
          '<button type="button" class="ra-facet-show-more" data-plp-facet-show-less data-facet-key="' +
          escapeHtml(key) +
          '">Show less</button>';
      }

      html += '</div></div>';
    });

    body.innerHTML = html;
  }

  function bindRaFacetEvents(section, gridInner) {
    if (!section || section.dataset.raFacetEventsBound) return;
    var body = section.querySelector('[data-plp-facet-body]');
    if (!body) return;
    section.dataset.raFacetEventsBound = 'true';
    ensureRaFacetState(section);
    updateRaFacetClearAllVisibility(section);

    var filterBtn = section.querySelector('[data-filter-toggle]');
    var overlay = section.querySelector('[data-plp-facet-overlay]');

    function setRaFiltersDrawerOpen(open) {
      if (open) {
        section.classList.add('rising-auctions-grid--filters-open');
        if (filterBtn) filterBtn.setAttribute('aria-expanded', 'true');
        if (overlay) overlay.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        /* Lets sticky CTA (rising-auctions-cta-bar) drop to position:static via
           html[scroll-lock] so it cannot bleed through the facet drawer. */
        document.documentElement.setAttribute('scroll-lock', '');
      } else {
        section.classList.remove('rising-auctions-grid--filters-open');
        if (filterBtn) filterBtn.setAttribute('aria-expanded', 'false');
        if (overlay) overlay.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        document.documentElement.removeAttribute('scroll-lock');
      }
    }

    function closeRaFilters() {
      /* Discard draft; keep applied filters; no refetch */
      ensureRaFacetState(section);
      section._raFacetDraft = cloneRaFacetMap(section._raFacetApplied);
      resetRaFacetDrawerUiState(section);
      setRaFiltersDrawerOpen(false);
    }

    function openRaFilters() {
      ensureRaFacetState(section);
      section._raFacetDraft = cloneRaFacetMap(section._raFacetApplied);
      resetRaFacetDrawerUiState(section);
      if (section._raLastFacetPanel) renderRaFacetPanel(section, section._raLastFacetPanel, gridInner);
      setRaFiltersDrawerOpen(true);
    }

    /**
     * Apply draft facets and refetch grid + scoped facetPanel from AMS (Done only).
     */
    function applyRaFacetDraftAndRefetch(closeDrawer) {
      ensureRaFacetState(section);
      section._raFacetApplied = cloneRaFacetMap(section._raFacetDraft);
      section.setAttribute('data-current-page', '1');
      syncRaPlpUrl(section);
      updateRaFacetClearAllVisibility(section);
      if (closeDrawer) setRaFiltersDrawerOpen(false);
      plpRefetch(section, gridInner);
    }

    function applyRaFacetDraftAndClose() {
      applyRaFacetDraftAndRefetch(true);
    }

    /** Reset draft selections in the drawer; grid refetches only when shopper clicks Done. */
    function clearAllRaFacetDraft() {
      ensureRaFacetState(section);
      section._raFacetDraft = {};
      resetRaFacetDrawerUiState(section);
      if (section._raLastFacetPanel) renderRaFacetPanel(section, section._raLastFacetPanel, gridInner);
      updateRaFacetClearAllVisibility(section);
    }

    if (filterBtn) {
      filterBtn.addEventListener('click', function () {
        if (section.classList.contains('rising-auctions-grid--filters-open')) closeRaFilters();
        else openRaFilters();
      });
    }

    var closeBtn = section.querySelector('[data-plp-facet-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', closeRaFilters);
    }

    if (overlay) {
      overlay.addEventListener('click', closeRaFilters);
    }

    var doneBtn = section.querySelector('[data-plp-facet-done]');
    if (doneBtn) {
      doneBtn.addEventListener('click', applyRaFacetDraftAndClose);
    }

    var clearAllBtn = section.querySelector('[data-plp-facet-clear-all]');
    if (clearAllBtn) {
      clearAllBtn.addEventListener('click', clearAllRaFacetDraft);
    }

    if (!section._raFacetEscapeBound) {
      section._raFacetEscapeBound = true;
      document.addEventListener('keydown', function raFacetEscape(e) {
        if (e.key !== 'Escape') return;
        if (!section.classList.contains('rising-auctions-grid--filters-open')) return;
        closeRaFilters();
        if (filterBtn) filterBtn.focus();
      });
    }

    body.addEventListener('click', function (e) {
      var t = e.target;
      var trig = t.closest && t.closest('[data-plp-facet-accordion-trigger]');
      if (trig) {
        var acc = trig.closest('.ra-facet-accordion');
        if (!acc) return;
        var key = acc.getAttribute('data-facet-key');
        if (!key) return;
        ensureRaFacetState(section);
        var wasOpen = section._raFacetExpanded[key] === true;
        section._raFacetExpanded[key] = !wasOpen;
        if (section._raLastFacetPanel) renderRaFacetPanel(section, section._raLastFacetPanel, gridInner);
        return;
      }
      var showMore = t.closest && t.closest('[data-plp-facet-show-more]');
      if (showMore) {
        var keyM = showMore.getAttribute('data-facet-key');
        if (!keyM) return;
        ensureRaFacetState(section);
        section._raFacetShowAll[keyM] = true;
        if (section._raLastFacetPanel) renderRaFacetPanel(section, section._raLastFacetPanel, gridInner);
        return;
      }
      var showLess = t.closest && t.closest('[data-plp-facet-show-less]');
      if (showLess) {
        var keyL = showLess.getAttribute('data-facet-key');
        if (!keyL) return;
        ensureRaFacetState(section);
        section._raFacetShowAll[keyL] = false;
        if (section._raLastFacetPanel) renderRaFacetPanel(section, section._raLastFacetPanel, gridInner);
      }
    });

    body.addEventListener('input', function (e) {
      var inp = e.target;
      if (!inp || !inp.matches || !inp.matches('[data-plp-facet-search]')) return;
      var skey = inp.getAttribute('data-facet-key');
      if (!skey) return;
      ensureRaFacetState(section);
      section._raFacetSearch[skey] = inp.value || '';
      if (section._raLastFacetPanel) {
        var activeKey = skey;
        var caret = typeof inp.selectionStart === 'number' ? inp.selectionStart : null;
        renderRaFacetPanel(section, section._raLastFacetPanel, gridInner);
        var next = null;
        var searchInputs = body.querySelectorAll('[data-plp-facet-search]');
        for (var si = 0; si < searchInputs.length; si++) {
          if (searchInputs[si].getAttribute('data-facet-key') === activeKey) {
            next = searchInputs[si];
            break;
          }
        }
        if (next) {
          next.focus();
          if (caret != null && typeof next.setSelectionRange === 'function') {
            try {
              next.setSelectionRange(caret, caret);
            } catch (err) {
              /* ignore */
            }
          }
        }
      }
    });

    body.addEventListener('change', function (e) {
      var inp = e.target;
      if (!inp || !inp.matches || !inp.matches('input[type="checkbox"][data-plp-facet-checkbox]')) return;
      var fkey = inp.getAttribute('data-facet-key');
      var fval = inp.getAttribute('data-facet-value');
      if (!fkey || fval == null) return;
      ensureRaFacetState(section);
      if (fkey === RA_DURATION_FACET_KEY) {
        section._raFacetDraft[fkey] = inp.checked ? [fval] : [];
      } else {
        if (!section._raFacetDraft[fkey]) section._raFacetDraft[fkey] = [];
        var arr = section._raFacetDraft[fkey];
        var idx = arr.indexOf(fval);
        if (inp.checked) {
          if (idx < 0) arr.push(fval);
        } else if (idx >= 0) {
          arr.splice(idx, 1);
        }
      }
      if (section._raLastFacetPanel) renderRaFacetPanel(section, section._raLastFacetPanel, gridInner);
      updateRaFacetClearAllVisibility(section);
    });
  }

  function getPlpGridMain(gridInner) {
    return gridInner && gridInner.closest ? gridInner.closest('[data-plp-grid-main]') : null;
  }

  function getRaPlpScrollBufferPx(section) {
    var raw = section && section.getAttribute('data-pagination-scroll-offset');
    var n = parseInt(raw, 10);
    return !isNaN(n) && n >= 0 ? n : 16;
  }

  function getRaPlpStickyOffset() {
    var offset = 0;
    var headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'), 10);
    if (!isNaN(headerHeight) && headerHeight > 0) {
      offset += headerHeight;
    }
    var ctaWrapper = document.querySelector('.rising-auctions-cta-bar__wrapper');
    if (ctaWrapper) {
      var wrapperStyle = window.getComputedStyle(ctaWrapper);
      if (wrapperStyle.position === 'sticky' || wrapperStyle.position === 'fixed') {
        offset += ctaWrapper.getBoundingClientRect().height;
      }
    }
    return offset;
  }

  function scrollPlpAboveGrid(section, gridInner, behavior) {
    var gridMain = getPlpGridMain(gridInner);
    if (!gridMain) return;
    var stickyOffset = getRaPlpStickyOffset();
    var top = gridMain.getBoundingClientRect().top + window.scrollY - stickyOffset - getRaPlpScrollBufferPx(section);
    window.scrollTo({ top: Math.max(0, top), behavior: behavior || 'instant' });
  }

  function plpPaginateAndRefetch(section, gridInner) {
    section._raPlpScrollAfterPagination = true;
    scrollPlpAboveGrid(section, gridInner, 'instant');
    syncRaPlpUrl(section);
    plpRefetch(section, gridInner);
  }

  function detachRaSortOutsideListeners(dropdownRoot) {
    if (!dropdownRoot) return;
    if (dropdownRoot._raSortDocPointerDown) {
      document.removeEventListener('pointerdown', dropdownRoot._raSortDocPointerDown, true);
      dropdownRoot._raSortDocPointerDown = null;
    }
    if (dropdownRoot._raSortDocKeydown) {
      document.removeEventListener('keydown', dropdownRoot._raSortDocKeydown, true);
      dropdownRoot._raSortDocKeydown = null;
    }
  }

  function syncRaSortCustomUi(dropdownRoot) {
    if (!dropdownRoot) return;
    var select = dropdownRoot.querySelector('[data-sort-select]');
    var triggerLabel = dropdownRoot.querySelector('[data-sort-trigger-label]');
    var options = dropdownRoot.querySelectorAll('[data-sort-value]');
    if (!select) return;
    var val = select.value || 'ending-soon';
    options.forEach(function (opt) {
      var isSel = opt.getAttribute('data-sort-value') === val;
      opt.setAttribute('aria-selected', isSel ? 'true' : 'false');
    });
    if (triggerLabel && select.options && select.selectedIndex >= 0) {
      var sel = select.options[select.selectedIndex];
      if (sel) triggerLabel.textContent = sel.text;
    }
  }

  function setRaSortPanelOpen(dropdownRoot, open) {
    if (!dropdownRoot) return;
    var panel = dropdownRoot.querySelector('[data-sort-panel]');
    var trigger = dropdownRoot.querySelector('[data-sort-trigger]');
    var triggerLabel = dropdownRoot.querySelector('[data-sort-trigger-label]');
    var openLabel = (dropdownRoot.getAttribute('data-sort-open-label') || '').trim() || 'Sort';
    if (!panel || !trigger) return;
    if (open) {
      panel.removeAttribute('hidden');
      trigger.setAttribute('aria-expanded', 'true');
      dropdownRoot.classList.add('rising-auctions-grid__sort-dropdown--open');
      if (triggerLabel) triggerLabel.textContent = openLabel;
      if (!dropdownRoot._raSortDocPointerDown) {
        dropdownRoot._raSortDocPointerDown = function (e) {
          if (!dropdownRoot.contains(e.target)) {
            setRaSortPanelOpen(dropdownRoot, false);
          }
        };
        dropdownRoot._raSortDocKeydown = function (e) {
          if (e.key === 'Escape') {
            setRaSortPanelOpen(dropdownRoot, false);
            trigger.focus();
          }
        };
        document.addEventListener('pointerdown', dropdownRoot._raSortDocPointerDown, true);
        document.addEventListener('keydown', dropdownRoot._raSortDocKeydown, true);
      }
    } else {
      detachRaSortOutsideListeners(dropdownRoot);
      panel.setAttribute('hidden', '');
      trigger.setAttribute('aria-expanded', 'false');
      dropdownRoot.classList.remove('rising-auctions-grid__sort-dropdown--open');
      syncRaSortCustomUi(dropdownRoot);
    }
  }

  function initRaSortDropdown(section, gridInner) {
    var root = section.querySelector('[data-sort-dropdown]');
    if (!root || root.dataset.raSortDropdownInit) return;
    root.dataset.raSortDropdownInit = 'true';
    var select = root.querySelector('[data-sort-select]');
    var trigger = root.querySelector('[data-sort-trigger]');
    var panel = root.querySelector('[data-sort-panel]');
    if (!select || !trigger || !panel) return;

    syncRaSortCustomUi(root);

    trigger.addEventListener('click', function () {
      if (trigger.disabled) return;
      var isOpen = trigger.getAttribute('aria-expanded') === 'true';
      setRaSortPanelOpen(root, !isOpen);
    });

    panel.querySelectorAll('[data-sort-value]').forEach(function (optBtn) {
      optBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (trigger.disabled) return;
        var v = optBtn.getAttribute('data-sort-value');
        if (!v) return;
        if (select.value === v) {
          setRaSortPanelOpen(root, false);
          return;
        }
        select.value = v;
        setRaSortPanelOpen(root, false);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function setPlpFetchingUi(section, gridInner, loading) {
    if (!section || !gridInner) return;
    var main = getPlpGridMain(gridInner);
    var overlay = section._raPlpLoadingOverlay ||
      section.querySelector('[data-plp-grid-loading-overlay]');
    var visibleLabel = section.querySelector('[data-plp-grid-loading-label]');
    var sr = section.querySelector('[data-plp-grid-loading-sr]');

    if (loading) {
      if (section._raPlpLoadingMessageTimer) {
        clearInterval(section._raPlpLoadingMessageTimer);
        section._raPlpLoadingMessageTimer = null;
      }
      var loadingMessages = [
        section.getAttribute('data-loading-label'),
        section.getAttribute('data-loading-label-2'),
        section.getAttribute('data-loading-label-3'),
      ].map(function (message) { return (message || '').trim(); }).filter(Boolean);
      if (!loadingMessages.length) loadingMessages.push('Loading auctions…');
      var messageIndex = 0;
      var updateLoadingMessage = function () {
        var message = loadingMessages[messageIndex];
        if (visibleLabel) visibleLabel.textContent = message;
        messageIndex = (messageIndex + 1) % loadingMessages.length;
      };
      updateLoadingMessage();
      if (sr) sr.textContent = loadingMessages[0];
      if (loadingMessages.length > 1) {
        section._raPlpLoadingMessageTimer = setInterval(updateLoadingMessage, 3000);
      }
      section.classList.add('rising-auctions-grid--plp-fetching');
      if (overlay && overlay.parentNode !== document.body) {
        overlay._raPlpOriginalParent = overlay.parentNode;
        overlay._raPlpOriginalNextSibling = overlay.nextSibling;
        document.body.appendChild(overlay);
        section._raPlpLoadingOverlay = overlay;
      }
      if (main) {
        main.classList.add('rising-auctions-grid__main--loading');
        main.setAttribute('aria-busy', 'true');
      }
      if (overlay) {
        overlay.removeAttribute('hidden');
        overlay.setAttribute('aria-hidden', 'false');
      }
      var sSort = section.querySelector('[data-sort-select]');
      if (sSort) sSort.disabled = true;
      section.querySelectorAll('[data-sort-dropdown]').forEach(function (dd) {
        setRaSortPanelOpen(dd, false);
      });
      section.querySelectorAll('[data-sort-trigger]').forEach(function (t) {
        t.disabled = true;
      });
      var sPer = section.querySelector('[data-per-page-select]');
      if (sPer) sPer.disabled = true;
    } else {
      if (section._raPlpLoadingMessageTimer) {
        clearInterval(section._raPlpLoadingMessageTimer);
        section._raPlpLoadingMessageTimer = null;
      }
      section.classList.remove('rising-auctions-grid--plp-fetching');
      if (main) {
        main.classList.remove('rising-auctions-grid__main--loading');
        main.setAttribute('aria-busy', 'false');
      }
      if (overlay) {
        overlay.setAttribute('hidden', '');
        overlay.setAttribute('aria-hidden', 'true');
        var originalParent = overlay._raPlpOriginalParent;
        if (originalParent) {
          var originalNextSibling = overlay._raPlpOriginalNextSibling;
          if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
            originalParent.insertBefore(overlay, originalNextSibling);
          } else {
            originalParent.appendChild(overlay);
          }
          overlay._raPlpOriginalParent = null;
          overlay._raPlpOriginalNextSibling = null;
          section._raPlpLoadingOverlay = null;
        }
      }
      if (visibleLabel) visibleLabel.textContent = '';
      if (sr) sr.textContent = '';
      var sSort2 = section.querySelector('[data-sort-select]');
      if (sSort2) sSort2.disabled = false;
      section.querySelectorAll('[data-sort-trigger]').forEach(function (t) {
        t.disabled = false;
      });
      // Stays disabled until a response has supplied real sizes — on a failed fetch the
      // select still holds only the non-numeric placeholder, which is not selectable.
      var sPer2 = section.querySelector('[data-per-page-select]');
      if (sPer2) sPer2.disabled = !sPer2.querySelector('option[value]:not([value=""])');
    }
  }

  /** How many page indices to show on each side of the current page (besides first/last). */
  var PLP_PAGINATION_SIBLINGS = 2;

  function renderPlpPaginationPages(section, page, totalPages) {
    var pagesContainer = section.querySelector('[data-pagination-pages]');
    if (!pagesContainer) return;
    pagesContainer.innerHTML = '';
    if (totalPages <= 1) {
      pagesContainer.setAttribute('hidden', '');
      pagesContainer.setAttribute('aria-hidden', 'true');
      return;
    }
    pagesContainer.removeAttribute('hidden');
    pagesContainer.removeAttribute('aria-hidden');

    /** @param {number} p */
    function appendPage(p) {
      if (p === page) {
        var span = document.createElement('span');
        span.className = 'rising-auctions-grid__page is-current';
        span.setAttribute('aria-current', 'page');
        span.textContent = String(p);
        pagesContainer.appendChild(span);
      } else {
        var a = document.createElement('a');
        a.href = '#';
        a.className = 'rising-auctions-grid__page';
        a.setAttribute('data-plp-page', String(p));
        a.textContent = String(p);
        pagesContainer.appendChild(a);
      }
    }

    function appendEllipsis() {
      var span = document.createElement('span');
      span.className = 'rising-auctions-grid__page-ellipsis';
      span.setAttribute('aria-hidden', 'true');
      span.textContent = '…';
      pagesContainer.appendChild(span);
    }

    var windowStart = Math.max(2, page - PLP_PAGINATION_SIBLINGS);
    var windowEnd = Math.min(totalPages - 1, page + PLP_PAGINATION_SIBLINGS);

    appendPage(1);
    if (windowStart > 2) appendEllipsis();
    for (var p = windowStart; p <= windowEnd; p++) appendPage(p);
    if (windowEnd < totalPages - 1) appendEllipsis();
    if (totalPages > 1) appendPage(totalPages);
  }

  /**
   * Load active auctions from API (app proxy) and render tiles into grid.
   * Uses data-current-limit and data-current-page on section when set; first load omits limit so API uses preference.
   * Updates product count, showing text, per-page dropdown, and pagination UI.
   */
  // TWS-1184: the active-auctions API used to ship a pre-merged items[] (auction
  // tiles + promos) alongside auctions[], duplicating every tile payload. Newer
  // middleware omits items[] and sends only auctions[] + promoTiles[]; we rebuild
  // the mixed grid here using the same position-based insertion the server used
  // (sort by 1-based position then slot, splice each into [0, current length]).
  // Kept back-compatible: if a response still includes items[], we use it as-is.
  function mergeRaPromoTilesIntoAuctions(auctions, promoTiles) {
    var items = (Array.isArray(auctions) ? auctions : []).map(function (payload) {
      return { kind: 'auction', payload: payload };
    });
    if (!Array.isArray(promoTiles) || promoTiles.length === 0) return items;
    var sortedPromos = promoTiles.slice().sort(function (a, b) {
      var pa = Number(a && a.position) || 0;
      var pb = Number(b && b.position) || 0;
      if (pa !== pb) return pa - pb;
      return (Number(a && a.slot) || 0) - (Number(b && b.slot) || 0);
    });
    sortedPromos.forEach(function (promo) {
      var pos = Number(promo && promo.position) || 0;
      var targetIndex = Math.min(Math.max(pos - 1, 0), items.length);
      items.splice(targetIndex, 0, { kind: 'promo', payload: promo });
    });
    return items;
  }

  /**
   * Deferred below-fold tiles (retail PLP parity). Kill-switch:
   * section data-deferred-auction-tiles="true". Default off.
   */
  var RA_DEFERRED_TILE_BATCH = 6;
  var raDeferredPayloadByCell = typeof WeakMap !== 'undefined' ? new WeakMap() : null;

  function isDeferredAuctionTilesEnabled(section) {
    return !!(section && section.getAttribute('data-deferred-auction-tiles') === 'true');
  }

  function clearRaDeferredTileObserver(section) {
    if (!section) return;
    if (section._raDeferredTileObserver) {
      try { section._raDeferredTileObserver.disconnect(); } catch (e) {}
      section._raDeferredTileObserver = null;
    }
    section._raDeferredTileQueue = null;
    section._raDeferredTileBatchScheduled = false;
  }

  function buildRaDeferredTilePlaceholder() {
    var wrap = document.createElement('div');
    wrap.className = 'ra-deferred-tile';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML =
      '<div class="ra-skeleton-tile">' +
        '<div class="ra-skeleton-tile__media"></div>' +
        '<div class="ra-skeleton-tile__line ra-skeleton-tile__line--title"></div>' +
        '<div class="ra-skeleton-tile__line ra-skeleton-tile__line--meta"></div>' +
        '<div class="ra-skeleton-tile__btn"></div>' +
      '</div>';
    return wrap;
  }

  function storeRaDeferredPayload(cell, entry) {
    if (raDeferredPayloadByCell) raDeferredPayloadByCell.set(cell, entry);
    else cell._raDeferredPayload = entry;
  }

  function takeRaDeferredPayload(cell) {
    if (raDeferredPayloadByCell && raDeferredPayloadByCell.has(cell)) {
      var mapped = raDeferredPayloadByCell.get(cell);
      raDeferredPayloadByCell.delete(cell);
      return mapped;
    }
    var entry = cell._raDeferredPayload;
    try { delete cell._raDeferredPayload; } catch (e) {}
    return entry;
  }

  function hydrateRaDeferredTileCell(cell, section) {
    var entry = takeRaDeferredPayload(cell);
    if (!entry || !entry.payload) return;
    cell.removeAttribute('data-ra-lazy-tile');
    cell.removeAttribute('aria-busy');
    cell.innerHTML = '';
    try {
      if (entry.kind === 'promo') {
        cell.appendChild(buildPromoTile(entry.payload, entry.currencySymbol, section));
      } else {
        cell.appendChild(buildAuctionTile(entry.payload, entry.currencySymbol, entry.tileOptions || {}));
      }
    } catch (err) {
      console.error('[RA-PLP] Deferred tile hydrate failed:', err);
      cell.classList.add('rising-auctions-grid__cell--deferred-error');
      return;
    }
    initAuctionTiles();
    refreshPlpAddToBagStates(section);
  }

  function flushRaDeferredTileQueue(section) {
    if (!section) return;
    section._raDeferredTileBatchScheduled = false;
    var queue = section._raDeferredTileQueue || [];
    section._raDeferredTileQueue = [];
    var i;
    for (i = 0; i < queue.length; i += RA_DEFERRED_TILE_BATCH) {
      queue.slice(i, i + RA_DEFERRED_TILE_BATCH).forEach(function (cell) {
        if (!cell || !cell.isConnected || !cell.hasAttribute('data-ra-lazy-tile')) return;
        hydrateRaDeferredTileCell(cell, section);
      });
    }
  }

  function queueRaDeferredTileCell(cell, section) {
    if (!section._raDeferredTileQueue) section._raDeferredTileQueue = [];
    if (section._raDeferredTileQueue.indexOf(cell) !== -1) return;
    section._raDeferredTileQueue.push(cell);
    if (section._raDeferredTileBatchScheduled) return;
    section._raDeferredTileBatchScheduled = true;
    requestAnimationFrame(function () {
      flushRaDeferredTileQueue(section);
    });
  }

  function observeRaDeferredTileCells(section, gridInner) {
    clearRaDeferredTileObserver(section);
    if (!gridInner) return;
    var lazyCells = gridInner.querySelectorAll('[data-ra-lazy-tile]');
    if (!lazyCells.length) return;

    if (!('IntersectionObserver' in window)) {
      lazyCells.forEach(function (cell) {
        hydrateRaDeferredTileCell(cell, section);
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) return;
        observer.unobserve(entry.target);
        queueRaDeferredTileCell(entry.target, section);
      });
    }, { rootMargin: '400px 0px', threshold: 0.01 });

    section._raDeferredTileObserver = observer;
    lazyCells.forEach(function (cell) {
      observer.observe(cell);
    });
  }

  /**
   * Live prices reach the grid through PubNub, which does not replay messages sent while the
   * tab was hidden, frozen (mobile browsers freeze background tabs) or offline. Without this a
   * shopper who comes back to the page bids from the old price and gets "Bid amount too low".
   * Reads the current page once more and repaints only price, bid count and winner on the
   * tiles already shown: no re-render, no spinner.
   */
  var RA_RESYNC_MIN_HIDDEN_MS = 10000;

  function reconcileRaGridLivePrices(section) {
    if (!section || !section.classList || !section.classList.contains('rising-auctions-grid')) return;
    var gridInner = section.querySelector('[data-grid-inner][data-api-url]');
    if (!gridInner || !gridInner.dataset.raPlpLoaded || section._raResyncInFlight) return;
    var limitAttr = parseInt(section.getAttribute('data-current-limit'), 10);
    var pageAttr = parseInt(section.getAttribute('data-current-page'), 10);
    var opts = {
      page: isNaN(pageAttr) || pageAttr < 1 ? 1 : pageAttr,
      pageSize: isNaN(limitAttr) || limitAttr < 1 ? null : limitAttr,
    };
    var searchQuery = getRaPlpSearchQuery();
    if (searchQuery) opts.searchQuery = searchQuery;
    var fetchGen = section._raPlpFetchGen;
    section._raResyncInFlight = true;
    fetchRaActiveAuctionsJsonOnce(buildRaActiveAuctionsUrl(section, gridInner.getAttribute('data-api-url'), opts))
      .then(function (data) {
        // The grid was re-rendered meanwhile (page, sort, search): it already has fresh prices.
        if (fetchGen !== section._raPlpFetchGen) return;
        var auctions = data && Array.isArray(data.auctions) ? data.auctions : [];
        if (!auctions.length) return;
        var customerId = section.getAttribute('data-customer-id') || '';
        var currencySymbol = section.getAttribute('data-currency-symbol') || '£';
        var tilesById = {};
        section.querySelectorAll('[data-auction-id]').forEach(function (el) {
          var id = el.getAttribute('data-auction-id');
          (tilesById[id] = tilesById[id] || []).push(el);
        });
        auctions.forEach(function (a) {
          var matched = a && tilesById[a.auctionId];
          if (!matched || a.currentBid == null || typeof a.bidCount !== 'number') return;
          // Only auctions that moved: a tile that still shows "Be the first to bid" is left as is.
          var stale = matched.filter(function (tile) {
            var shown = parseInt(tile.getAttribute('data-bid-count'), 10);
            return isNaN(shown) || a.bidCount > shown;
          });
          if (!stale.length) return;
          applyLiveBidToTiles(
            stale,
            {
              price: a.currentBid,
              betCount: a.bidCount,
              winningInitials: a.winningInitials || '',
              highestBidderId: a.isUserWinning === true ? customerId : '',
            },
            customerId,
            currencySymbol,
            section
          );
        });
      })
      .catch(function (err) {
        console.warn('[RA-PLP] Live price re-read failed:', err);
      })
      .then(function () {
        section._raResyncInFlight = false;
      });
  }

  function reconcileAllRaGridLivePrices() {
    document.querySelectorAll('.rising-auctions-grid').forEach(reconcileRaGridLivePrices);
  }

  function initRaLivePriceResync() {
    if (document._raLivePriceResyncAttached) return;
    document._raLivePriceResyncAttached = true;
    var hiddenAt = 0;
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt && Date.now() - hiddenAt >= RA_RESYNC_MIN_HIDDEN_MS) reconcileAllRaGridLivePrices();
      hiddenAt = 0;
    });
    window.addEventListener('online', reconcileAllRaGridLivePrices);
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) reconcileAllRaGridLivePrices();
    });
  }

  function loadActiveAuctionsGrid(gridInner) {
    var apiUrl = gridInner.getAttribute('data-api-url');
    if (!apiUrl) return;
    var section = gridInner.closest('.rising-auctions-grid');
    if (!section) return;

    clearRaDeferredTileObserver(section);
    section._raPlpFetchGen = (section._raPlpFetchGen || 0) + 1;
    var fetchGen = section._raPlpFetchGen;
    setPlpFetchingUi(section, gridInner, true);

    var currencySymbol = section.getAttribute('data-currency-symbol') || '£';
    var loadingEl = gridInner.querySelector('[data-plp-loading]');
    var emptyEl = gridInner.querySelector('[data-plp-empty]');

    var searchQuery = getRaPlpSearchQuery();
    var currentLimitAttr = section.getAttribute('data-current-limit');
    var currentPageAttr = section.getAttribute('data-current-page');
    // Null until the shopper picks a size (or a response has set data-current-limit):
    // the request then omits `limit` and the server applies its own resolved default.
    var pageSize =
      currentLimitAttr != null && currentLimitAttr !== ''
        ? parseInt(currentLimitAttr, 10)
        : null;
    var page =
      currentPageAttr != null && currentPageAttr !== ''
        ? parseInt(currentPageAttr, 10)
        : 1;
    if (pageSize != null && (isNaN(pageSize) || pageSize < 1)) pageSize = null;
    if (isNaN(page) || page < 1) page = 1;

    function fetchAmsPlpData(includeSearchQuery) {
      var opts = { page: page, pageSize: pageSize };
      if (includeSearchQuery && searchQuery) opts.searchQuery = searchQuery;
      var fetchUrl = buildRaActiveAuctionsUrl(section, apiUrl, opts);
      // TWS-1184: reuse the in-flight request the inline head script started, but
      // only when it targets the exact same URL. Consume it once; on any miss or
      // failure fall back to a normal fetch so behaviour is never changed.
      var early = window.__raPlpEarlyFetch;
      if (early && early.promise && early.url === fetchUrl) {
        window.__raPlpEarlyFetch = null;
        var earlySentAt = early.sentAt;
        console.log('Loading active auctions from API (head-start):', fetchUrl);
        return early.promise.then(function (data) {
          if (!data) return fetchRaActiveAuctionsJson(fetchUrl);
          raSyncClock(data, earlySentAt);
          return data;
        });
      }
      console.log('Loading active auctions from API:', fetchUrl);
      return fetchRaActiveAuctionsJson(fetchUrl);
    }

    var dataPromise = fetchAmsPlpData(!!searchQuery);

    dataPromise
      .then(function (data) {
        // Clock sync happens in fetchRaActiveAuctionsJson, before this runs.
        if (fetchGen !== section._raPlpFetchGen) return;

        var auctions = (data && Array.isArray(data.auctions)) ? data.auctions : [];
        var promoTiles = (data && Array.isArray(data.promoTiles)) ? data.promoTiles : [];
        // Prefer a server-merged items[] when present (older middleware); otherwise
        // merge promoTiles into auctions client-side (TWS-1184 payload dedup).
        var items = (data && Array.isArray(data.items)) ? data.items : null;
        var mixedItems = (Array.isArray(items) && items.length)
          ? items
          : mergeRaPromoTilesIntoAuctions(auctions, promoTiles);

        // Search results should only show matching auction tiles (no promo inserts).
        if (searchQuery) {
          promoTiles = [];
          mixedItems = auctions.map(function (payload) {
            return { kind: 'auction', payload: payload };
          });
        }

        var total = typeof data.total === 'number' ? data.total : auctions.length;
        var pageSize = resolveRaPageSize(data, section);
        var page = typeof data.page === 'number' && data.page >= 1 ? data.page : 1;
        if (auctions.length === 0 && total > 0) {
          console.warn('[RA-PLP] API returned a non-zero total with no renderable auctions; normalizing empty UI.', { total: total, page: page });
          total = 0;
          page = 1;
        }
        var thresholdMin = data.timerCounterColorThresholdMinutes;
        if (section) {
          section.setAttribute('data-timer-threshold-minutes', (typeof thresholdMin === 'number' && !isNaN(thresholdMin)) ? String(thresholdMin) : '');
          applyMaxBidAmountDataAttribute(section, data.maxBidAmount);
          section.setAttribute('data-current-limit', String(pageSize));
          section.setAttribute('data-current-page', String(page));
          section.setAttribute('data-total', String(total));
        }
        var perPageSelect = section.querySelector('[data-per-page-select]');
        if (perPageSelect) {
          // Options come from the response every time, so they cannot drift when the
          // merchant edits the allowed sizes in the app admin.
          var allowedSizes = normalizeRaAllowedPageSizes(data.allowedPageSizes, pageSize);
          section._raAllowedPageSizes = allowedSizes;
          var labelTemplate =
            perPageSelect.getAttribute('data-per-page-label-template') || 'Show __COUNT__';
          perPageSelect.innerHTML = '';
          allowedSizes.forEach(function (n) {
            var opt = document.createElement('option');
            opt.value = String(n);
            opt.textContent = labelTemplate.replace('__COUNT__', String(n));
            perPageSelect.appendChild(opt);
          });
          perPageSelect.value = String(
            allowedSizes.indexOf(pageSize) !== -1 ? pageSize : allowedSizes[0]
          );
        }
        console.log('[RA-PLP] Client received auctions:', auctions.length, 'promos:', promoTiles.length, 'items:', mixedItems.length, 'total:', total, 'page:', page);
        if (loadingEl) loadingEl.remove();
        // TWS-1184: drop the server-rendered loading skeletons before the real
        // tiles go in (they share the grid cell class, so leaving them would
        // double up the first row).
        gridInner.querySelectorAll('[data-ra-skeleton]').forEach(function (el) { el.remove(); });
        if (emptyEl && auctions.length === 0) {
          if (!emptyEl.dataset.defaultEmpty) {
            emptyEl.dataset.defaultEmpty = emptyEl.textContent || '';
          }
          if (searchQuery) {
            emptyEl.textContent =
              'No auctions found for "' + searchQuery + '". Try another search.';
          } else if (emptyEl.dataset.defaultEmpty) {
            emptyEl.textContent = emptyEl.dataset.defaultEmpty;
          }
          emptyEl.removeAttribute('hidden');
        } else if (emptyEl) {
          emptyEl.setAttribute('hidden', '');
        }
        var isLoggedIn = section.getAttribute('data-customer-logged-in') === 'true';
        var loginUrl = section.getAttribute('data-login-url') || '/account/login';
        var tileOptions = {
          isLoggedIn: isLoggedIn,
          loginUrl: loginUrl,
          showQuickview: section.hasAttribute('data-rising-auctions-plp'),
          placeholderImageUrl: getPlaceholderImageUrl(section),
          imageWidths: getAuctionImageWidths(section),
          // Default on when attribute missing (older section markup).
          showRating: section.getAttribute('data-show-tile-rating') !== 'false',
          includeHoverImage: shouldIncludeAuctionTileHoverImage(section),
        };
        var visibleColumns = getVisibleAuctionColumnCount(section);
        var occupiedFirstRowSlots = 0;
        var productTileIndex = 0;
        var deferTiles = isDeferredAuctionTilesEnabled(section);
        mixedItems.forEach(function (item, index) {
          var kind = item && item.kind;
          var payload = item && item.payload;
          if (!payload) return;
          var itemSpan = kind === 'promo' && payload.span === 2 ? 2 : 1;
          var isFirstRow = occupiedFirstRowSlots < visibleColumns;
          occupiedFirstRowSlots += itemSpan;
          try {
            var cell = document.createElement('div');
            cell.className = 'rising-auctions-grid__cell';
            if (kind === 'promo' && payload.span === 2) {
              cell.classList.add('rising-auctions-grid__cell--span-2');
            }
            if (kind === 'promo') {
              if (deferTiles && !isFirstRow) {
                cell.setAttribute('data-ra-lazy-tile', '');
                cell.setAttribute('aria-busy', 'true');
                storeRaDeferredPayload(cell, {
                  kind: 'promo',
                  payload: payload,
                  currencySymbol: currencySymbol,
                });
                cell.appendChild(buildRaDeferredTilePlaceholder());
              } else {
                cell.appendChild(buildPromoTile(payload, currencySymbol, section, {
                  eager: isFirstRow && page === 1,
                }));
              }
            } else {
              // First-page products occupying the first grid row load eagerly.
              // The first product alone remains the high-priority LCP candidate.
              tileOptions.tileIndex = index;
              tileOptions.productTileIndex = productTileIndex;
              tileOptions.isFirstRow = isFirstRow;
              tileOptions.isFirstPage = (page === 1);
              if (deferTiles && !isFirstRow) {
                cell.setAttribute('data-ra-lazy-tile', '');
                cell.setAttribute('aria-busy', 'true');
                storeRaDeferredPayload(cell, {
                  kind: 'auction',
                  payload: payload,
                  currencySymbol: currencySymbol,
                  tileOptions: {
                    isLoggedIn: tileOptions.isLoggedIn,
                    loginUrl: tileOptions.loginUrl,
                    showQuickview: tileOptions.showQuickview,
                    placeholderImageUrl: tileOptions.placeholderImageUrl,
                    imageWidths: tileOptions.imageWidths,
                    showRating: tileOptions.showRating,
                    includeHoverImage: tileOptions.includeHoverImage,
                    tileIndex: index,
                    productTileIndex: productTileIndex,
                    isFirstRow: isFirstRow,
                    isFirstPage: (page === 1),
                  },
                });
                cell.appendChild(buildRaDeferredTilePlaceholder());
              } else {
                cell.appendChild(buildAuctionTile(payload, currencySymbol, tileOptions));
              }
              productTileIndex += 1;
            }
            gridInner.appendChild(cell);
          } catch (tileErr) {
            console.error('[RA-PLP] Failed to build ' + kind + ' tile at index ' + index + ':', tileErr, 'payload keys:', payload ? Object.keys(payload) : 'null');
          }
        });
        var countEl = section.querySelector('[data-plp-product-count]');
        if (countEl) countEl.textContent = '(' + total + ' product' + (total !== 1 ? 's' : '') + ')';
        var countLabel = section.querySelector('[data-plp-count-label]');
        if (countLabel) countLabel.textContent = total + ' product' + (total !== 1 ? 's' : '');
        var from = total === 0 ? 0 : (page - 1) * pageSize + 1;
        var to = total === 0 ? 0 : Math.min(page * pageSize, total);
        var fromEl = section.querySelector('[data-plp-showing-from]');
        var toEl = section.querySelector('[data-plp-showing-to]');
        var totalEl = section.querySelector('[data-plp-showing-total]');
        if (fromEl) fromEl.textContent = String(from);
        if (toEl) toEl.textContent = String(to);
        if (totalEl) totalEl.textContent = String(total);
        var totalPages = pageSize > 0 ? Math.ceil(total / pageSize) : 0;
        var paginationNeeded = totalPages > 1;
        var showingText = section.querySelector('[data-showing-text]');
        if (showingText) {
          if (paginationNeeded) {
            showingText.removeAttribute('hidden');
          } else {
            showingText.setAttribute('hidden', '');
          }
        }
        var paginationNav = section.querySelector('.rising-auctions-grid__pagination');
        if (paginationNav) {
          if (paginationNeeded) {
            paginationNav.removeAttribute('hidden');
          } else {
            paginationNav.setAttribute('hidden', '');
          }
        }
        renderPlpPaginationPages(section, page, totalPages);
        var paginationPrev = section.querySelector('[data-pagination-prev]');
        if (paginationPrev && paginationNeeded) {
          if (page <= 1) {
            paginationPrev.setAttribute('aria-disabled', 'true');
            paginationPrev.classList.add('rising-auctions-grid__page--disabled');
          } else {
            paginationPrev.removeAttribute('aria-disabled');
            paginationPrev.classList.remove('rising-auctions-grid__page--disabled');
          }
        }
        var paginationNextLink = section.querySelector('[data-pagination-next]');
        if (paginationNextLink && paginationNeeded) {
          if (page >= totalPages) {
            paginationNextLink.setAttribute('aria-disabled', 'true');
            paginationNextLink.classList.add('rising-auctions-grid__page--disabled');
          } else {
            paginationNextLink.removeAttribute('aria-disabled');
            paginationNextLink.classList.remove('rising-auctions-grid__page--disabled');
          }
        }

        section._raLastFacetPanel = data.facetPanel && typeof data.facetPanel === 'object'
          ? data.facetPanel
          : { configured: false, groups: [] };
        renderRaFacetPanel(section, section._raLastFacetPanel, gridInner);
        bindRaFacetEvents(section, gridInner);

        initAuctionTiles();
        if (deferTiles) {
          observeRaDeferredTileCells(section, gridInner);
        }
        initRisingAuctionsPubNub(section);
        refreshPlpAddToBagStates(section);

        setPlpFetchingUi(section, gridInner, false);

        if (section._raPlpScrollAfterPagination) {
          section._raPlpScrollAfterPagination = false;
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              scrollPlpAboveGrid(section, gridInner, 'instant');
            });
          });
        }
      })
      .catch(function (err) {
        if (fetchGen !== section._raPlpFetchGen) return;
        if (loadingEl) loadingEl.remove();
        // TWS-1184: clear loading skeletons on failure too, so they do not sit
        // above the error message.
        gridInner.querySelectorAll('[data-ra-skeleton]').forEach(function (el) { el.remove(); });
        if (emptyEl) {
          if (!emptyEl.dataset.defaultEmpty) {
            emptyEl.dataset.defaultEmpty = emptyEl.textContent || '';
          }
          emptyEl.textContent = searchQuery
            ? 'Search is temporarily unavailable. Please try again.'
            : emptyEl.dataset.defaultEmpty || emptyEl.textContent;
          emptyEl.removeAttribute('hidden');
        }
        console.error('[RA-PLP] Failed to load active auctions:', err);
        setPlpFetchingUi(section, gridInner, false);
        section._raPlpScrollAfterPagination = false;
      });
  }

  function plpRefetch(section, gridInner) {
    clearRaDeferredTileObserver(section);
    clearCountdownRegistry(section);
    var cells = gridInner.querySelectorAll('.rising-auctions-grid__cell');
    cells.forEach(function (el) { el.remove(); });
    // Overlay spinner only (setPlpFetchingUi) — do not re-append text [data-plp-loading] (TWS-866).
    var loadingEl = gridInner.querySelector('[data-plp-loading]');
    if (loadingEl) loadingEl.remove();
    var emptyEl = gridInner.querySelector('[data-plp-empty]');
    if (emptyEl) emptyEl.setAttribute('hidden', '');
    loadActiveAuctionsGrid(gridInner);
  }

  function initActiveAuctionsPlpGrid() {
    document.querySelectorAll('[data-grid-inner][data-api-url]').forEach(function (gridInner) {
      if (gridInner.dataset.raPlpLoaded) return;
      gridInner.dataset.raPlpLoaded = 'true';

      var section = gridInner.closest('.rising-auctions-grid');
      if (section && !section.getAttribute('data-current-page')) {
        try {
          var urlPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
          if (!isNaN(urlPage) && urlPage > 1) {
            section.setAttribute('data-current-page', String(urlPage));
          }
        } catch (e) {}
      }

      if (section) {
        // Deep-linked loads: pre-select sort + facets from the URL before the first fetch,
        // so a shared/ad link (e.g. ?facets=...&sort=...) renders the right view on a cold load.
        try {
          var urlSort = new URLSearchParams(window.location.search).get('sort');
          var sortSelectEl = section.querySelector('[data-sort-select]');
          if (urlSort && sortSelectEl) {
            var isValidSort = false;
            for (var si = 0; si < sortSelectEl.options.length; si++) {
              if (sortSelectEl.options[si].value === urlSort) { isValidSort = true; break; }
            }
            if (isValidSort) {
              sortSelectEl.value = urlSort;
              var sortDd = section.querySelector('[data-sort-dropdown]');
              if (sortDd) syncRaSortCustomUi(sortDd);
            }
          }
        } catch (e) {}
        var urlFacets = parseRaFacetsFromUrl();
        if (Object.keys(urlFacets).length) {
          ensureRaFacetState(section);
          section._raFacetApplied = urlFacets;
          section._raFacetDraft = cloneRaFacetMap(urlFacets);
        }
      }

      loadActiveAuctionsGrid(gridInner);

      if (!section) return;

      var sortSelect = section.querySelector('[data-sort-select]');
      if (sortSelect && !sortSelect.dataset.raSortListenerAttached) {
        sortSelect.dataset.raSortListenerAttached = 'true';
        sortSelect.addEventListener('change', function () {
          var dd = section.querySelector('[data-sort-dropdown]');
          if (dd) syncRaSortCustomUi(dd);
          section.setAttribute('data-current-page', '1');
          syncRaPlpUrl(section);
          plpRefetch(section, gridInner);
        });
      }
      initRaSortDropdown(section, gridInner);
      var perPageSelect = section.querySelector('[data-per-page-select]');
      if (perPageSelect && !perPageSelect.dataset.raPerPageListenerAttached) {
        perPageSelect.dataset.raPerPageListenerAttached = 'true';
        perPageSelect.addEventListener('change', function () {
          var limit = parseInt(perPageSelect.value, 10);
          if (isNaN(limit) || limit < 1) return;
          // Only send a size the server advertised — anything else is ignored server-side
          // and would silently snap back to its default.
          var allowed = section._raAllowedPageSizes;
          if (Array.isArray(allowed) && allowed.length > 0 && allowed.indexOf(limit) === -1) return;
          section.setAttribute('data-current-limit', String(limit));
          section.setAttribute('data-current-page', '1');
          // Scroll to the grid like pagination (TWS-1188): plpRefetch clears every
          // tile first, so the collapsing height strands the viewport at the footer.
          plpPaginateAndRefetch(section, gridInner);
        });
      }
      var paginationPrev = section.querySelector('[data-pagination-prev]');
      if (paginationPrev && !paginationPrev.dataset.raPaginationListenerAttached) {
        paginationPrev.dataset.raPaginationListenerAttached = 'true';
        paginationPrev.addEventListener('click', function (e) {
          e.preventDefault();
          var page = parseInt(section.getAttribute('data-current-page'), 10) || 1;
          if (page <= 1) return;
          section.setAttribute('data-current-page', String(page - 1));
          plpPaginateAndRefetch(section, gridInner);
        });
      }
      var paginationNextLink = section.querySelector('[data-pagination-next]');
      if (paginationNextLink && !paginationNextLink.dataset.raPaginationNextAttached) {
        paginationNextLink.dataset.raPaginationNextAttached = 'true';
        paginationNextLink.addEventListener('click', function (e) {
          e.preventDefault();
          var total = parseInt(section.getAttribute('data-total'), 10) || 0;
          // Set from the response's pageSize; if it is missing no response has landed yet.
          var limit = parseInt(section.getAttribute('data-current-limit'), 10);
          if (isNaN(limit) || limit < 1) return;
          var totalPages = Math.ceil(total / limit);
          var page = parseInt(section.getAttribute('data-current-page'), 10) || 1;
          if (page >= totalPages) return;
          section.setAttribute('data-current-page', String(page + 1));
          plpPaginateAndRefetch(section, gridInner);
        });
      }
      if (!section.dataset.raPlPageClickDelegated) {
        section.dataset.raPlPageClickDelegated = 'true';
        section.addEventListener('click', function (e) {
          var t = e.target && e.target.closest('[data-plp-page]');
          if (!t || !section.contains(t)) return;
          e.preventDefault();
          var n = parseInt(t.getAttribute('data-plp-page'), 10);
          if (isNaN(n) || n < 1) return;
          section.setAttribute('data-current-page', String(n));
          plpPaginateAndRefetch(section, gridInner);
        });
      }
    });
  }

  function validateAuctionBidInput(tile, input, options) {
    if (!tile || !input) return;
    var silent = options && options.silent === true;
    var currentBid = parseFloat(tile.getAttribute('data-current-bid'), 10);
    var bidIncrement = parseFloat(tile.getAttribute('data-bid-increment'), 10);
    var bidCount = parseInt(tile.getAttribute('data-bid-count'), 10);
    if (isNaN(currentBid)) currentBid = 0;
    if (isNaN(bidIncrement) || bidIncrement <= 0) bidIncrement = 1;
    if (isNaN(bidCount)) bidCount = 0;
    var minBid = bidCount === 0 ? currentBid : currentBid + bidIncrement;
    var section = getBidSectionRoot(tile);
    var maxBid = parseMaxBidAmountFromTileOrSection(tile, section);
    var currencySymbol = (section && section.getAttribute('data-currency-symbol')) || '£';
    var normalizedMinBid = normalizeBidAmount(minBid);
    if (maxBid != null && maxBid < minBid) {
      applyTooHighBidInputReset(tile, input, section);
      if (!silent) {
        showBidErrorModalMessage('Your bid value is too high, try with a lesser amount.');
      }
      return;
    }
    var raw = parseFloat(input.value, 10);
    var normalizedRaw = normalizeBidAmount(raw);
    var belowMin = isNaN(normalizedRaw) || normalizedRaw < minBid;
    var aboveMax = maxBid != null && !isNaN(normalizedRaw) && normalizedRaw > maxBid;
    if (aboveMax) {
      applyTooHighBidInputReset(tile, input, section);
      if (!silent) {
        showBidErrorModalMessage('Your bid value is too high, try with a lesser amount.');
      }
      return;
    }
    var val = normalizedRaw;
    if (isNaN(val) || val < minBid) {
      val = normalizedMinBid;
    }
    if (val < minBid) {
      input.value = formatBidInputValue(normalizedMinBid);
    } else {
      input.value = formatBidInputValue(val);
    }
    var minusBtn = tile.querySelector('[data-bid-minus]');
    if (minusBtn) {
      var currentVal = parseFloat(input.value, 10) || 0;
      minusBtn.disabled = currentVal <= minBid;
    }
    if (!silent && belowMin) {
      showBidErrorModal(normalizedMinBid, currencySymbol, tile);
    }
  }

  /**
   * A bid placed in the quickview only touches the modal's own article, so the
   * grid tile behind it keeps showing "Bid now". Push the same auction's fresh
   * state onto every other tile on the page so the CTA, amount, bid count and
   * next-bid input match what was just confirmed by the API.
   */
  function syncSiblingAuctionTilesAfterBid(sourceTile, section, newCurrentBid, newBidCount, newBidIncrement, isUserWinning, winningInitials) {
    if (!sourceTile) return;
    var auctionId = (sourceTile.getAttribute('data-auction-id') || '').trim();
    if (!auctionId) return;
    var symbol = (section && section.getAttribute('data-currency-symbol')) || '£';
    document.querySelectorAll('[data-auction-tile]').forEach(function (other) {
      if (other === sourceTile) return;
      if ((other.getAttribute('data-auction-id') || '').trim() !== auctionId) return;
      other.setAttribute('data-current-bid', String(newCurrentBid));
      other.setAttribute('data-bid-count', String(newBidCount));
      other.setAttribute('data-bid-increment', String(newBidIncrement));
      other.setAttribute('data-special-auction', newBidIncrement > 1 ? 'true' : 'false');
      setTileBidAmountDisplay(other.querySelector('.rising-auctions-tile__bid-amount'), newCurrentBid, symbol);
      var otherCountEl = other.querySelector('.rising-auctions-tile__bid-count');
      if (otherCountEl) otherCountEl.textContent = '(' + newBidCount + ' bids)';
      other.querySelectorAll('.rising-auctions-tile__winner-pill').forEach(function (winnerPill) {
        if (isUserWinning) {
          winnerPill.className = 'rising-auctions-tile__winner-pill rising-auctions-tile__winner-pill--winning';
          winnerPill.innerHTML = getTileStatusIconHtml('thumbs-up') + 'You\'re winning!';
        } else {
          winnerPill.className = 'rising-auctions-tile__winner-pill';
          winnerPill.innerHTML = getTileStatusIconHtml('thumbs-down') + (winningInitials ? winningInitials + ' is winning!' : '— is winning!');
        }
      });
      var otherCtaWrap = other.querySelector('.rising-auctions-tile__actions-state--ctas');
      if (otherCtaWrap) {
        otherCtaWrap.querySelectorAll('[data-bid-submit]').forEach(function (otherBtn) {
          otherBtn.textContent = 'Increase bid';
          otherBtn.className = 'rising-auctions-tile__btn rising-auctions-tile__btn--increase';
        });
      }
      other.querySelectorAll('[data-auction-pdp-submit]').forEach(function (otherPdpBtn) {
        otherPdpBtn.textContent = 'Increase bid';
        otherPdpBtn.classList.add('auction-pdp__btn--has-bid');
      });
      other.querySelectorAll('[data-bid-input]').forEach(function (otherInput) {
        var nextValue = newBidCount === 0 ? newCurrentBid : newCurrentBid + newBidIncrement;
        var otherCap = parseMaxBidAmountFromTileOrSection(other, section);
        if (otherCap != null && nextValue > otherCap) nextValue = otherCap;
        otherInput.value = formatBidInputValue(nextValue);
        validateAuctionBidInput(other, otherInput, { silent: true });
      });
    });
  }

  function initAuctionTiles() {
    document.querySelectorAll('[data-auction-tile]').forEach(function (tile) {
      bindAuctionTileImages(tile);
      if (tile.dataset.raTileInit) return;
      tile.dataset.raTileInit = 'true';

      var controlsList = tile.querySelectorAll('[data-tile-bid-controls]');
      if (controlsList.length) {
        controlsList.forEach(function (controls) {
        var input = controls.querySelector('[data-bid-input]');
        var minusBtn = controls.querySelector('[data-bid-minus]');
        var plusBtn = controls.querySelector('[data-bid-plus]');
        // Read on every click: live updates and bid replies change the price and increment
        // after the tile is built, so values captured here would go stale.
        function readTileBidIncrement() {
          var inc = parseFloat(tile.getAttribute('data-bid-increment'), 10);
          return isNaN(inc) || inc <= 0 ? 1 : inc;
        }
        function readTileMinBid() {
          var currentBid = parseFloat(tile.getAttribute('data-current-bid'), 10);
          var bidCount = parseInt(tile.getAttribute('data-bid-count'), 10);
          if (isNaN(currentBid)) currentBid = 0;
          if (isNaN(bidCount)) bidCount = 0;
          return bidCount === 0 ? currentBid : currentBid + readTileBidIncrement();
        }

        validateAuctionBidInput(tile, input, { silent: true });

        if (input && minusBtn) {
          minusBtn.addEventListener('click', function () {
            var val = normalizeBidAmount(parseFloat(input.value, 10));
            if (isNaN(val)) val = readTileMinBid();
            input.value = formatBidInputValue(val - readTileBidIncrement());
            validateAuctionBidInput(tile, input);
          });
        }
        if (input && plusBtn) {
          plusBtn.addEventListener('click', function () {
            var sectionPlus = getBidSectionRoot(tile);
            var maxBidPlus = parseMaxBidAmountFromTileOrSection(tile, sectionPlus);
            var val = normalizeBidAmount(parseFloat(input.value, 10)) || 0;
            // Never step up from below the live minimum.
            var minBidPlus = normalizeBidAmount(readTileMinBid());
            if (val < minBidPlus) val = minBidPlus - readTileBidIncrement();
            var newVal = val + readTileBidIncrement();
            if (maxBidPlus != null && newVal > maxBidPlus) newVal = maxBidPlus;
            input.value = formatBidInputValue(newVal);
            validateAuctionBidInput(tile, input);
          });
        }
        if (input) {
          input.addEventListener('input', function () {
            enforceBidInputDigitLimit(input);
          });
          input.addEventListener('blur', function () {
            validateAuctionBidInput(tile, input);
          });
          input.addEventListener('change', function () {
            validateAuctionBidInput(tile, input);
          });
        }
        });
      }

      var pdpBidInputOnly = tile.querySelector('.auction-pdp__input[data-bid-input]');
      if (pdpBidInputOnly && !controlsList.length) {
        pdpBidInputOnly.addEventListener('input', function () {
          enforceBidInputDigitLimit(pdpBidInputOnly);
        });
        pdpBidInputOnly.addEventListener('blur', function () {
          validateAuctionBidInput(tile, pdpBidInputOnly);
        });
        pdpBidInputOnly.addEventListener('change', function () {
          validateAuctionBidInput(tile, pdpBidInputOnly);
        });
      }

      function getInputForSubmit(submitBtn) {
        var submitScope = submitBtn.closest('.rising-auctions-active-bids__cell--increase, .rising-auctions-active-bids__mobile-card-actions, .rising-auctions-tile__actions, .auction-pdp__actions');
        var scopedInput = submitScope ? submitScope.querySelector('[data-bid-input]') : null;
        return scopedInput || tile.querySelector('[data-bid-input]');
      }

      var submitButtons = tile.querySelectorAll('[data-bid-submit]');
      if (submitButtons.length) {
        submitButtons.forEach(function (submitBtn) {
        submitBtn.addEventListener('click', function () {
          if (tile.getAttribute('data-auction-expired') === 'true') {
            showBidErrorModalMessage('The auction is expired.');
            return;
          }
          var section = getBidSectionRoot(tile);
          if (!section) return;
          if (section.getAttribute('data-customer-logged-in') !== 'true') {
            showRaBidLoginPrompt(section);
            return;
          }
          var auctionId = tile.getAttribute('data-auction-id');
          var currentBid = parseFloat(tile.getAttribute('data-current-bid'), 10);
          var bidIncrement = parseFloat(tile.getAttribute('data-bid-increment'), 10);
          var bidCount = parseInt(tile.getAttribute('data-bid-count'), 10);
          if (isNaN(currentBid)) currentBid = 0;
          if (isNaN(bidIncrement) || bidIncrement <= 0) bidIncrement = 1;
          if (isNaN(bidCount)) bidCount = 0;
          var minBid = bidCount === 0 ? currentBid : currentBid + bidIncrement;
          var input = getInputForSubmit(submitBtn);
          var amount = input ? normalizeBidAmount(parseFloat(input.value, 10)) : NaN;
          var normalizedMinBid = normalizeBidAmount(minBid);
          var maxBidCap = parseMaxBidAmountFromTileOrSection(tile, section);
          var currencySymbolPre = section.getAttribute('data-currency-symbol') || '£';
          if (isNaN(amount)) {
            if (input) input.value = formatBidInputValue(normalizedMinBid);
            showBidErrorModal(normalizedMinBid, currencySymbolPre, tile);
            return;
          }
          if (maxBidCap != null && minBid > maxBidCap) {
            applyTooHighBidInputReset(tile, input, section);
            showBidErrorModalMessage('Your bid value is too high, try with a lesser amount.');
            return;
          }
          if (maxBidCap != null && amount > maxBidCap) {
            applyTooHighBidInputReset(tile, input, section);
            showBidErrorModalMessage('Your bid value is too high, try with a lesser amount.');
            return;
          }
          if (amount < minBid) {
            amount = normalizedMinBid;
          }
          if (input) input.value = formatBidInputValue(amount);
          if (amount < minBid) {
            showBidErrorModal(normalizedMinBid, currencySymbolPre, tile);
            return;
          }
          var customerId = section.getAttribute('data-customer-id');
          var customerName = (section.getAttribute('data-customer-name') || '').trim() || 'Customer';
          var customerEmail = (section.getAttribute('data-customer-email') || '').trim();
          if (!customerId) {
            showRaBidLoginPrompt(section);
            return;
          }
          if (!customerEmail) {
            console.error('[RA-PLP] Missing customer email for bid');
            showBidErrorModalMessage(RA_BID_NO_EMAIL_MESSAGE);
            return;
          }
          var apiUrl = (section.querySelector('[data-grid-inner]') && section.querySelector('[data-grid-inner]').getAttribute('data-api-url')) || section.getAttribute('data-api-url') || '';
          var baseUrl = apiUrl.replace(/\/active-auctions\/?$/, '').replace(/\/my-active-bids\/?$/, '').replace(/\/favourites\/?$/, '');
          var bidUrl = baseUrl ? (baseUrl + '/bid') : '/apps/rising-auctions/bid';
          var bidLoaderHost = getBidLoaderHostElement(submitBtn);
          var bidLoader = window.loader;
          if (bidLoader && typeof bidLoader.start === 'function' && bidLoaderHost) {
            bidLoader.start(bidLoaderHost);
          }
          submitBtn.disabled = true;
          /**
           * Paints a bid result on the tile. `d` is the bid reply, or the auction state fetched
           * after an unclear failure. `confirmed` is true only when this bid is known to have landed.
           */
          function applyBidStateToTile(d, confirmed) {
          var newBidCount = d.bidCount != null ? d.bidCount : bidCount + 1;
          var newCurrentBid = d.currentBid != null ? d.currentBid : amount;
          var isUserWinning = d.isUserWinning === true;
          var winningInitialsFromApi = (d.winningInitials != null && d.winningInitials !== '') ? d.winningInitials : '';
          tile.setAttribute('data-current-bid', String(newCurrentBid));
          tile.setAttribute('data-bid-count', String(newBidCount));
          if (d.maxBidAmount !== undefined) {
            applyMaxBidAmountToTile(tile, d.maxBidAmount);
          }
          var newIncFromApi = (d.bidIncrement != null ? d.bidIncrement : bidIncrement) || 1;
          if (isNaN(newIncFromApi) || newIncFromApi <= 0) newIncFromApi = 1;
          tile.setAttribute('data-bid-increment', String(newIncFromApi));
          tile.setAttribute('data-special-auction', newIncFromApi > 1 ? 'true' : 'false');
          var bidAmountEl = tile.querySelector('.rising-auctions-tile__bid-amount');
          setTileBidAmountDisplay(
            bidAmountEl,
            newCurrentBid,
            section.getAttribute('data-currency-symbol') || '£'
          );
          var bidCountEl = tile.querySelector('.rising-auctions-tile__bid-count');
          if (bidCountEl) bidCountEl.textContent = '(' + newBidCount + ' bids)';
          tile.querySelectorAll('.rising-auctions-tile__winner-pill').forEach(function (winnerPill) {
            if (isUserWinning) {
              winnerPill.className = 'rising-auctions-tile__winner-pill rising-auctions-tile__winner-pill--winning';
              winnerPill.innerHTML = getTileStatusIconHtml('thumbs-up') + 'You\'re winning!';
            } else {
              winnerPill.className = 'rising-auctions-tile__winner-pill';
              winnerPill.innerHTML = getTileStatusIconHtml('thumbs-down') + (winningInitialsFromApi ? winningInitialsFromApi + ' is winning!' : '— is winning!');
            }
          });
          raQwSyncPdpBidRowFromPubNub(
            tile,
            newCurrentBid,
            newBidCount,
            isUserWinning,
            winningInitialsFromApi,
            section.getAttribute('data-currency-symbol') || '£'
          );
          var ctaWrap = tile.querySelector('.rising-auctions-tile__actions-state--ctas');
          if (d.hasUserBid === true && ctaWrap && submitBtn.parentNode === ctaWrap) {
            submitBtn.textContent = 'Increase bid';
            submitBtn.className = 'rising-auctions-tile__btn rising-auctions-tile__btn--increase';
          }
          syncSiblingAuctionTilesAfterBid(
            tile,
            section,
            newCurrentBid,
            newBidCount,
            newIncFromApi,
            isUserWinning,
            winningInitialsFromApi
          );
          var pdpSubmitAfterBid = tile.querySelector('[data-auction-pdp-submit]');
          if (d.hasUserBid === true && pdpSubmitAfterBid) {
            pdpSubmitAfterBid.textContent = 'Increase bid';
            pdpSubmitAfterBid.classList.add('auction-pdp__btn--has-bid');
          }
          var controls = tile.querySelectorAll('[data-tile-bid-controls]');
          if (controls.length) {
            controls.forEach(function (controls) {
            var inp = controls.querySelector('[data-bid-input]');
            if (inp) {
              var minNext = newBidCount === 0 ? newCurrentBid : newCurrentBid + newIncFromApi;
              var nextInput = minNext;
              var maxCapSuccess = parseMaxBidAmountFromTileOrSection(tile, section);
              if (maxCapSuccess != null && nextInput > maxCapSuccess) nextInput = maxCapSuccess;
              inp.value = formatBidInputValue(nextInput);
              validateAuctionBidInput(tile, inp, { silent: true });
            }
            });
          } else {
            var pdpInpSucc = tile.querySelector('.auction-pdp__input[data-bid-input]');
            if (pdpInpSucc) {
              var minNext2 = newBidCount === 0 ? newCurrentBid : newCurrentBid + newIncFromApi;
              var nextInput2 = minNext2;
              var maxCap2 = parseMaxBidAmountFromTileOrSection(tile, section);
              if (maxCap2 != null && nextInput2 > maxCap2) nextInput2 = maxCap2;
              pdpInpSucc.value = formatBidInputValue(nextInput2);
              validateAuctionBidInput(tile, pdpInpSucc, { silent: true });
            }
          }
          var currencySymbol = section.getAttribute('data-currency-symbol') || '£';
          if (confirmed && tile.classList && tile.classList.contains('rising-auctions-active-bids__row')) {
            tile.querySelectorAll('.rising-auctions-active-bids__current-bid').forEach(function (currentBidCell) {
              currentBidCell.textContent = formatMoney(newCurrentBid, currencySymbol);
            });
            tile.querySelectorAll('.rising-auctions-active-bids__your-bid-amount').forEach(function (yourBidAmountCell) {
              yourBidAmountCell.textContent = formatMoney(amount, currencySymbol);
            });
            var winningStatusHtml = '<span class="rising-auctions-tile__winner-pill rising-auctions-tile__winner-pill--winning">' +
              getTileStatusIconHtml('thumbs-up') + 'You\'re winning!' +
              '</span>';
            var yourBidCell = tile.querySelector('.rising-auctions-active-bids__cell--your-bid');
            var statusTextEl = yourBidCell ? yourBidCell.querySelector('.rising-auctions-active-bids__status-text') : null;
            tile.querySelectorAll('.rising-auctions-tile__winner-pill').forEach(function (pill) {
              pill.className = 'rising-auctions-tile__winner-pill rising-auctions-tile__winner-pill--winning';
              pill.innerHTML = getTileStatusIconHtml('thumbs-up') + 'You\'re winning!';
            });
            if (statusTextEl) {
              statusTextEl.outerHTML = winningStatusHtml;
            }
            if (tile.hasAttribute('data-favourites-row') && !tile.querySelector('.rising-auctions-active-bids__mobile-card-status')) {
              var mobileCardInfo = tile.querySelector('.rising-auctions-active-bids__mobile-card-info');
              if (mobileCardInfo) {
                mobileCardInfo.insertAdjacentHTML('beforeend', '<div class="rising-auctions-active-bids__mobile-card-status">' + winningStatusHtml + '</div>');
              }
            }
            tile.querySelectorAll('.rising-auctions-active-bids__outbid-msg').forEach(function (outbidMsg) {
              outbidMsg.remove();
            });
            if (tile.hasAttribute('data-favourites-row')) {
              tile.querySelectorAll('[data-bid-submit]').forEach(function (rowSubmitBtn) {
                rowSubmitBtn.textContent = 'Increase Bid';
                rowSubmitBtn.className = 'rising-auctions-active-bids__increase-btn';
              });
            }
          }
          try { document.dispatchEvent(new CustomEvent('rising-auctions:refresh-cta-badges')); } catch (e) {}
          }
          /**
           * The bid reply was lost (timeout, dropped connection, proxy error page, 5xx). The bid may
           * still have landed, so read the auction state and show it as it is. No message is shown
           * either way: the shopper sees the live auction and can bid again.
           */
          function recheckBidQuietly() {
            return fetchRaAuctionStateQuiet(baseUrl, auctionId, customerId)
              .then(function (state) {
                if (!state) return;
                var landed =
                  state.isUserWinning === true &&
                  typeof state.currentBid === 'number' &&
                  state.currentBid >= amount - 0.001;
                applyBidStateToTile(state, landed);
              })
              .catch(function (err) {
                console.warn('[RA-PLP] Auction re-check after bid failed:', err);
              })
              .then(function () {
                submitBtn.disabled = false;
              });
          }
          /**
           * "Bid amount too low": the price moved since the tile was drawn. Put the server's
           * minimum in the input now, then read the auction so price, bid count and winner
           * are current too. The modal already tells the shopper the new minimum.
           */
          function applyMinimumBidFromRejection(minRequired) {
            if (input && typeof minRequired === 'number' && !isNaN(minRequired)) {
              input.value = formatBidInputValue(normalizeBidAmount(minRequired));
              validateAuctionBidInput(tile, input, { silent: true });
            }
            fetchRaAuctionStateQuiet(baseUrl, auctionId, customerId)
              .then(function (state) {
                if (state) applyBidStateToTile(state, false);
              })
              .catch(function (err) {
                console.warn('[RA-PLP] Auction re-read after rejected bid failed:', err);
              });
          }
          /** The server says the auction is closed: show ENDED and stop further bids from this tile. */
          function markTileEndedFromRejection() {
            var endedState = buildCountdownTileState(tile);
            if (endedState) {
              applyLocalTileExpiry(tile, endedState);
              unregisterCountdownTile(section, tile);
            } else {
              tile.setAttribute('data-auction-expired', 'true');
            }
            submitBtn.disabled = true;
          }
          var bidSentAt = Date.now();
          postRaBidJson(bidUrl, {
            auctionId: auctionId,
            amount: amount,
            customerId: customerId,
            customerName: customerName,
            customerEmail: customerEmail,
          })
            .then(function (result) {
              raSyncClock(result.data, bidSentAt);
              if (isRaBidLoginRequired(result)) {
                submitBtn.disabled = false;
                showRaBidLoginPrompt(section, "We couldn't verify your sign-in. Please try your bid again, or log in.");
                return;
              }
              if (isRaBidOutcomeUnclear(result)) {
                console.warn('[RA-PLP] Bid reply unclear, re-checking auction:', result.status, result.data);
                return recheckBidQuietly();
              }
              submitBtn.disabled = false;
              if (!result.ok) {
                if (result.data && result.data.minimumBid != null) {
                  var currencySymbolErr = section.getAttribute('data-currency-symbol') || '£';
                  var minRequired = result.data.minimumBid;
                  var maxCapErr = parseMaxBidAmountFromTileOrSection(tile, section);
                  if (maxCapErr != null && typeof minRequired === 'number' && !isNaN(minRequired) && minRequired > maxCapErr) {
                    applyTooHighBidInputReset(tile, input, section);
                    showBidErrorModalMessage('Your bid value is too high, try with a lesser amount.');
                  } else {
                    showBidErrorModal(minRequired, currencySymbolErr, tile);
                    applyMinimumBidFromRejection(minRequired);
                  }
                } else if (result.data && (result.data.error === 'Bid too high' || result.data.message && result.data.message.indexOf('too high') !== -1)) {
                  applyTooHighBidInputReset(tile, input, section);
                  showBidErrorModalMessage('Your bid value is too high, try with a lesser amount.');
                } else if (result.data && (result.data.error === 'Auction has ended' || result.data.error === 'Auction is not open for bidding')) {
                  markTileEndedFromRejection();
                  showBidErrorModalMessage('The auction is expired.');
                } else if (result.data && result.data.error) {
                  showBidErrorModalMessage(String(result.data.error));
                } else {
                  console.error('[RA-PLP] Bid failed:', result.status, result.data);
                  showBidErrorModalMessage('Something went wrong.');
                }
                return;
              }
              applyBidStateToTile(result.data, true);
            })
            .catch(function (err) {
              console.warn('[RA-PLP] Bid request error, re-checking auction:', err);
              return recheckBidQuietly();
            })
            .finally(function () {
              if (bidLoader && typeof bidLoader.stop === 'function' && bidLoaderHost) {
                bidLoader.stop(bidLoaderHost);
              }
            });
        });
        });
      }

      var countdownEls = tile.querySelectorAll('[data-countdown-text]');
      var countdownEnds = tile.getAttribute('data-countdown-ends');
      if (countdownEls.length && countdownEnds) {
        registerCountdownTile(getBidSectionRoot(tile), tile);
      }

      var pdpCountdownEl = tile.querySelector('[data-auction-pdp-countdown]');
      var countdownEndsPdp = tile.getAttribute('data-countdown-ends');
      if (pdpCountdownEl && countdownEndsPdp && !countdownEls.length) {
        var endMsPdp = new Date(countdownEndsPdp).getTime();
        var qwSectionRoot = getBidSectionRoot(tile);
        function tickQwPdp() {
          if (raNow() >= endMsPdp) {
            tile.setAttribute('data-auction-expired', 'true');
            raQwUpdatePdpCountdown(pdpCountdownEl, endMsPdp, qwSectionRoot);
            var qwM = tile.closest('[data-ra-qw-bid-mount]');
            if (qwM) {
              refetchRaQwBidMountOnExpiry(qwM, qwSectionRoot, tile);
            }
            return;
          }
          raQwUpdatePdpCountdown(pdpCountdownEl, endMsPdp, qwSectionRoot);
          setTimeout(tickQwPdp, 1000);
        }
        raQwUpdatePdpCountdown(pdpCountdownEl, endMsPdp, qwSectionRoot);
        tickQwPdp();
      }
    });
  }

  var ACCOUNT_PAGE_SIZE = 24;
  var ACCOUNT_PAGINATION_WINDOW = 5;

  function appendAccountQueryParam(url, key, value) {
    return url + fetchUrlSep(url) + encodeURIComponent(key) + '=' + encodeURIComponent(value);
  }

  function setAccountPaginationDisabled(link, disabled) {
    if (!link) return;
    if (disabled) {
      link.setAttribute('aria-disabled', 'true');
      link.classList.add('rising-auctions-active-bids__page--disabled');
    } else {
      link.removeAttribute('aria-disabled');
      link.classList.remove('rising-auctions-active-bids__page--disabled');
    }
  }

  function renderAccountPaginationPages(section, page, totalPages) {
    var pagesContainer = section.querySelector('[data-account-pagination-pages]');
    if (!pagesContainer) return;
    pagesContainer.innerHTML = '';
    if (totalPages <= 1) {
      pagesContainer.setAttribute('hidden', '');
      pagesContainer.setAttribute('aria-hidden', 'true');
      return;
    }
    pagesContainer.removeAttribute('hidden');
    pagesContainer.removeAttribute('aria-hidden');
    var start = Math.max(1, page - ACCOUNT_PAGINATION_WINDOW);
    var end = Math.min(totalPages, page + ACCOUNT_PAGINATION_WINDOW);
    for (var p = start; p <= end; p++) {
      if (p === page) {
        var span = document.createElement('span');
        span.className = 'rising-auctions-active-bids__page is-current';
        span.setAttribute('aria-current', 'page');
        span.textContent = String(p);
        pagesContainer.appendChild(span);
      } else {
        var a = document.createElement('a');
        a.href = '#';
        a.className = 'rising-auctions-active-bids__page';
        a.setAttribute('data-account-page', String(p));
        a.textContent = String(p);
        pagesContainer.appendChild(a);
      }
    }
  }

  function updateAccountPaginationUi(section, page, pageSize, total) {
    var footer = section.querySelector('[data-account-pagination-footer]');
    var fromEl = section.querySelector('[data-account-showing-from]');
    var toEl = section.querySelector('[data-account-showing-to]');
    var totalEl = section.querySelector('[data-account-showing-total]');
    var totalPages = pageSize > 0 ? Math.ceil(total / pageSize) : 0;
    var from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    var to = total === 0 ? 0 : Math.min(page * pageSize, total);

    if (footer) footer.hidden = total === 0;
    if (fromEl) fromEl.textContent = String(from);
    if (toEl) toEl.textContent = String(to);
    if (totalEl) totalEl.textContent = String(total);

    renderAccountPaginationPages(section, page, totalPages);
    setAccountPaginationDisabled(section.querySelector('[data-account-pagination-prev]'), page <= 1);
    setAccountPaginationDisabled(section.querySelector('[data-account-pagination-next]'), page >= totalPages || totalPages <= 1);
  }

  function syncAccountPageToUrl(page) {
    try {
      var u = new URL(window.location.href);
      if (page > 1) { u.searchParams.set('page', String(page)); } else { u.searchParams.delete('page'); }
      history.replaceState(null, '', u.toString());
    } catch (e) {}
  }

  function initAccountPagination(section, renderFn) {
    if (section.dataset.raAccountPaginationInit) return;
    section.dataset.raAccountPaginationInit = 'true';
    var prev = section.querySelector('[data-account-pagination-prev]');
    if (prev) {
      prev.addEventListener('click', function (e) {
        e.preventDefault();
        var page = parseInt(section.getAttribute('data-current-page'), 10) || 1;
        if (page <= 1) return;
        section.setAttribute('data-current-page', String(page - 1));
        syncAccountPageToUrl(page - 1);
        renderFn();
      });
    }
    var next = section.querySelector('[data-account-pagination-next]');
    if (next) {
      next.addEventListener('click', function (e) {
        e.preventDefault();
        var total = parseInt(section.getAttribute('data-total'), 10) || 0;
        var limit = parseInt(section.getAttribute('data-current-limit'), 10) || ACCOUNT_PAGE_SIZE;
        var totalPages = limit > 0 ? Math.ceil(total / limit) : 0;
        var page = parseInt(section.getAttribute('data-current-page'), 10) || 1;
        if (page >= totalPages) return;
        section.setAttribute('data-current-page', String(page + 1));
        syncAccountPageToUrl(page + 1);
        renderFn();
      });
    }
    section.addEventListener('click', function (e) {
      var target = e.target && e.target.closest('[data-account-page]');
      if (!target || !section.contains(target)) return;
      e.preventDefault();
      var page = parseInt(target.getAttribute('data-account-page'), 10);
      if (isNaN(page) || page < 1) return;
      section.setAttribute('data-current-page', String(page));
      syncAccountPageToUrl(page);
      renderFn();
    });
  }

  function renderAccountBidsPage(section, options) {
    var loadingEl = section.querySelector(options.loadingSelector);
    var emptyEl = section.querySelector(options.emptySelector);
    var tableWrap = section.querySelector(options.tableWrapSelector);
    var tbody = section.querySelector(options.tbodySelector);
    var apiUrl = section.getAttribute('data-api-url');
    var currencySymbol = section.getAttribute('data-currency-symbol') || '£';
    var plpUrl = (section.getAttribute('data-plp-url') || '').trim() || '/collections/online-auctions';
    if (!apiUrl || !tbody) return;

    var page = parseInt(section.getAttribute('data-current-page'), 10) || 1;
    var pageSize = parseInt(section.getAttribute('data-current-limit'), 10) || ACCOUNT_PAGE_SIZE;
    section.setAttribute('data-current-page', String(page));
    section.setAttribute('data-current-limit', String(pageSize));

    if (loadingEl) loadingEl.hidden = false;
    if (emptyEl) emptyEl.hidden = true;

    var fetchUrl = appendAccountQueryParam(apiUrl, 'limit', pageSize);
    fetchUrl = appendAccountQueryParam(fetchUrl, 'page', page);

    var accountSentAt = Date.now();
    fetch(fetchUrl, { credentials: 'include' })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        raSyncClock(data, accountSentAt);
        if (loadingEl) loadingEl.hidden = true;
        var auctions = Array.isArray(data.auctions) ? data.auctions : [];
        var total = typeof data.total === 'number' ? data.total : auctions.length;
        var responsePageSize = typeof data.pageSize === 'number' && data.pageSize > 0 ? data.pageSize : pageSize;
        var responsePage = typeof data.page === 'number' && data.page >= 1 ? data.page : page;
        var totalPages = responsePageSize > 0 ? Math.ceil(total / responsePageSize) : 0;
        if (auctions.length === 0 && total > 0 && responsePage <= totalPages) {
          console.warn('[RA] Account bids API returned a non-zero total with no renderable rows; normalizing empty UI.', { total: total, page: responsePage });
          total = 0;
          responsePage = 1;
          totalPages = 0;
        }

        if (auctions.length === 0 && total > 0 && responsePage > totalPages && totalPages > 0) {
          section.setAttribute('data-current-page', String(totalPages));
          renderAccountBidsPage(section, options);
          return;
        }

        section.setAttribute('data-current-page', String(responsePage));
        section.setAttribute('data-current-limit', String(responsePageSize));
        section.setAttribute('data-total', String(total));
        applyMaxBidAmountDataAttribute(section, data.maxBidAmount);
        if (data.timerCounterColorThresholdMinutes != null) {
          section.setAttribute('data-timer-threshold-minutes', String(data.timerCounterColorThresholdMinutes));
        }

        if (data.requiresLogin || total === 0) {
          if (emptyEl) {
            emptyEl.hidden = false;
            var bidNowLink = emptyEl.querySelector('.rising-auctions-active-bids__bid-now-btn');
            if (bidNowLink && plpUrl) bidNowLink.href = plpUrl;
          }
          if (tableWrap) tableWrap.hidden = true;
          clearCountdownRegistry(section);
          tbody.innerHTML = '';
          updateAccountPaginationUi(section, responsePage, responsePageSize, total);
          return;
        }

        if (emptyEl) emptyEl.hidden = true;
        if (tableWrap) tableWrap.hidden = false;
        clearCountdownRegistry(section);
        tbody.innerHTML = '';
        auctions.forEach(function (payload) {
          var tr = options.buildRow(payload, currencySymbol, section);
          tbody.appendChild(tr);
        });
        wireAccountListImageHovers(tbody);
        updateAccountPaginationUi(section, responsePage, responsePageSize, total);
        initAuctionTiles();
        initRisingAuctionsPubNub(section);
      })
      .catch(function (err) {
        if (loadingEl) loadingEl.hidden = true;
        if (emptyEl) emptyEl.hidden = false;
        if (tableWrap) tableWrap.hidden = true;
        var footer = section.querySelector('[data-account-pagination-footer]');
        if (footer) footer.hidden = true;
        console.error(options.errorMessage, err);
      });
  }

  /**
   * Active Bids page: fetch my-active-bids and render table or empty state.
   * Table rows use data-auction-tile and same data attrs so initAuctionTiles (timer, bid) works.
   */
  function loadActiveBidsPage() {
    var section = document.querySelector('[data-active-bids]');
    if (!section) return;
    if (!section.getAttribute('data-current-page')) {
      try {
        var urlPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
        if (!isNaN(urlPage) && urlPage > 1) section.setAttribute('data-current-page', String(urlPage));
      } catch (e) {}
    }
    var render = function () {
      renderAccountBidsPage(section, {
        loadingSelector: '[data-active-bids-loading]',
        emptySelector: '[data-active-bids-empty]',
        tableWrapSelector: '[data-active-bids-table-wrap]',
        tbodySelector: '[data-active-bids-tbody]',
        buildRow: buildActiveBidsRow,
        errorMessage: '[RA] Active Bids load failed:',
      });
    };
    initAccountPagination(section, render);
    render();
  }

  /**
   * Thumbnail markup for Active Bids / Favourites: stacked primary/secondary with hover when both exist.
   */
  function buildAccountListImageHtml(featuredUrl, secondaryUrl, placeholderImageUrl, altText) {
    var pri = resolveAuctionImageUrl(featuredUrl, placeholderImageUrl);
    var sec = cleanImageUrl(secondaryUrl);
    if (pri && sec && sec !== pri) {
      return '<div class="rising-auctions-active-bids__media-inner" data-account-media-hover>' +
        '<img src="' + escapeHtml(pri) + '" alt="' + escapeHtml(altText || '') + '" width="80" height="80" loading="lazy" class="rising-auctions-active-bids__img rising-auctions-active-bids__img--primary">' +
        '<img src="' + escapeHtml(sec) + '" alt="" width="80" height="80" loading="lazy" class="rising-auctions-active-bids__img rising-auctions-active-bids__img--secondary">' +
        '</div>';
    }
    if (pri) {
      return '<img src="' + escapeHtml(pri) + '" alt="' + escapeHtml(altText || '') + '" width="80" height="80" loading="lazy" class="rising-auctions-active-bids__img">';
    }
    return '<div class="rising-auctions-active-bids__img-placeholder">No image</div>';
  }

  function wireAccountListImageHovers(root) {
    if (!root) return;
    root.querySelectorAll('[data-account-media-hover]').forEach(function (wrap) {
      if (wrap.dataset.raAccountHoverAttached) return;
      wrap.dataset.raAccountHoverAttached = 'true';
      wrap.addEventListener('mouseenter', function () {
        wrap.classList.add('rising-auctions-active-bids__media-inner--show-alt');
      });
      wrap.addEventListener('mouseleave', function () {
        wrap.classList.remove('rising-auctions-active-bids__media-inner--show-alt');
      });
    });
  }

  function buildActiveBidsRow(payload, currencySymbol, section) {
    var symbol = currencySymbol || '£';
    var bidIncrement = typeof payload.bidIncrement === 'number' && !isNaN(payload.bidIncrement) ? payload.bidIncrement : 1;
    var nextBid = payload.currentBid + bidIncrement;
    var inputVal = payload.bidCount === 0 ? formatBidInputValue(payload.currentBid) : formatBidInputValue(nextBid);
    var userBid = payload.userBidAmount != null ? formatMoney(payload.userBidAmount, symbol) : '—';
    var winningText = payload.isUserWinning ? "You're winning!" : (payload.bidStatusText != null ? payload.bidStatusText : (payload.winningInitials ? payload.winningInitials + ' is winning!' : '— is winning!'));
    var pillClass = 'rising-auctions-tile__winner-pill' + (payload.isUserWinning ? ' rising-auctions-tile__winner-pill--winning' : '');
    var productUrl = payload.url || '#';
    var title = payload.title || '';
    var sizeLabel = getActiveBidsSize(payload);
    var sizeHtml = sizeLabel
      ? '<span class="rising-auctions-active-bids__size">Size: <b>' + escapeHtml(sizeLabel) + '</b></span>'
      : '';
    var imgHtml = buildAccountListImageHtml(
      payload.featuredImageUrl,
      payload.secondaryImageUrl,
      getPlaceholderImageUrl(section),
      title
    );
    var outbidMsg = payload.isUserWinning ? '' : '<p class="rising-auctions-active-bids__outbid-msg">You have been outbid. <br> Please place a higher bid for a chance to win this item.</p>';
    var currentBid = formatMoney(payload.currentBid, symbol);
    var bidInputHtml = '<div class="rising-auctions-active-bids__bid-controls" data-tile-bid-controls>' + 
    '<div class="price_with_symbol">' + 
      '<span class="symbol_text">' + 'Enter Bid:' + '</span>' + 
      '<div class="enter_bid" >' +
        '<span class="symbol_icon">' + escapeHtml(symbol) + '</span>' + 
        '<input type="number" class="rising-auctions-active-bids__bid-input rising-auctions-tile__bid-input" value="' + inputVal + '" min="0" max="99999" step="1" data-bid-input placeholder="Enter Bid: ' + escapeHtml(symbol + (payload.bidCount === 0 ? formatBidInputValue(payload.currentBid) : formatBidInputValue(nextBid))) + '" aria-label="Bid amount"/></div>' +
      '</div>' +
    '</div>';
    var statusHtml = '<span class="' + pillClass + '">' +
      getTileStatusIconHtml(payload.isUserWinning ? 'thumbs-up' : 'thumbs-down') + escapeHtml(winningText) +
    '</span>';
    var countdownHtml = '<span class="rising-auctions-active-bids__countdown" data-tile-countdown aria-live="polite">' +
      '<span data-countdown-text>' + escapeHtml(payload.countdownText || '') + '</span>' +
    '</span>';
    var mobileOutbidMsg = payload.isUserWinning ? '' : outbidMsg;
    var mobileCard = '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--mobile-card" colspan="5">' +
      '<div class="rising-auctions-active-bids__mobile-card">' +
        '<div class="rising-auctions-active-bids__mobile-card-main">' +
          '<a href="' + escapeHtml(productUrl) + '" class="rising-auctions-active-bids__mobile-card-image-link">' + imgHtml + '</a>' +
          '<div class="rising-auctions-active-bids__mobile-card-info">' +
            '<a href="' + escapeHtml(productUrl) + '" class="rising-auctions-active-bids__mobile-card-title">' + escapeHtml(title) + '</a>' +
            sizeHtml +
            '<div class="rising-auctions-active-bids__mobile-card-detail"><span class="rising-auctions-active-bids__mobile-card-label">Current Bid</span><span class="rising-auctions-active-bids__mobile-card-value rising-auctions-active-bids__current-bid">' + currentBid + '</span></div>' +
            '<div class="rising-auctions-active-bids__mobile-card-detail"><span class="rising-auctions-active-bids__mobile-card-label">Your Bid</span><span class="rising-auctions-active-bids__mobile-card-value rising-auctions-active-bids__your-bid-amount">' + userBid + '</span></div>' +
            '<div class="rising-auctions-active-bids__mobile-card-status">' + statusHtml + '</div>' +
          '</div>' +
        '</div>' +
        '<div class="rising-auctions-active-bids__mobile-card-actions">' +
          bidInputHtml +
          '<button type="button" class="rising-auctions-active-bids__increase-btn" data-bid-submit>Increase Bid</button>' +
        '</div>' +
        '<div class="rising-auctions-active-bids__mobile-card-time">' + countdownHtml + mobileOutbidMsg + '</div>' +
      '</div>' +
    '</td>';
    var tr = document.createElement('tr');
    tr.className = 'rising-auctions-active-bids__row';
    tr.setAttribute('data-auction-tile', '');
    tr.setAttribute('data-auction-id', payload.auctionId || '');
    tr.setAttribute('data-current-bid', String(payload.currentBid));
    tr.setAttribute('data-bid-increment', String(bidIncrement));
    tr.setAttribute('data-special-auction', bidIncrement > 1 ? 'true' : 'false');
    tr.setAttribute('data-bid-count', String(payload.bidCount || 0));
    tr.setAttribute('data-countdown-ends', payload.countdownEndsAt || '');
    tr.setAttribute('aria-label', payload.title || 'Auction');
    applyMaxBidAmountToTile(tr, payload.maxBidAmount);
    tr.innerHTML =
      mobileCard +
      '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--description">' +
        '<a href="' + escapeHtml(productUrl) + '" class="rising-auctions-active-bids__product-link">' +
          '<span class="rising-auctions-active-bids__thumb">' + imgHtml + '</span>' +
          '<span class="rising-auctions-active-bids__product-text">' +
            '<span class="rising-auctions-active-bids__title">' + escapeHtml(title) + '</span>' +
            sizeHtml +
          '</span>' +
        '</a></td>' +
      '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--time">' +
        countdownHtml + outbidMsg + '</td>' +
      '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--current-bid">' +
        '<span class="rising-auctions-active-bids__current-bid">' + currentBid + '</span></td>' +
      '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--your-bid">' +
        '<span class="rising-auctions-active-bids__your-bid-amount">' + userBid + '</span>' +
        statusHtml + '</td>' +
      '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--increase">' +
        bidInputHtml +
        '<button type="button" class="rising-auctions-active-bids__increase-btn" data-bid-submit>Increase Bid</button></td>';
    return tr;
  }

  /**
   * Favourites page: fetch favourites API and render table or empty state.
   * Uses separate buildFavouritesRow (Action column, Bid Now / Increase Bid by user bid).
   * Rows use data-auction-tile so initAuctionTiles (timer, bid, error modals) works.
   */
  function loadFavouritesPage() {
    var section = document.querySelector('[data-favourites]');
    if (!section) return;
    if (!section.getAttribute('data-current-page')) {
      try {
        var urlPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
        if (!isNaN(urlPage) && urlPage > 1) section.setAttribute('data-current-page', String(urlPage));
      } catch (e) {}
    }
    var render = function () {
      renderAccountBidsPage(section, {
        loadingSelector: '[data-favourites-loading]',
        emptySelector: '[data-favourites-empty]',
        tableWrapSelector: '[data-favourites-table-wrap]',
        tbodySelector: '[data-favourites-tbody]',
        buildRow: buildFavouritesRow,
        errorMessage: '[RA] Favourites load failed:',
      });
    };
    initAccountPagination(section, render);
    render();
  }

  /**
   * Build one table row for Favourites. Same structure as Active Bids but Action column:
   * "Bid Now" when user has no bid, "Increase Bid" when they have a bid. Expired state shows "CLOSED".
   */
  function buildFavouritesRow(payload, currencySymbol, section) {
    var symbol = currencySymbol || '£';
    var bidIncrement = typeof payload.bidIncrement === 'number' && !isNaN(payload.bidIncrement) ? payload.bidIncrement : 1;
    var nextBid = payload.currentBid + bidIncrement;
    var hasUserBid = payload.userBidAmount != null && payload.userBidAmount > 0;
    var bidCount = payload.bidCount != null ? payload.bidCount : 0;
    var nobodyHasBid = bidCount === 0;
    var inputVal = nobodyHasBid ? formatBidInputValue(payload.currentBid) : formatBidInputValue(nextBid);
    var userBid = hasUserBid ? formatMoney(payload.userBidAmount, symbol) : '-';
    var winningText = payload.isUserWinning ? "You're winning!" : (payload.bidStatusText != null ? payload.bidStatusText : (payload.winningInitials ? payload.winningInitials + ' is winning!' : (nobodyHasBid ? 'Be the first to bid' : '— is winning!')));
    var pillClass = 'rising-auctions-tile__winner-pill' + (payload.isUserWinning ? ' rising-auctions-tile__winner-pill--winning' : '');
    var productUrl = payload.url || '#';
    var title = payload.title || '';
    var sizeLabel = getActiveBidsSize(payload);
    var sizeHtml = sizeLabel
      ? '<span class="rising-auctions-active-bids__size">Size: <b>' + escapeHtml(sizeLabel) + '</b></span>'
      : '';
    var imgHtml = buildAccountListImageHtml(
      payload.featuredImageUrl,
      payload.secondaryImageUrl,
      getPlaceholderImageUrl(section),
      title
    );
    var outbidMsg = (payload.isUserWinning || nobodyHasBid) ? '' : '<p class="rising-auctions-active-bids__outbid-msg">You have been outbid. <br> Please place a higher bid for a chance to win this item.</p>';
    var statusHtml = nobodyHasBid
      ? '<span class="rising-auctions-active-bids__status-text">' + escapeHtml(winningText) + '</span>'
      : '<span class="' + pillClass + '">' +
          getTileStatusIconHtml(payload.isUserWinning ? 'thumbs-up' : 'thumbs-down') + escapeHtml(winningText) +
        '</span>';
    var btnText = hasUserBid ? 'Increase Bid' : 'Bid Now';
    var btnClass = hasUserBid ? 'rising-auctions-active-bids__increase-btn' : 'rising-auctions-active-bids__bid-now-btn';
    var currentBid = formatMoney(payload.currentBid, symbol);
    var bidInputHtml = '<div class="rising-auctions-active-bids__bid-controls" data-tile-bid-controls>' +
    '<div class="price_with_symbol">' + 
      '<span class="symbol_text">' + 'Enter Bid:' + '</span>' + 
      '<div class="enter_bid" >' +
        '<span class="symbol_icon">' + escapeHtml(symbol) + '</span>' + 
        '<input type="number" class="rising-auctions-active-bids__bid-input rising-auctions-tile__bid-input" value="' + inputVal + '" min="0" max="99999" step="1" data-bid-input placeholder="Enter Bid: ' + escapeHtml(symbol + (payload.bidCount === 0 ? formatBidInputValue(payload.currentBid) : formatBidInputValue(nextBid))) + '" aria-label="Bid amount"/></div>' +
      '</div>' +
    '</div>';
    var countdownHtml = '<span class="rising-auctions-active-bids__countdown" data-tile-countdown aria-live="polite">' +
      '<span data-countdown-text>' + escapeHtml(payload.countdownText || '') + '</span>' +
    '</span>';
    var mobileStatusHtml = nobodyHasBid ? '' : '<div class="rising-auctions-active-bids__mobile-card-status">' + statusHtml + '</div>';
    var mobileCard = '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--mobile-card" colspan="5">' +
      '<div class="rising-auctions-active-bids__mobile-card">' +
        '<div class="rising-auctions-active-bids__mobile-card-main">' +
          '<a href="' + escapeHtml(productUrl) + '" class="rising-auctions-active-bids__mobile-card-image-link">' + imgHtml + '</a>' +
          '<div class="rising-auctions-active-bids__mobile-card-info">' +
            '<a href="' + escapeHtml(productUrl) + '" class="rising-auctions-active-bids__mobile-card-title">' + escapeHtml(title) + '</a>' +
            sizeHtml +
            '<div class="rising-auctions-active-bids__mobile-card-detail"><span class="rising-auctions-active-bids__mobile-card-label">Current Bid</span><span class="rising-auctions-active-bids__mobile-card-value rising-auctions-active-bids__current-bid">' + currentBid + '</span></div>' +
            '<div class="rising-auctions-active-bids__mobile-card-detail"><span class="rising-auctions-active-bids__mobile-card-label">Your Bid</span><span class="rising-auctions-active-bids__mobile-card-value rising-auctions-active-bids__your-bid-amount">' + userBid + '</span></div>' +
            mobileStatusHtml +
          '</div>' +
        '</div>' +
        '<div class="rising-auctions-active-bids__mobile-card-actions">' +
          bidInputHtml +
          '<button type="button" class="' + btnClass + '" data-bid-submit>' + escapeHtml(btnText) + '</button>' +
        '</div>' +
        '<div class="rising-auctions-active-bids__mobile-card-time">' + countdownHtml + outbidMsg + '</div>' +
      '</div>' +
    '</td>';
    var tr = document.createElement('tr');
    tr.className = 'rising-auctions-active-bids__row rising-auctions-favourites__row';
    tr.setAttribute('data-auction-tile', '');
    tr.setAttribute('data-auction-id', payload.auctionId || '');
    tr.setAttribute('data-current-bid', String(payload.currentBid));
    tr.setAttribute('data-bid-increment', String(bidIncrement));
    tr.setAttribute('data-special-auction', bidIncrement > 1 ? 'true' : 'false');
    tr.setAttribute('data-bid-count', String(payload.bidCount || 0));
    tr.setAttribute('data-countdown-ends', payload.countdownEndsAt || '');
    tr.setAttribute('data-favourites-row', '');
    tr.setAttribute('aria-label', payload.title || 'Auction');
    applyMaxBidAmountToTile(tr, payload.maxBidAmount);
    tr.innerHTML =
      mobileCard +
      '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--description">' +
        '<a href="' + escapeHtml(productUrl) + '" class="rising-auctions-active-bids__product-link">' +
          '<span class="rising-auctions-active-bids__thumb">' + imgHtml + '</span>' +
          '<span class="rising-auctions-active-bids__product-text">' +
            '<span class="rising-auctions-active-bids__title">' + escapeHtml(title) + '</span>' +
            sizeHtml +
          '</span>' +
        '</a></td>' +
      '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--time">' +
        countdownHtml + outbidMsg + '</td>' +
      '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--current-bid">' +
        '<span class="rising-auctions-active-bids__current-bid">' + currentBid + '</span></td>' +
      '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--your-bid">' +
        '<span class="rising-auctions-active-bids__your-bid-amount">' + userBid + '</span>' +
        statusHtml + '</td>' +
      '<td class="rising-auctions-active-bids__cell rising-auctions-active-bids__cell--increase">' +
        bidInputHtml +
        '<button type="button" class="' + btnClass + '" data-bid-submit>' + escapeHtml(btnText) + '</button></td>';
    return tr;
  }

  /* -------------------------------------------------------------------------
     Auction PLP quickview (collection grid only)
     ------------------------------------------------------------------------- */

  function parseGalleryUrlsFromTile(tile) {
    var raw = tile.getAttribute('data-gallery-images') || '[]';
    try {
      var arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length) {
        return arr.filter(function (u) {
          return u && String(u).trim() !== '';
        });
      }
    } catch (e1) {}
    var urls = [];
    var pri = tile.querySelector('.rising-auctions-tile__image--primary');
    var sec = tile.querySelector('.rising-auctions-tile__image--secondary');
    var single = tile.querySelector('.rising-auctions-tile__media-link > .rising-auctions-tile__image');
    if (pri && pri.src) urls.push(pri.src);
    if (sec && sec.src) urls.push(sec.src);
    if (urls.length === 0 && single && single.src) urls.push(single.src);
    return urls;
  }

  function buildQuickviewEndedBidHtml(displayState, payload, currencySymbol, tileAttrs) {
    var sym = currencySymbol || '£';
    var variantId =
      (tileAttrs && tileAttrs.variantId) || (payload && payload.variantId) || '';
    var variantSku = (tileAttrs && tileAttrs.variantSku) || '';
    var auctionId =
      (payload && payload.auctionId) || (tileAttrs && tileAttrs.auctionId) || '';
    var amsAuctionCode =
      (payload && payload.amsAuctionCode) ||
      (tileAttrs && tileAttrs.amsAuctionCode) ||
      '';
    var mainProductCode =
      (payload && payload.mainProductCode) ||
      (tileAttrs && tileAttrs.mainProductCode) ||
      '';
    var paymentExpirationAt =
      (payload && (payload.paymentExpirationAt || payload.paymentDeadlineAt)) || '';
    var resolvedPrice =
      tileAttrs && tileAttrs.price != null && !isNaN(Number(tileAttrs.price))
        ? tileAttrs.price
        : payload && payload.variantPrice;
    var basePriceAttr = '';
    if (payload && payload.basePrice != null && String(payload.basePrice).trim() !== '') {
      basePriceAttr = String(payload.basePrice);
    } else if (resolvedPrice != null && resolvedPrice !== '') {
      basePriceAttr = formatRaBasePriceDecimal(resolvedPrice);
    }
    var cb = payload && payload.currentBid != null ? payload.currentBid : 0;
    var bc = payload && typeof payload.bidCount === 'number' ? payload.bidCount : 0;
    var initials = payload && payload.winnerInitials != null ? String(payload.winnerInitials) : '—';
    var inner;
    if (displayState === 'closed') {
      inner = raQwBuildPdpClosedInnerHtml();
    } else if (displayState === 'won') {
      inner = raQwBuildPdpWonInnerHtml(
        {
          currentBid: cb,
          bidCount: bc,
          variantId: variantId,
          auctionId: auctionId,
          amsAuctionCode: amsAuctionCode,
          mainProductCode: mainProductCode,
          paymentExpirationAt: paymentExpirationAt,
          variantPrice: payload && payload.variantPrice,
          price: resolvedPrice,
          basePrice: payload && payload.basePrice,
        },
        sym,
        variantId
      );
    } else {
      inner = raQwBuildPdpLostInnerHtml({ currentBid: cb, bidCount: bc, winnerInitials: initials }, sym);
    }
    return (
      '<article class="ra-qw__bid-article ra-qw__pdp-bid-article"' +
      ' data-auction-id="' +
      escapeHtml(auctionId) +
      '"' +
      ' data-ams-auction-code="' +
      escapeHtml(amsAuctionCode) +
      '"' +
      ' data-ra-main-product-code="' +
      escapeHtml(mainProductCode) +
      '"' +
      ' data-base-price="' +
      escapeHtml(basePriceAttr) +
      '"' +
      ' data-payment-expiration-at="' +
      escapeHtml(paymentExpirationAt) +
      '"' +
      ' data-variant-id="' +
      escapeHtml(variantId) +
      '"' +
      ' data-variant-sku="' +
      escapeHtml(variantSku) +
      '"' +
      ' data-auction-state="' +
      escapeHtml(displayState) +
      '"' +
      ' data-current-bid="' +
      escapeHtml(String(cb)) +
      '"' +
      ' data-bid-count="' +
      escapeHtml(String(bc)) +
      '">' +
      '<div class="auction-pdp ra-qw-auction-pdp"><div class="auction-pdp__container">' +
      inner +
      '</div></div></article>'
    );
  }

  function buildQuickviewActiveBidArticleHtml(payload, currencySymbol, section) {
    var isLoggedIn = section.getAttribute('data-customer-logged-in') === 'true';
    var loginUrl = section.getAttribute('data-login-url') || '/account/login';
    var state = payload.auctionState || 'active';
    var bidIncrement =
      typeof payload.bidIncrement === 'number' && !isNaN(payload.bidIncrement) ? payload.bidIncrement : 1;
    var maxBidCap =
      typeof payload.maxBidAmount === 'number' && !isNaN(payload.maxBidAmount) ? payload.maxBidAmount : null;
    var built = raQwBuildPdpActiveMarkup(
      {
        countdownEndsAt: payload.countdownEndsAt,
        currentBid: payload.currentBid,
        bidCount: payload.bidCount,
        bidIncrement: bidIncrement,
        isUserWinning: !!payload.isUserWinning,
        hasUserBid: !!payload.hasUserBid,
        bidStatusText: payload.bidStatusText,
      },
      currencySymbol,
      maxBidCap,
      isLoggedIn,
      loginUrl
    );
    return (
      '<article class="ra-qw__bid-article ra-qw__pdp-bid-article rising-auctions-tile--' +
      escapeHtml(state) +
      '" data-auction-tile="" data-auction-id="' +
      escapeHtml(payload.auctionId || '') +
      '" data-ams-auction-code="' +
      escapeHtml(payload.amsAuctionCode || '') +
      '" data-product-id="' +
      escapeHtml(payload.productId || '') +
      '" data-variant-id="' +
      escapeHtml(payload.variantId || '') +
      '" data-variant-sku="' +
      escapeHtml(payload.variantSku || '') +
      '" data-ra-main-product-code="' +
      escapeHtml(payload.mainProductCode || '') +
      '" data-base-price="' +
      escapeHtml(formatRaBasePriceDecimal(payload.price)) +
      '" data-auction-state="' +
      escapeHtml(state) +
      '" data-countdown-ends="' +
      escapeHtml(payload.countdownEndsAt || '') +
      '" data-current-bid="' +
      escapeHtml(String(payload.currentBid)) +
      '" data-bid-increment="' +
      escapeHtml(String(bidIncrement)) +
      '" data-special-auction="' +
      (bidIncrement > 1 ? 'true' : 'false') +
      '" data-bid-count="' +
      escapeHtml(String(payload.bidCount)) +
      '"' +
      (maxBidCap != null
        ? ' data-max-bid-amount="' + escapeHtml(String(maxBidCap)) + '"'
        : '') +
      '>' +
      '<div class="auction-pdp ra-qw-auction-pdp"><div class="auction-pdp__container">' +
      built.html +
      '</div></div></article>'
    );
  }

  function renderRaQwBidMountFromFetch(bidMount, section, data, sym, tileAttrs) {
    if (!bidMount || !data) return;
    bidMount.hidden = false;
    var ds = data.displayState;
    if (ds === 'won' || ds === 'lost' || ds === 'closed') {
      bidMount.innerHTML = buildQuickviewEndedBidHtml(ds, data, sym, tileAttrs);
      if (ds === 'won') {
        var artW = bidMount.querySelector('.ra-qw__bid-article');
        if (artW) {
          initAddToBagPlaceholder(artW);
          refreshPlpAddToBagStates(section);
        }
      }
    } else {
      bidMount.innerHTML = buildQuickviewActiveBidArticleHtml(data, sym, section);
      initAuctionTiles();
    }
  }

  function refetchRaQwBidMountOnExpiry(bidMount, section, contextTile) {
    if (!bidMount || !section) return;
    var auctionId = (contextTile && contextTile.getAttribute('data-auction-id')) || '';
    if (!auctionId) return;
    var apiUrl = '/apps/rising-auctions/auction?auction_id=' + encodeURIComponent(auctionId);
    var cust = section.getAttribute('data-customer-id');
    if (cust) apiUrl += '&logged_in_customer_id=' + encodeURIComponent(cust);
    var qwExpirySentAt = Date.now();
    fetch(apiUrl, { credentials: 'include' })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        raSyncClock(data, qwExpirySentAt);
        return data;
      })
      .then(function (data) {
        if (!data || data.error) return;
        var sym = section.getAttribute('data-currency-symbol') || '£';
        var bpRaw = contextTile && contextTile.getAttribute('data-base-price');
        var bpNum = bpRaw != null && bpRaw !== '' ? parseFloat(bpRaw) : NaN;
        var tileAttrs = {
          auctionId: auctionId,
          amsAuctionCode: (contextTile && contextTile.getAttribute('data-ams-auction-code')) || '',
          variantId: (contextTile && contextTile.getAttribute('data-variant-id')) || '',
          variantSku: (contextTile && contextTile.getAttribute('data-variant-sku')) || '',
          mainProductCode: (contextTile && contextTile.getAttribute('data-ra-main-product-code')) || '',
          price: !isNaN(bpNum) ? bpNum : null,
        };
        renderRaQwBidMountFromFetch(bidMount, section, data, sym, tileAttrs);
      })
      .catch(function () {});
  }

  var RA_QW_BODY_OPEN_CLASS = 'ra-auction-quickview-open';

  function getRaQwSectionKey(section) {
    return section.getAttribute('data-block-id') || section.id || 'plp';
  }

  function getRaAuctionQuickviewModal(section) {
    if (!section) return null;
    if (section._raQwModalEl && document.contains(section._raQwModalEl)) {
      return section._raQwModalEl;
    }
    var key = getRaQwSectionKey(section);
    var el =
      document.querySelector('[data-ra-auction-quickview][data-ra-qw-section="' + key + '"]') ||
      section.querySelector('[data-ra-auction-quickview]');
    if (el) section._raQwModalEl = el;
    return el;
  }

  function portalRaAuctionQuickviewModal(modal, section) {
    if (!modal || modal.parentNode === document.body) return;
    section._raQwModalHome = { parent: modal.parentNode, next: modal.nextSibling };
    document.body.appendChild(modal);
  }

  function restoreRaAuctionQuickviewModal(modal, section) {
    if (!modal || !section._raQwModalHome || !section._raQwModalHome.parent) return;
    var home = section._raQwModalHome;
    home.parent.insertBefore(modal, home.next);
    section._raQwModalHome = null;
  }

  function ensureRaAuctionQuickviewModal(section) {
    var el = getRaAuctionQuickviewModal(section);
    if (el) return el;
    el = document.createElement('div');
    el.className = 'ra-auction-quickview';
    el.setAttribute('data-ra-auction-quickview', '');
    el.setAttribute('data-ra-qw-section', getRaQwSectionKey(section));
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<div class="ra-auction-quickview__backdrop" data-ra-qw-backdrop tabindex="-1"></div>' +
      '<div class="ra-auction-quickview__panel" role="dialog" aria-modal="true" data-ra-qw-panel>' +
      '<div class="ra-auction-quickview__header">' +
      '<h2 class="ra-auction-quickview__heading" data-ra-qw-heading>Quick View</h2>' +
      '<button type="button" class="ra-auction-quickview__close" data-ra-qw-close aria-label="Close">' +
      '<span class="svg-wrapper" aria-hidden="true">' +
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M12 2L2 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M12 12L2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg></span></button></div>' +
      '<div class="ra-auction-quickview__grid">' +
      '<div class="ra-auction-quickview__col ra-auction-quickview__col--gallery">' +
      '<div class="ra-auction-quickview__main-wrap">' +
      '<img src="" alt="" class="ra-auction-quickview__main" data-ra-qw-main width="480" height="480" loading="lazy" />' +
      '</div>' +
      '<div class="ra-auction-quickview__thumbs" data-ra-qw-thumbs></div>' +
      '</div>' +
      '<div class="ra-auction-quickview__col ra-auction-quickview__col--detail">' +
      '<h3 class="ra-auction-quickview__title" data-ra-qw-title></h3>' +
      '<p class="ra-auction-quickview__size" data-ra-qw-size hidden></p>' +
      '<a href="#" class="ra-auction-quickview__pdp-link" data-ra-qw-pdp-link>View full product details</a>' +
      '<div class="ra-auction-quickview__divider"></div>' +
      '<p class="ra-auction-quickview__loading" data-ra-qw-loading>Loading…</p>' +
      '<div class="ra-auction-quickview__bid" data-ra-qw-bid-mount hidden></div>' +
      '</div></div></div>';
    section.appendChild(el);
    section._raQwModalEl = el;

    function onCloseClick(e) {
      if (e.target === el.querySelector('[data-ra-qw-backdrop]') || e.target.closest('[data-ra-qw-close]')) {
        closeRaAuctionQuickview(section);
      }
    }
    el.addEventListener('click', onCloseClick);
    var blockId = section.getAttribute('data-block-id') || 'plp';
    var headingId = 'ra-qw-heading-' + blockId;
    var headingNode = el.querySelector('[data-ra-qw-heading]');
    var panelNode = el.querySelector('[data-ra-qw-panel]');
    if (headingNode) headingNode.id = headingId;
    if (panelNode) panelNode.setAttribute('aria-labelledby', headingId);
    return el;
  }

  /**
   * Swap the quickview hero image without ever painting the previous auction's
   * frame: a cached URL is assigned straight away, an uncached one blanks the
   * <img> first and is assigned only once it has decoded. The token drops a
   * preload that finishes after the shopper has already opened another tile.
   */
  function setRaQwMainImage(main, url, alt) {
    if (!main) return;
    var token = (main._raQwImgToken || 0) + 1;
    main._raQwImgToken = token;
    main.alt = alt || '';
    if (!url) {
      main.removeAttribute('src');
      return;
    }
    var probe = new Image();
    probe.src = url;
    if (probe.complete && probe.naturalWidth > 0) {
      main.src = url;
      return;
    }
    main.removeAttribute('src');
    probe.onload = function () {
      if (main._raQwImgToken !== token) return;
      main.src = url;
    };
    probe.onerror = function () {
      if (main._raQwImgToken !== token) return;
      main.removeAttribute('src');
    };
  }

  function setRaQwGallery(modal, urls, title, placeholderImageUrl) {
    var main = modal.querySelector('[data-ra-qw-main]');
    var thumbs = modal.querySelector('[data-ra-qw-thumbs]');
    if (!main || !thumbs) return;
    thumbs.innerHTML = '';
    var list = urls && urls.length ? urls.filter(function (url) {
      return cleanImageUrl(url) !== '';
    }) : [];
    var placeholderUrl = cleanImageUrl(placeholderImageUrl);
    if (list.length === 0 && placeholderUrl) {
      list = [placeholderUrl];
    }
    if (list.length === 0) {
      setRaQwMainImage(main, '', '');
      return;
    }
    setRaQwMainImage(main, list[0], title || '');
    list.forEach(function (url, idx) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ra-auction-quickview__thumb' + (idx === 0 ? ' ra-auction-quickview__thumb--active' : '');
      b.setAttribute('aria-label', 'Image ' + (idx + 1));
      b.setAttribute('data-ra-qw-thumb-idx', String(idx));
      var im = document.createElement('img');
      im.src = url;
      im.alt = '';
      im.loading = 'lazy';
      b.appendChild(im);
      thumbs.appendChild(b);
    });
    thumbs.onclick = function (ev) {
      var btn = ev.target.closest('[data-ra-qw-thumb-idx]');
      if (!btn || !thumbs.contains(btn)) return;
      var ix = parseInt(btn.getAttribute('data-ra-qw-thumb-idx'), 10);
      if (isNaN(ix) || !list[ix]) return;
      main.src = list[ix];
      thumbs.querySelectorAll('.ra-auction-quickview__thumb').forEach(function (t) {
        t.classList.toggle('ra-auction-quickview__thumb--active', t === btn);
      });
    };
  }

  function closeRaAuctionQuickview(section) {
    var modal = getRaAuctionQuickviewModal(section);
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('ra-auction-quickview--open');
    document.body.classList.remove(RA_QW_BODY_OPEN_CLASS);
    restoreRaAuctionQuickviewModal(modal, section);
    var bidMount = modal.querySelector('[data-ra-qw-bid-mount]');
    if (bidMount) {
      bidMount.innerHTML = '';
      bidMount.hidden = true;
    }
    var loading = modal.querySelector('[data-ra-qw-loading]');
    if (loading) loading.hidden = false;
    setRaQwMainImage(modal.querySelector('[data-ra-qw-main]'), '', '');
    var closedThumbs = modal.querySelector('[data-ra-qw-thumbs]');
    if (closedThumbs) closedThumbs.innerHTML = '';
    if (section._raQwPrevFocus && typeof section._raQwPrevFocus.focus === 'function') {
      try {
        section._raQwPrevFocus.focus();
      } catch (e2) {}
    }
    section._raQwPrevFocus = null;
    if (section._raQwOnEscape) {
      document.removeEventListener('keydown', section._raQwOnEscape);
      section._raQwOnEscape = null;
    }
  }

  function openRaAuctionQuickview(sourceTile, section) {
    if (!sourceTile || !section) return;
    var modal = ensureRaAuctionQuickviewModal(section);
    var title = sourceTile.getAttribute('data-product-title') || sourceTile.getAttribute('aria-label') || '';
    var pdpUrl = sourceTile.getAttribute('data-pdp-url') || '#';
    var auctionId = (sourceTile.getAttribute('data-auction-id') || '').trim();
    var mpcQw = sourceTile.getAttribute('data-ra-main-product-code') || '';
    var bpQw = sourceTile.getAttribute('data-base-price') || '';
    var bpQwNum = bpQw !== '' ? parseFloat(bpQw) : NaN;
    var tileAttrs = {
      auctionId: auctionId,
      amsAuctionCode: sourceTile.getAttribute('data-ams-auction-code') || '',
      variantId: sourceTile.getAttribute('data-variant-id') || '',
      variantSku: sourceTile.getAttribute('data-variant-sku') || '',
      mainProductCode: mpcQw,
      price: !isNaN(bpQwNum) ? bpQwNum : null,
    };
    var sym = section.getAttribute('data-currency-symbol') || '£';

    section._raQwPrevFocus = document.activeElement;

    var titleEl = modal.querySelector('[data-ra-qw-title]');
    var sizeEl = modal.querySelector('[data-ra-qw-size]');
    if (!sizeEl && titleEl && titleEl.parentNode) {
      sizeEl = document.createElement('p');
      sizeEl.className = 'ra-auction-quickview__size';
      sizeEl.setAttribute('data-ra-qw-size', '');
      sizeEl.hidden = true;
      titleEl.insertAdjacentElement('afterend', sizeEl);
    }
    var linkEl = modal.querySelector('[data-ra-qw-pdp-link]');
    var bidMount = modal.querySelector('[data-ra-qw-bid-mount]');
    var loadingEl = modal.querySelector('[data-ra-qw-loading]');
    var variantSize = (sourceTile.getAttribute('data-variant-size') || '').trim();
    if (titleEl) titleEl.textContent = title;
    if (sizeEl) {
      sizeEl.textContent = variantSize ? 'Size: ' + variantSize : '';
      sizeEl.hidden = !variantSize;
    }
    if (linkEl) {
      linkEl.href = pdpUrl;
      linkEl.textContent = 'View full product details';
    }
    if (bidMount) {
      bidMount.innerHTML = '';
      bidMount.hidden = true;
    }
    if (loadingEl) loadingEl.hidden = false;

    setRaQwGallery(modal, parseGalleryUrlsFromTile(sourceTile), title, getPlaceholderImageUrl(section));

    portalRaAuctionQuickviewModal(modal, section);
    modal.classList.add('ra-auction-quickview--open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add(RA_QW_BODY_OPEN_CLASS);

    var closeBtn = modal.querySelector('[data-ra-qw-close]');
    if (closeBtn) closeBtn.focus();

    if (section._raQwOnEscape) {
      document.removeEventListener('keydown', section._raQwOnEscape);
    }
    section._raQwOnEscape = function (ke) {
      if (ke.key !== 'Escape') return;
      if (!modal.classList.contains('ra-auction-quickview--open')) return;
      ke.preventDefault();
      closeRaAuctionQuickview(section);
    };
    document.addEventListener('keydown', section._raQwOnEscape);

    if (!auctionId) {
      if (loadingEl) {
        loadingEl.textContent = 'Missing auction.';
        loadingEl.hidden = false;
      }
      return;
    }

    var apiUrl = '/apps/rising-auctions/auction?auction_id=' + encodeURIComponent(auctionId);
    var cust = section.getAttribute('data-customer-id');
    if (cust) apiUrl += '&logged_in_customer_id=' + encodeURIComponent(cust);

    var qwSentAt = Date.now();
    fetch(apiUrl, { credentials: 'include' })
      .then(function (res) {
        if (loadingEl) loadingEl.hidden = true;
        if (res.status === 404) {
          if (loadingEl) {
            loadingEl.hidden = false;
            loadingEl.textContent = 'This auction is not available.';
          }
          return null;
        }
        return res.json();
      })
      .then(function (data) {
        raSyncClock(data, qwSentAt);
        if (!data || data.error) {
          if (loadingEl) {
            loadingEl.hidden = false;
            loadingEl.textContent = (data && data.error) || 'Could not load auction.';
          }
          return;
        }
        if (loadingEl) loadingEl.hidden = true;
        if (!bidMount) return;
        renderRaQwBidMountFromFetch(bidMount, section, data, sym, tileAttrs);
      })
      .catch(function () {
        if (loadingEl) {
          loadingEl.hidden = false;
          loadingEl.textContent = 'Could not load auction.';
        }
      });
  }

  function bindRaAuctionQuickviewDelegated(section) {
    if (!section || section.dataset.raQwDelegated) return;
    section.dataset.raQwDelegated = 'true';
    section.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[data-auction-quickview]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var tile = btn.closest('[data-auction-tile]');
      if (!tile) return;
      openRaAuctionQuickview(tile, section);
    });
  }

  function initRaAuctionQuickview() {
    document.querySelectorAll('[data-rising-auctions-plp]').forEach(bindRaAuctionQuickviewDelegated);
  }

  function init() {
    initCountdownVisibilityHandler();
    initRaLivePriceResync();
    initSoldTileConfettiObserver();
    initActiveAuctionsPlpGrid();
    initAuctionTiles();
    initRaAuctionQuickview();
    loadActiveBidsPage();
    loadFavouritesPage();
    document.addEventListener('cart:update', schedulePlpAddToBagCartSync);
    document.addEventListener('cart:updated', schedulePlpAddToBagCartSync);
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      retryBrokenAuctionTileImages(document);
    });
  }

  /**
   * Auctions PLP fast path. sections/ra-product-grid.liquid injects this file right after the
   * grid section markup and sets __raPlpStartEarly, so the section (toolbar, grid, pagination,
   * facet drawer) is complete even while the rest of the page is still parsing. Start just
   * the grid and what its tiles need now; init() still runs at DOMContentLoaded and skips
   * what already ran (each of these is idempotent).
   */
  function initPlpGridEarly() {
    if (!document.body || !document.querySelector('[data-grid-inner][data-api-url]')) return;
    initCountdownVisibilityHandler();
    initRaLivePriceResync();
    initSoldTileConfettiObserver();
    initActiveAuctionsPlpGrid();
    initRaAuctionQuickview();
  }

  if (document.readyState === 'loading') {
    if (window.__raPlpStartEarly) initPlpGridEarly();
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
